import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Modal, notification } from "antd";
import axios from "axios";
import { useLocation, useNavigate, useOutletContext } from "react-router-dom";
import io from "socket.io-client";
import {
  IoGameControllerOutline,
  IoGiftOutline,
  IoCogOutline,
  IoLanguageOutline,
  IoHammerOutline,
  IoDiamondOutline,
  IoPeopleOutline,
  IoRocketOutline,
  IoBookOutline,
  IoStatsChartOutline,
  IoFolderOpenOutline,
  IoCubeOutline,
  IoPlayCircleOutline,
  IoMenuOutline,
  IoMailOutline,
  IoAddCircleOutline,
  IoTrophyOutline,
} from "react-icons/io5";
import { GiBinoculars } from "react-icons/gi";
import { TbListCheck, TbPuzzle } from "react-icons/tb";
import LobbyBoard3D from "../../components/LobbyBoard3D";
import LobbyBoardTower3D from "../../components/LobbyBoardTower3D";
import LobbyModern from "../../components/lobby/LobbyModern";
import LobbyLive from "../../components/lobby/LobbyLive";
import { isOwnerAccount } from "../../utils/ownerAccess";
import {
  LOBBY_QUICK_PLAY_DEFAULT_MATCH,
  pickLobbyQuickPlayTournament,
  readLastJoinedTournament,
  readQuickPlayPreferences,
  writeLastJoinedTournament,
} from "../../utils/lobbyQuickPlay";
import { useAuth } from "../../services/AuthContext";
import { clearStoredAuthToken, getStoredAuthToken } from "../../services/authStorage";
import {
  canShowConnectedPlayerMessage,
  DIRECT_MESSAGE_OPEN_EVENT,
  DIRECT_MESSAGE_UNREAD_EVENT,
  readPublishedDirectMessageUnreadSummary,
} from "../../services/directMessageUi";
import { getLocaleForLanguage, normalizeLanguage } from "../../services/language";
import { t, t as i18nT } from "../../i18n";
import {
  buildCurrentUserProfileIdentitySet,
  resolvePlayerProfileRoute,
  resolveProfileTargetFromUserLike,
} from "./leaderboardProfileNavigation";
import { resolveAvatarSrc } from "../../utils/avatarUrl";
import {
  buildFirstLobbyTutorialTarget,
  buildTutorialLevel2Target,
  rememberTutorialReturnPath,
} from "../../utils/firstLobbyTutorial";
import { unlockGameAudio } from "../../utils/gameAudioUnlock";

const LOBBY_DESIGN_STORAGE_KEY = "xima_lobby_design_v1";
const TILE_SIZE = 280;
const RECT_BUTTON_WIDTH = 400;
const RECT_BUTTON_HEIGHT = 800;
const LOBBY_LAYOUT_STORAGE_PREFIX = "xima_lobby_layout";
const LOBBY_LAYOUT_VERSION = 2;
const MOBILE_LOBBY_PORTRAIT_LAYOUT_VERSION = 3;
const DEFAULT_VIDEO_WIDTH_GUESS = 300;
const MOBILE_LOBBY_SCALE_MIN = 0.12;
const HOVER_SOUND_MIN_INTERVAL_MS = 120;
const VIDEO_SCALE_MIN = 0.2;
const VIDEO_SCALE_MAX = 12;
const VIDEO_SCALE_STEP = 0.05;
const DEFAULT_VIDEO_SCALE = 1;
const MOBILE_LOBBY_LONG_PRESS_MS = 1000;
const LOBBY_BUTTON_DISPLAY_SCALE = 0.5;
const MOBILE_LOBBY_BUTTON_SCALE = 0.5;
const MOBILE_LOBBY_PORTRAIT_SQUARE_SCALE = MOBILE_LOBBY_BUTTON_SCALE;
const DESKTOP_LOBBY_SQUARE_SCALE = 200 / (TILE_SIZE * LOBBY_BUTTON_DISPLAY_SCALE);
const DESKTOP_LOBBY_SQUARE_SIZE = Math.round(
  TILE_SIZE * LOBBY_BUTTON_DISPLAY_SCALE * DESKTOP_LOBBY_SQUARE_SCALE,
);
const MOBILE_LOBBY_PORTRAIT_SQUARE_SIZE_BOOST = 1.25;
const DESKTOP_LOBBY_BUTTON_IMAGE_SIZE_CLASS = "h-[140px] w-[140px]";
const MOBILE_LOBBY_PORTRAIT_SIDE_PADDING = 16;
const MOBILE_LOBBY_PORTRAIT_TOP_OFFSET = 18;
const MOBILE_LOBBY_PORTRAIT_RECT_GAP = 18;
const MOBILE_LOBBY_PORTRAIT_RECT_HEIGHT = 172;
const MOBILE_LOBBY_PORTRAIT_BOTTOM_PADDING = 190;
const MOBILE_LOBBY_TOP_PLAYERS_PANEL_GAP = 18;
const MOBILE_LOBBY_TOP_PLAYERS_PANEL_HEIGHT = 430;
const LOBBY_BUTTON_SOUND_SRC = "/assets/sounds/atras.mp3";
const ONLINE_PLAYERS_COUNT_UPDATED_EVENT = "online_players_count_updated";
const ONLINE_PLAYERS_COUNT_REQUEST_EVENT = "online_players_count_request";
const LEGACY_CONNECTED_PLAYERS_COUNT_EVENT = "CONNECTED_PLAYERS_COUNT";
const LEGACY_CONNECTED_PLAYERS_COUNT_REQUEST_EVENT = "CONNECTED_PLAYERS_COUNT_REQUEST";
const CONNECTED_PLAYERS_LIST_LIMIT = 10;
const CONNECTED_PLAYERS_COUNT_EVENTS = [
  ONLINE_PLAYERS_COUNT_UPDATED_EVENT,
  LEGACY_CONNECTED_PLAYERS_COUNT_EVENT,
];
const GLOBAL_CHAT_EVENT_NAME = "GLOBAL_CHAT_MESSAGE";
const GLOBAL_CHAT_HISTORY_EVENT_NAME = "GLOBAL_CHAT_HISTORY";
const GLOBAL_CHAT_HISTORY_REQUEST_EVENT_NAME = "GLOBAL_CHAT_HISTORY_REQUEST";
const GLOBAL_CHAT_CLEAR_EVENT_NAME = "GLOBAL_CHAT_CLEAR";
const GLOBAL_CHAT_CLEARED_EVENT_NAME = "GLOBAL_CHAT_CLEARED";
const GLOBAL_CHAT_CLEAR_REJECTED_EVENT_NAME = "GLOBAL_CHAT_CLEAR_REJECTED";
const GLOBAL_CHAT_SESSION_KEY = "xima_global_chat_messages_session_v1";
const GLOBAL_CHAT_MAX_MESSAGES = 80;
const GLOBAL_CHAT_MAX_LENGTH = 280;
const XIMA_WORLD_CHAMPIONSHIP_DATE = new Date(Date.UTC(2026, 9, 1, 12, 0, 0));
const LOBBY_COPY = Object.freeze({
  playPanelTitle: {
    es: "Como quieres jugar?",
    en: "How do you want to play?",
    de: "Wie möchtest du spielen?",
    ru: "Как ты хочешь играть?",
  },
  worldChampionshipAnnouncement: {
    es: "Próximamente campeonato mundial de XIMA el {date}",
    en: "Coming soon: XIMA World Championship on {date}",
    de: "Demnächst: XIMA-Weltmeisterschaft am {date}",
    ru: "Скоро: чемпионат мира по XIMA, {date}",
  },
  rankWorldwide: {
    es: "#{rank} Mundial",
    en: "#{rank} Worldwide",
    de: "Weltweit Nr. {rank}",
    ru: "№ {rank} в мире",
  },
  unrankedWorldwide: {
    es: "#-- Mundial",
    en: "#-- Worldwide",
    de: "Weltweit Nr. --",
    ru: "№ -- в мире",
  },
  bonusesLabel: {
    es: "Bonos: {value}",
    en: "Bonuses: {value}",
    de: "Boni: {value}",
    ru: "Бонусы: {value}",
  },
  firstTutorialAccess: {
    es: "Tutorial guiado",
    en: "Guided tutorial",
    de: "Geführtes Tutorial",
    ru: "Пошаговое обучение",
  },
  guidedTutorialLevel1: {
    es: "Tutorial 1 · Controles básicos",
    en: "Tutorial 1 · Basic controls",
    de: "Tutorial 1 · Grundlagen",
    ru: "Обучение 1 · Основы управления",
  },
  guidedTutorialLevel2: {
    es: "Tutorial 2 · Capturas y dados",
    en: "Tutorial 2 · Captures and dice",
    de: "Tutorial 2 · Schlagen und Würfel",
    ru: "Обучение 2 · Захваты и кости",
  },
  firstTutorialEyebrow: {
    es: "TUTORIAL XIMA · NIVEL 1",
    en: "XIMA TUTORIAL · LEVEL 1",
    de: "XIMA-TUTORIAL · LEVEL 1",
    ru: "ОБУЧЕНИЕ XIMA · УРОВЕНЬ 1",
  },
  firstTutorialWelcome: {
    es: "¡Bienvenido a XIMA, {name}!",
    en: "Welcome to XIMA, {name}!",
    de: "Willkommen bei XIMA, {name}!",
    ru: "Добро пожаловать в XIMA, {name}!",
  },
  firstTutorialQuestion: {
    es: "¿Quieres comenzar un tutorial guiado para aprender a jugar?",
    en: "Would you like to start a guided tutorial to learn how to play?",
    de: "Möchtest du mit einem geführten Tutorial das Spielen lernen?",
    ru: "Хотите пройти пошаговое обучение игре?",
  },
  firstTutorialDescription: {
    es: "Practicarás los controles, seleccionarás bloques y completarás movimientos reales en el tablero.",
    en: "You will practice the controls, select blocks, and complete real moves on the board.",
    de: "Du übst die Steuerung, wählst Blöcke aus und führst echte Züge auf dem Brett aus.",
    ru: "Вы освоите управление, выбор блоков и реальные ходы на доске.",
  },
  firstTutorialAccept: {
    es: "Sí, iniciar tutorial",
    en: "Yes, start tutorial",
    de: "Ja, Tutorial starten",
    ru: "Да, начать обучение",
  },
  firstTutorialDecline: {
    es: "Ahora no",
    en: "Not now",
    de: "Jetzt nicht",
    ru: "Не сейчас",
  },
  connectedPlayers: {
    es: "Jugadores conectados",
    en: "Players online",
    de: "Spieler online",
    ru: "Игроки онлайн",
  },
  lastConnectedPlayers: {
    es: "Ultimos 10 jugadores conectados",
    en: "Last 10 players online",
    de: "Letzte 10 Spieler online",
    ru: "Last 10 players online",
  },
  noConnectedPlayers: {
    es: "No hay jugadores conectados",
    en: "No players online",
    de: "Keine Spieler online",
    ru: "No players online",
  },
  generatedGuestName: {
    es: "invitado{n}",
    en: "guest{n}",
    de: "Gast{n}",
    ru: "Гость{n}",
  },
  languageTitle: {
    es: "Idioma",
    en: "Language",
    de: "Sprache",
    ru: "Язык",
  },
  languageDescription: {
    es: "Cambia a tu idioma",
    en: "Change to your language",
    de: "Sprache wechseln",
    ru: "Выбери свой язык",
  },
  learnTitle: {
    es: "Aprender",
    en: "Learn",
    de: "Lernen",
    ru: "Обучение",
  },
  learnDescription: {
    es: "Videos y Reglas del juego Xima",
    en: "Videos and XIMA game rules",
    de: "Videos und XIMA-Spielregeln",
    ru: "Видео и правила игры XIMA",
  },
  watchMatchesTitle: {
    es: "Ver Partidos",
    en: "Watch Matches",
    de: "Partien ansehen",
    ru: "Смотреть партии",
  },
  watchMatchesDescription: {
    es: "Observa partidos en vivo y aprende de los mejores",
    en: "Watch live matches and learn from the best",
    de: "Sieh dir Live-Partien an und lerne von den Besten",
    ru: "Смотри партии в прямом эфире и учись у лучших",
  },
  historyTitle: {
    es: "Historial",
    en: "History",
    de: "Verlauf",
    ru: "История",
  },
  historyDescription: {
    es: "Volver a ver y estudiar tus partidas anteriores",
    en: "Review and analyze your previous matches",
    de: "Sieh dir frühere Partien erneut an und analysiere sie",
    ru: "Пересматривай и анализируй свои предыдущие партии",
  },
  shopTitle: {
    es: "Tienda",
    en: "Shop",
    de: "Shop",
    ru: "Магазин",
  },
  shopDescription: {
    es: "Juguete físico de XIMA, skins y mas",
    en: "Physical XIMA game, skins, and more",
    de: "Physisches XIMA-Spiel, Skins und mehr",
    ru: "Физическая версия XIMA, скины и многое другое",
  },
  statisticsTitle: {
    es: "Estadísticas",
    en: "Statistics",
    de: "Statistiken",
    ru: "Статистика",
  },
  statisticsDescription: {
    es: "Mira tu fuerza, potencia y nivel de juego",
    en: "View your strength, power, and skill level",
    de: "Sieh dir deine Stärke, Kraft und dein Spielniveau an",
    ru: "Посмотри свою силу, мощь и игровой уровень",
  },
  sandboxTitle: {
    es: "Caja de arena",
    en: "Sandbox",
    de: "Sandbox",
    ru: "Песочница",
  },
  sandboxDescription: {
    es: "Practica libre y rompe reglas de movimiento",
    en: "Practice freely and break the movement rules",
    de: "Übe frei und brich die Bewegungsregeln",
    ru: "Тренируйся свободно и нарушай правила перемещения",
  },
  friendsPlayTitle: {
    es: "Jugar con amigos",
    en: "Play with Friends",
    de: "Mit Freunden spielen",
    ru: "Играть с друзьями",
  },
  friendsPlayDescription: {
    es: "Partida local en el mismo dispositivo",
    en: "Local game on the same device",
    de: "Lokales Spiel auf demselben Gerät",
    ru: "Локальная игра на одном устройстве",
  },
  designBoardTitle: {
    es: "Diseñar tablero",
    en: "Design Board",
    de: "Spielbrett gestalten",
    ru: "Настроить доску",
  },
  designBoardDescription: {
    es: "Ajusta bloques, skins y estilo visual",
    en: "Customize blocks, skins, and visual style",
    de: "Passe Blöcke, Skins und den visuellen Stil an",
    ru: "Настрой блоки, скины и визуальный стиль",
  },
  puzzleDescription: {
    es: "Resuelve desafios y mejora tu estrategia",
    en: "Solve challenges and improve your strategy",
    de: "Löse Herausforderungen und verbessere deine Strategie",
    ru: "Решай испытания и улучшай свою стратегию",
  },
  puzzleTrainingDescription: {
    es: "Resuelve desafios y entrena tu mente.",
    en: "Solve challenges and train your mind.",
    de: "Löse Herausforderungen und trainiere deinen Geist.",
    ru: "Решай испытания и тренируй ум.",
  },
  botDescription: {
    es: "Desafia a nuestros bots en distintos niveles",
    en: "Challenge our bots at different levels",
    de: "Fordere unsere Bots auf verschiedenen Stufen heraus",
    ru: "Бросай вызов нашим ботам на разных уровнях",
  },
  friendsTitle: {
    es: "Amigos",
    en: "Friends",
    de: "Freunde",
    ru: "Друзья",
  },
  friendsDescription: {
    es: "Invita, acepta solicitudes y juega juntos",
    en: "Invite friends, accept requests, and play together",
    de: "Lade Freunde ein, nimm Anfragen an und spiele mit ihnen",
    ru: "Приглашай друзей, принимай заявки и играй вместе с ними",
  },
  createGame: {
    es: "Crear juego",
    en: "Create game",
    de: "Spiel erstellen",
    ru: "Создать игру",
  },
  scienceModeTitle: {
    es: "Modo Ciencia",
    en: "Science mode",
    de: "Wissenschaftsmodus",
    ru: "Режим «Наука»",
  },
  scienceModeShort: {
    es: "Ciencia",
    en: "Science",
    de: "Wissenschaft",
    ru: "Наука",
  },
  scienceModeDescription: {
    es: "La suerte improvisa; la ciencia conquista.",
    en: "Luck improvises; science conquers.",
    de: "Das Glück improvisiert; die Wissenschaft siegt.",
    ru: "Удача импровизирует; наука побеждает.",
  },
  chanceModeTitle: {
    es: "Modo Azar",
    en: "Chance mode",
    de: "Zufallsmodus",
    ru: "Режим случайности",
  },
  chanceModeShort: {
    es: "Azar",
    en: "Chance",
    de: "Zufall",
    ru: "Случайность",
  },
  chanceModeDescription: {
    es: "La ciencia planea; el azar cambia el destino.",
    en: "Science plans; chance changes destiny.",
    de: "Die Wissenschaft plant; der Zufall verändert das Schicksal.",
    ru: "Наука планирует; случайность меняет судьбу.",
  },
  cienzarModeTitle: {
    es: "Modo Cienzar",
    en: "Cienzar mode",
    de: "Cienzar-Modus",
    ru: "Режим Cienzar",
  },
  cienzarModeDescription: {
    es: "El equilibrio entre ciencia y azar.",
    en: "The balance between science and chance.",
    de: "Das Gleichgewicht zwischen Wissenschaft und Zufall.",
    ru: "Баланс между наукой и случайностью.",
  },
  scienceVsAzarModeTitle: {
    es: "Ciencia V/S Azar",
    en: "Science V/S Chance",
    de: "Wissenschaft V/S Zufall",
    ru: "Science V/S Chance",
  },
  scienceVsAzarModeShort: {
    es: "Ciencia V/S Azar",
    en: "Science V/S Chance",
    de: "Wissenschaft V/S Zufall",
    ru: "Science V/S Chance",
  },
  scienceVsAzarModeDescription: {
    es: "Un jugador domina con ciencia; otro responde con azar.",
    en: "One player rules with science; another answers with chance.",
    de: "Ein Spieler plant mit Wissenschaft; ein anderer antwortet mit Zufall.",
    ru: "One player rules with science; another answers with chance.",
  },
  solvePuzzle: {
    es: "Resuelve puzzle",
    en: "Solve puzzle",
    de: "Rätsel lösen",
    ru: "Решить головоломку",
  },
  localGame: {
    es: "Juego local",
    en: "Local game",
    de: "Lokales Spiel",
    ru: "Локальная игра",
  },
  lobbyDesignToggle: {
    es: "Elige el diseño del lobby que más te guste",
    en: "Choose the lobby design you like best",
    de: "Wähle das Lobby-Design, das dir am besten gefällt",
    ru: "Выбери дизайн лобби, который тебе больше нравится",
  },
  lobbyDesignModern: {
    es: "Modo 3D",
    en: "3D mode",
    de: "3D-Modus",
    ru: "3D-режим",
  },
  lobbyDesignClassic: {
    es: "Clásico",
    en: "Classic",
    de: "Klassisch",
    ru: "Классика",
  },
  lobbyDesignLive: {
    es: "En vivo",
    en: "Live",
    de: "Live",
    ru: "В эфире",
  },
  lobbyDesignBoard: {
    es: "Tablero",
    en: "Board",
    de: "Brett",
    ru: "Доска",
  },
  play: {
    es: "Jugar",
    en: "Play",
    de: "Spielen",
    ru: "Играть",
  },
  playVsBot: {
    es: "Jugar con bot",
    en: "Play vs bot",
    de: "Gegen Bot spielen",
    ru: "Играть против бота",
  },
  findMatches: {
    es: "Buscar partidas",
    en: "Find matches",
    de: "Partien suchen",
    ru: "Найти партии",
  },
  pyramidAllTournaments: {
    es: "Todos los torneos",
    en: "All tournaments",
    de: "Alle Turniere",
    ru: "Все турниры",
  },
  pyramidCreateMatch: {
    es: "Crear juego",
    en: "Create game",
    de: "Spiel erstellen",
    ru: "Создать игру",
  },
  towerChampionship: { es: "Campeonato", en: "Championship", de: "Meisterschaft", ru: "Чемпионат" },
  towerStats: { es: "Estadísticas", en: "Statistics", de: "Statistiken", ru: "Статистика" },
  towerLanguage: { es: "Idioma", en: "Language", de: "Sprache", ru: "Язык" },
  towerSettings: { es: "Ajustes", en: "Settings", de: "Einstellungen", ru: "Настройки" },
  towerStore: { es: "Tienda", en: "Store", de: "Shop", ru: "Магазин" },
  towerFriends: { es: "Jugar con amigos", en: "Play with friends", de: "Mit Freunden spielen", ru: "Играть с друзьями" },
  towerDesign: { es: "Diseñar tablero", en: "Design board", de: "Brett gestalten", ru: "Дизайн доски" },
  towerSupport: { es: "Soporte", en: "Support", de: "Hilfe", ru: "Поддержка" },
  pyramidFindMatch: {
    es: "Buscar partida",
    en: "Find match",
    de: "Partie suchen",
    ru: "Найти партию",
  },
  quickPlayError: {
    es: "No se pudo entrar a jugar",
    en: "Could not start playing",
    de: "Spiel konnte nicht gestartet werden",
    ru: "Не удалось начать игру",
  },
  pyramidPuzzles: {
    es: "Puzzles",
    en: "Puzzles",
    de: "Rätsel",
    ru: "Головоломки",
  },
  topPlayers: {
    es: "Mejores Jugadores",
    en: "Top Players",
    de: "Beste Spieler",
    ru: "Лучшие игроки",
  },
  fullLeaderboard: {
    es: "Ver ranking completo >",
    en: "View full leaderboard >",
    de: "Vollständige Rangliste anzeigen >",
    ru: "Посмотреть полный рейтинг >",
  },
  globalChat: {
    es: "Chat Global",
    en: "Global Chat",
    de: "Globaler Chat",
    ru: "Общий чат",
  },
  clear: {
    es: "Limpiar",
    en: "Clear",
    de: "Leeren",
    ru: "Очистить",
  },
  chatPlaceholder: {
    es: "Escribe un mensaje...",
    en: "Type a message...",
    de: "Nachricht eingeben...",
    ru: "Введите сообщение...",
  },
  rankingLoading: {
    es: "Cargando ranking...",
    en: "Loading leaderboard...",
    de: "Rangliste wird geladen...",
    ru: "Загрузка рейтинга...",
  },
  noRankingData: {
    es: "Sin datos",
    en: "No data",
    de: "Keine Daten",
    ru: "Нет данных",
  },
  playerFallbackName: {
    es: "Jugador",
    en: "Player",
    de: "Spieler",
    ru: "Игрок",
  },
  systemSender: {
    es: "Sistema",
    en: "System",
    de: "System",
    ru: "Система",
  },
  viewAll: {
    es: "Ver todo",
    en: "View all",
    de: "Alle ansehen",
    ru: "Посмотреть все",
  },
});

const getLobbyCopy = (language, key, values = {}) => {
  const normalizedLanguage = normalizeLanguage(language);
  const entry = LOBBY_COPY[key] || {};
  let copy = entry[normalizedLanguage] || entry.es || entry.en || "";

  Object.entries(values || {}).forEach(([name, value]) => {
    copy = copy.split(`{${name}}`).join(String(value));
  });

  return copy;
};

const formatLobbyDate = (language, date) => {
  const normalizedLanguage = normalizeLanguage(language);
  const formattedDate = new Intl.DateTimeFormat(getLocaleForLanguage(normalizedLanguage), {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(date);

  if (normalizedLanguage === "ru") {
    return formattedDate.replace(/\s?г\.$/, " года");
  }

  return formattedDate;
};

const localizeGeneratedGuestName = (language, value, source = null) => {
  const raw = String(value || "").trim();
  const guestMatch = /^invitadoo?(\d+)$/i.exec(raw);
  if (!guestMatch) return raw;

  const sourcePayload = source && typeof source === "object" ? source : null;
  const hasGuestFlag = [
    sourcePayload?.isGuest,
    sourcePayload?.guest,
    sourcePayload?.isTemporary,
    sourcePayload?.temporary,
    sourcePayload?.accountMode,
    sourcePayload?.accountType,
    sourcePayload?.type,
  ].some((candidate) => {
    const normalized = String(candidate || "").trim().toLowerCase();
    return candidate === true || normalized === "guest" || normalized === "temporary";
  });
  const matchesStoredUsername = [
    sourcePayload?.username,
    sourcePayload?.userName,
    sourcePayload?.loginIdentifier,
  ].some((candidate) => String(candidate || "").trim().toLowerCase() === raw.toLowerCase());

  if (matchesStoredUsername && !hasGuestFlag) return raw;

  return getLobbyCopy(language, "generatedGuestName", { n: guestMatch[1] });
};

const LOBBY_BUTTON_CLUSTER_X = 24;
const LOBBY_BUTTON_CLUSTER_Y = 24;
const LOBBY_BUTTON_CLUSTER_GAP = 16;
const LOBBY_TILE_DISPLAY_SIZE = TILE_SIZE * LOBBY_BUTTON_DISPLAY_SCALE;
const LOBBY_RECT_BUTTON_DISPLAY_WIDTH = RECT_BUTTON_WIDTH * LOBBY_BUTTON_DISPLAY_SCALE;
const LOBBY_RECT_BUTTON_DISPLAY_HEIGHT = RECT_BUTTON_HEIGHT * LOBBY_BUTTON_DISPLAY_SCALE;
const LOBBY_RECT_BUTTON_SIZE_SCALE = 1;
const RESIZABLE_RECTANGLE_BUTTON_IDS = new Set(["12", "13", "14"]);
const RECTANGLE_BUTTONS_WITH_FIXED_WIDTH = new Set(["12", "13", "14"]);
const RECTANGLE_MODE_BOARD_IMAGE_SIZE = 200;
const RECTANGLE_MODE_ICON_SIZE = 150;
const RECTANGLE_MODE_PLAY_IMAGE_SIZE = 140;
const RECTANGLE_MODE_RIGHT_PANEL_SIZE = 200;
const DESKTOP_MODE_RECTANGLE_BUTTON_WIDTH = 400;
const DESKTOP_MODE_RECTANGLE_BUTTON_HEIGHT = 200;
const RECTANGLE_RESIZABLE_MIN_WIDTH = 120;
const RECTANGLE_RESIZABLE_MIN_HEIGHT = 35;
const MOBILE_LOBBY_LIZ_SQUARE_LAYOUT = {
  "04": { col: 0, row: 0.5 },
  "01": { col: 3, row: 0.5 },
  "02": { col: 2, row: 1.55 },
  "03": { col: 3, row: 1.55 },
  "05": { col: 1, row: 2.55 },
  "07": { col: 2, row: 2.55 },
  "06": { col: 3, row: 2.55 },
  "09": { col: 0, row: 3.55 },
  "10": { col: 1, row: 3.55 },
  "11": { col: 2, row: 3.55 },
  "08": { col: 3, row: 3.55 },
};
const RECTANGLE_RESIZE_HANDLES = [
  { id: "nw", symbol: "↖", position: "left-2 top-2", cursor: "nw-resize" },
  { id: "ne", symbol: "↗", position: "right-2 top-2", cursor: "ne-resize" },
  { id: "sw", symbol: "↙", position: "left-2 bottom-2", cursor: "sw-resize" },
  { id: "se", symbol: "↘", position: "right-2 bottom-2", cursor: "se-resize" },
];
const getLobbyLayoutBounds = (layout) => {
  let width = 0;
  let height = 0;

  layout.forEach((entry) => {
    width = Math.max(width, entry.x + entry.width);
    height = Math.max(height, entry.y + entry.height);
  });

  return {
    width: Math.max(1, width + LOBBY_BUTTON_CLUSTER_X + 24),
    height: Math.max(1, height + 24),
  };
};

const clampLobbyScale = (value, minValue, maxValue) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return minValue;
  return Math.min(maxValue, Math.max(minValue, numericValue));
};

const clampVideoScale = (value) => {
  const safe = Number(value);
  if (!Number.isFinite(safe)) return DEFAULT_VIDEO_SCALE;
  return Math.min(VIDEO_SCALE_MAX, Math.max(VIDEO_SCALE_MIN, safe));
};

const getTouchOnlyOrCoarsePointer = () => {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(pointer: coarse)").matches ||
    window.matchMedia("(hover: none)").matches
  );
};

const getXimaSocketUrl = () => {
  const envSocketUrl = String(process.env.REACT_APP_SOCKET_URL || "").trim();
  if (envSocketUrl) return envSocketUrl;
  if (typeof window === "undefined") return "http://localhost:5001";
  if (window.location.port === "3000") {
    return `${window.location.protocol}//${window.location.hostname}:5001`;
  }
  return window.location.origin;
};

const XIMA_SOCKET_URL = getXimaSocketUrl();
const CONNECTED_PLAYERS_COUNT_URL = `${XIMA_SOCKET_URL.replace(/\/+$/, "")}/api/connected-players`;

const handleLobbySessionInvalidated = () => {
  delete axios.defaults.headers["Authorization"];
  clearStoredAuthToken();
  if (typeof window !== "undefined" && window.location.pathname !== "/login") {
    window.location.assign("/login");
  }
};

const verifyLobbySocketSession = (socket) => {
  if (!socket || typeof socket.emit !== "function") return;
  const token = String(getStoredAuthToken() || "0");
  socket.emit("verify", token);
  socket.emit(ONLINE_PLAYERS_COUNT_REQUEST_EVENT);
  socket.emit(LEGACY_CONNECTED_PLAYERS_COUNT_REQUEST_EVENT);
};

const ensureLobbySharedSocket = () => {
  if (typeof window === "undefined") return null;

  const existingSocket = window.__ximaSocket || window.socket || null;
  if (existingSocket) {
    if (typeof existingSocket.off === "function") {
      existingSocket.off("SESSION_INVALIDATED", handleLobbySessionInvalidated);
    }
    if (typeof existingSocket.on === "function") {
      existingSocket.on("SESSION_INVALIDATED", handleLobbySessionInvalidated);
    }
    if (
      existingSocket.disconnected &&
      typeof existingSocket.connect === "function"
    ) {
      existingSocket.connect();
    }
    if (existingSocket.connected) {
      verifyLobbySocketSession(existingSocket);
    }
    window.__ximaSocket = existingSocket;
    window.socket = existingSocket;
    return existingSocket;
  }

  const sharedSocket = io(XIMA_SOCKET_URL, {
    transports: ["websocket", "polling"],
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 800,
    reconnectionDelayMax: 10000,
    randomizationFactor: 0.5,
    timeout: 20000,
  });

  sharedSocket.on("connect", () => {
    verifyLobbySocketSession(sharedSocket);
  });
  sharedSocket.on("SESSION_INVALIDATED", handleLobbySessionInvalidated);

  window.__ximaSocket = sharedSocket;
  window.socket = sharedSocket;
  return sharedSocket;
};

const normalizeConnectedPlayersCount = (payload) => {
  const rawCount =
    typeof payload === "number" || typeof payload === "string"
      ? payload
      : payload?.count;
  const numericCount = Number(rawCount);
  if (!Number.isFinite(numericCount)) return null;
  return Math.max(0, Math.floor(numericCount));
};

const normalizeConnectedPlayerEntry = (entry, index) => {
  const source =
    typeof entry === "string" || typeof entry === "number"
      ? { id: entry, name: entry }
      : entry;
  if (!source || typeof source !== "object") return null;

  const rawName =
    source.name ||
    source.displayName ||
    source.username ||
    source.userName ||
    source.userId ||
    source.identity ||
    source.id;
  const name = String(rawName || "")
    .trim()
    .replace(/\s+/g, " ")
    .slice(0, 42);
  if (!name) return null;

  const publicId = String(source.publicId || source.playerCode || "")
    .trim()
    .toUpperCase();
  const id = String(source.id || publicId || rawName || `${name}-${index}`).trim();
  return {
    id: id || `${name}-${index}`,
    publicId,
    playerCode: publicId,
    name,
    connectedAt: String(source.connectedAt || ""),
    isGuest: source.isGuest === true,
    canReceiveMessages: source.canReceiveMessages === true || Boolean(publicId),
  };
};

const normalizeConnectedPlayersList = (payload) => {
  const source = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.players)
      ? payload.players
      : Array.isArray(payload?.lastConnectedPlayers)
        ? payload.lastConnectedPlayers
        : Array.isArray(payload?.activePlayers)
          ? payload.activePlayers
          : null;

  if (!source) return null;

  const seen = new Set();
  return source
    .map((entry, index) => normalizeConnectedPlayerEntry(entry, index))
    .filter((entry) => {
      if (!entry) return false;
      const key = String(entry.id || entry.name).trim().toLowerCase();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, CONNECTED_PLAYERS_LIST_LIMIT);
};

const formatConnectedPlayersCount = (value, language = "es") => {
  return new Intl.NumberFormat(getLocaleForLanguage(language)).format(
    Math.max(0, Number(value) || 0),
  );
};

const formatLobbyNumber = (value, language = "es") =>
  new Intl.NumberFormat(getLocaleForLanguage(language)).format(
    Math.max(0, Math.round(Number(value) || 0)),
  );

const normalizeLobbyPositiveInteger = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return Math.floor(parsed);
};

const getMobileLobbyScale = () => {
  if (typeof window === "undefined") return 1;
  const isLandscape = window.innerWidth > window.innerHeight;
  if (!isLandscape || !getTouchOnlyOrCoarsePointer()) return 1;

  const usableWidth = Math.max(1, window.innerWidth - 10);
  const usableHeight = Math.max(1, window.innerHeight - 10);
  const widthScale = usableWidth / LOBBY_LAYOUT_BOUNDS.width;
  const heightScale = usableHeight / LOBBY_LAYOUT_BOUNDS.height;
  return clampLobbyScale(Math.min(widthScale, heightScale, 1), MOBILE_LOBBY_SCALE_MIN, 1);
};

const getMobileLobbyButtonScale = () => {
  if (typeof window === "undefined") return 1;
  return getTouchOnlyOrCoarsePointer() ? MOBILE_LOBBY_BUTTON_SCALE : 1;
};

const getLobbyViewportSize = () => {
  if (typeof window === "undefined") return { width: 0, height: 0 };
  return {
    width: window.innerWidth,
    height: window.innerHeight,
  };
};

const getLayoutBounds = (layout) => {
  if (!layout.length) {
    return {
      minX: 0,
      minY: 0,
      width: 1,
      height: 1,
    };
  }

  const minX = Math.min(...layout.map((entry) => entry.x));
  const minY = Math.min(...layout.map((entry) => entry.y));
  const maxX = Math.max(...layout.map((entry) => entry.x + entry.width));
  const maxY = Math.max(...layout.map((entry) => entry.y + entry.height));

  return {
    minX,
    minY,
    width: Math.max(1, maxX - minX),
    height: Math.max(1, maxY - minY),
  };
};

const buildMobilePortraitButtonRenderLayout = (layout, viewportSize) => {
  const viewportWidth = Math.max(320, Number(viewportSize?.width) || 390);
  const usableWidth = Math.max(
    240,
    viewportWidth - MOBILE_LOBBY_PORTRAIT_SIDE_PADDING * 2,
  );
  const renderLayout = new Map();
  const squareButtons = layout.filter(
    (entry) => !RESIZABLE_RECTANGLE_BUTTON_IDS.has(String(entry.id)),
  );
  const rectangleButtonsById = new Map(
    layout
      .filter((entry) => RESIZABLE_RECTANGLE_BUTTON_IDS.has(String(entry.id)))
      .map((entry) => [String(entry.id), entry]),
  );
  const maxSquareCol = Math.max(
    1,
    ...Object.values(MOBILE_LOBBY_LIZ_SQUARE_LAYOUT).map((entry) => entry.col),
  );
  const squareSize = clampLobbyScale(
    Math.min(
      LOBBY_TILE_DISPLAY_SIZE *
        MOBILE_LOBBY_PORTRAIT_SQUARE_SCALE *
        MOBILE_LOBBY_PORTRAIT_SQUARE_SIZE_BOOST,
      (usableWidth / 5.6) * MOBILE_LOBBY_PORTRAIT_SQUARE_SIZE_BOOST,
      usableWidth / (maxSquareCol + 1),
    ),
    46,
    LOBBY_TILE_DISPLAY_SIZE *
      MOBILE_LOBBY_PORTRAIT_SQUARE_SCALE *
      MOBILE_LOBBY_PORTRAIT_SQUARE_SIZE_BOOST,
  );
  const columnStep = Math.max(1, (usableWidth - squareSize) / maxSquareCol);
  const rowStep = columnStep;
  const squareTop = MOBILE_LOBBY_PORTRAIT_TOP_OFFSET;

  squareButtons.forEach((button) => {
    const lizPosition = MOBILE_LOBBY_LIZ_SQUARE_LAYOUT[String(button.id)];
    if (!lizPosition) return;

    renderLayout.set(String(button.id), {
      x: MOBILE_LOBBY_PORTRAIT_SIDE_PADDING + lizPosition.col * columnStep,
      y: squareTop + lizPosition.row * rowStep,
      width: squareSize,
      height: squareSize,
    });
  });

  const squareSectionBottom = Math.max(
    ...Array.from(renderLayout.values()).map((entry) => entry.y + entry.height),
    squareTop + squareSize,
  );
  const rectangleTop =
    squareSectionBottom + MOBILE_LOBBY_PORTRAIT_RECT_GAP;

  ["12", "14", "13"].forEach((id, index) => {
    if (!rectangleButtonsById.has(id)) return;
    renderLayout.set(id, {
      x: MOBILE_LOBBY_PORTRAIT_SIDE_PADDING,
      y:
        rectangleTop +
        index * (MOBILE_LOBBY_PORTRAIT_RECT_HEIGHT + MOBILE_LOBBY_PORTRAIT_RECT_GAP),
      width: usableWidth,
      height: MOBILE_LOBBY_PORTRAIT_RECT_HEIGHT,
    });
  });

  return renderLayout;
};

const buildBoardLayout = () => [
  { id: "01", icon: IoDiamondOutline, row: 0, col: 0 },
  { id: "02", icon: IoCubeOutline, row: 0, col: 1 },
  { id: "03", icon: IoGiftOutline, row: 0, col: 2 },
  { id: "04", icon: IoPeopleOutline, row: 0, col: 3 },
  { id: "05", icon: IoGiftOutline, row: 1, col: 0 },
  { id: "06", icon: IoFolderOpenOutline, row: 1, col: 1 },
  { id: "07", icon: IoHammerOutline, row: 1, col: 2 },
  { id: "08", icon: IoCogOutline, row: 1, col: 3 },
  { id: "09", icon: IoBookOutline, row: 2, col: 0 },
  { id: "10", icon: GiBinoculars, row: 2, col: 1 },
  { id: "11", icon: TbPuzzle, row: 2, col: 2 },
  {
    id: "12",
    icon: IoGameControllerOutline,
    x: LOBBY_BUTTON_CLUSTER_X,
    y:
      LOBBY_BUTTON_CLUSTER_Y +
      3 * (LOBBY_TILE_DISPLAY_SIZE + LOBBY_BUTTON_CLUSTER_GAP) +
      LOBBY_BUTTON_CLUSTER_GAP,
    width: LOBBY_RECT_BUTTON_DISPLAY_WIDTH * LOBBY_RECT_BUTTON_SIZE_SCALE,
    height: LOBBY_RECT_BUTTON_DISPLAY_HEIGHT * LOBBY_RECT_BUTTON_SIZE_SCALE,
  },
  {
    id: "13",
    icon: IoPeopleOutline,
    x: LOBBY_BUTTON_CLUSTER_X,
    y:
      LOBBY_BUTTON_CLUSTER_Y +
      3 * (LOBBY_TILE_DISPLAY_SIZE + LOBBY_BUTTON_CLUSTER_GAP) +
      LOBBY_BUTTON_CLUSTER_GAP * 2 +
      LOBBY_RECT_BUTTON_DISPLAY_HEIGHT * LOBBY_RECT_BUTTON_SIZE_SCALE,
    width: LOBBY_RECT_BUTTON_DISPLAY_WIDTH * LOBBY_RECT_BUTTON_SIZE_SCALE,
    height: LOBBY_RECT_BUTTON_DISPLAY_HEIGHT * LOBBY_RECT_BUTTON_SIZE_SCALE,
  },
  {
    id: "14",
    icon: IoRocketOutline,
    x: LOBBY_BUTTON_CLUSTER_X,
    y:
      LOBBY_BUTTON_CLUSTER_Y +
      3 * (LOBBY_TILE_DISPLAY_SIZE + LOBBY_BUTTON_CLUSTER_GAP) +
      LOBBY_BUTTON_CLUSTER_GAP * 3 +
      LOBBY_RECT_BUTTON_DISPLAY_HEIGHT * 2 * LOBBY_RECT_BUTTON_SIZE_SCALE,
    width: LOBBY_RECT_BUTTON_DISPLAY_WIDTH * LOBBY_RECT_BUTTON_SIZE_SCALE,
    height: LOBBY_RECT_BUTTON_DISPLAY_HEIGHT * LOBBY_RECT_BUTTON_SIZE_SCALE,
  },
].map((entry) => ({
  ...entry,
  x: Number(
    entry.x ??
      LOBBY_BUTTON_CLUSTER_X +
        Number(entry.col || 0) * (LOBBY_TILE_DISPLAY_SIZE + LOBBY_BUTTON_CLUSTER_GAP),
  ),
  y: Number(
    entry.y ??
      LOBBY_BUTTON_CLUSTER_Y +
        Number(entry.row || 0) * (LOBBY_TILE_DISPLAY_SIZE + LOBBY_BUTTON_CLUSTER_GAP),
  ),
  width: entry.width ?? LOBBY_TILE_DISPLAY_SIZE,
  height: entry.height ?? LOBBY_TILE_DISPLAY_SIZE,
}));

const LOBBY_LAYOUT_BOUNDS = getLobbyLayoutBounds(buildBoardLayout());
const RECTANGLE_BUTTON_IMAGE_MAP = {
  "05": "/assets/images/amigos-button.png",
  "07": "/assets/images/lobby-create-game-white.svg",
  "12": "/assets/images/foto-tablero-ciencia.png",
  "13": "/assets/images/fotoazar.png",
  "14": "/assets/images/fotocienzar.png",
  "15": "/assets/images/fotocienzar.png",
  "11": "/assets/images/puzzlesolomorado35.png",
};
const RECTANGLE_PLAY_BUTTON_IMAGE_SRC = "/assets/images/boton-play-verde-plano.png";
const RECTANGLE_BUTTON_LABEL_MAP = {
  "12": "CIENCIA",
  "13": "AZAR",
  "14": "CIENZAR",
  "15": "CONTRAZAR",
};
const RECTANGLE_PUZZLE_MODE_BY_ID = {
  "12": "free",
  "13": "dice",
  "14": "cienzar",
  "15": "sciencevsazar",
};
const RECTANGLE_TOURNAMENT_MODE_BY_ID = {
  "12": "free",
  "13": "dice",
  "14": "cienzar",
  "15": "sciencevsazar",
};
const PRIMARY_PLAY_DEFAULT_MODE_ID = "14";
const PRIMARY_PLAY_MODE_STORAGE_KEY = "xima_lobby_primary_play_mode_id";
const PRIMARY_PLAY_MODE_IDS = new Set(["12", "13", "14", "15"]);
const normalizePrimaryPlayModeId = (value) => {
  const normalized = String(value || "").trim();
  return PRIMARY_PLAY_MODE_IDS.has(normalized)
    ? normalized
    : PRIMARY_PLAY_DEFAULT_MODE_ID;
};
const readStoredPrimaryPlayModeId = () => {
  if (typeof window === "undefined") return PRIMARY_PLAY_DEFAULT_MODE_ID;
  try {
    return normalizePrimaryPlayModeId(
      window.localStorage.getItem(PRIMARY_PLAY_MODE_STORAGE_KEY),
    );
  } catch (error) {
    return PRIMARY_PLAY_DEFAULT_MODE_ID;
  }
};
const storePrimaryPlayModeId = (value) => {
  const normalized = normalizePrimaryPlayModeId(value);
  if (typeof window === "undefined") return normalized;
  try {
    window.localStorage.setItem(PRIMARY_PLAY_MODE_STORAGE_KEY, normalized);
  } catch (error) {
    // Ignore storage errors; the in-memory selection still works for this session.
  }
  return normalized;
};
const RECTANGLE_BUTTON_MODE_ICON_MAP = {
  "12": "/mode-icons/free.png",
  "13": "/mode-icons/logo-modo-azar.png",
  "14": "/mode-icons/cienzar.png",
  "15": "/mode-icons/logo-contrazar.png",
};
const RECTANGLE_BUTTON_PALETTE_OVERRIDE = {
  "12": {
    background: "linear-gradient(155deg, hsl(216 75% 24%), hsl(206 90% 14%))",
    ring: "0 0 0 1px hsl(200 90% 52% / 0.55)",
  },
  "13": {
    background: "linear-gradient(155deg, hsl(48 85% 28%), hsl(38 95% 16%))",
    ring: "0 0 0 1px hsl(48 95% 58% / 0.55)",
  },
  "14": {
    background: "linear-gradient(155deg, hsl(146 60% 24%), hsl(138 70% 14%))",
    ring: "0 0 0 1px hsl(147 92% 54% / 0.55)",
  },
  "15": {
    background: "linear-gradient(155deg, hsl(356 72% 30%), hsl(348 78% 15%))",
    ring: "0 0 0 1px hsl(356 92% 62% / 0.6)",
  },
};
const WHITE_RECTANGLE_BUTTON_IMAGE_IDS = new Set(["05", "07"]);
const LOBBY_BUTTON_LABEL_MAP = {
  "01": "PREMIUM",
  "03": "TIENDA",
  "05": "AMIGOS",
  "06": "HISTORIAL",
  "07": "CREAR JUEGO",
  "08": "AJUSTES",
  "09": "APRENDER",
  "10": "VER",
  "11": "PUZZLE",
};
const PROFILE_STORAGE_PREFIX = "xima_profile_v1_";
const MAX_AVATAR_FILE_SIZE_BYTES = 3 * 1024 * 1024;
const AVATAR_MAX_DATA_URL_LENGTH = 70000;
const AVATAR_MAX_DIMENSION = 192;
const AVATAR_QUALITY_STEPS = [0.82, 0.72, 0.62, 0.52, 0.42, 0.34];
const PROFILE_NAME_MAX_LENGTH = 80;
const PROFILE_CONTACT_EMAIL_MAX_LENGTH = 160;
const PROFILE_PHONE_MAX_LENGTH = 40;
const PROFILE_CONTACT_EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PROFILE_PHONE_PATTERN = /^[0-9+()\-\s]*$/;

const normalizeProfilePhoneInput = (value) =>
  String(value || "")
    .normalize("NFKC")
    .replace(/[\u200B-\u200D\uFEFF]/g, "")
    .replace(/[\u00A0\u202F]/g, " ")
    .replace(/[‐‑‒–—―]/g, "-")
    .replace(/[.\s]+/g, " ")
    .replace(/[^\d+()\-\s]/g, "")
    .replace(/\s+/g, " ")
    .slice(0, PROFILE_PHONE_MAX_LENGTH);

const resolvePublicAssetPath = (assetPath) => {
  const cleanPath = String(assetPath || "").replace(/^\/+/, "");
  const publicUrl = String(process.env.PUBLIC_URL || "").replace(/\/+$/, "");
  return `${publicUrl}/${cleanPath}`;
};
const STATIC_PROFILE_AVATAR_VERSION = "20260718-static-assets-fix";
const withStaticAssetVersion = (assetPath) => {
  const value = String(assetPath || "").trim();
  if (!value || value.startsWith("data:") || value.includes("?v=")) return value;
  return `${value}${value.includes("?") ? "&" : "?"}v=${STATIC_PROFILE_AVATAR_VERSION}`;
};
const resolveVersionedPublicAssetPath = (assetPath) =>
  withStaticAssetVersion(resolvePublicAssetPath(assetPath));
const stripAssetVersion = (value) => String(value || "").split("?")[0];

const LOBBY_DESIGN_SPECIAL_BLOCK_STORAGE_PREFIX = "xima_special_block_v1_";
const LOBBY_DESIGN_COMMON_BLOCK_STORAGE_PREFIX = "xima_common_block_v1_";
const LOBBY_DESIGN_GLOBAL_SPECIAL_BLOCK_KEY = "xima_special_block_selected";
const LOBBY_DESIGN_GLOBAL_SPECIAL_BLOCK_IMAGE_KEY = "xima_special_block_image";
const LOBBY_DESIGN_GLOBAL_BOARD_THEME_KEY = "xima_board_theme_selected";
const LOBBY_DESIGN_GLOBAL_COMMON_BLOCK_THEME_KEY = "xima_common_block_theme_selected";
const LOBBY_DESIGN_DEFAULT_SPECIAL_BLOCK_ID = "default_star";
const LOBBY_DESIGN_DEFAULT_COMMON_BLOCK_THEME_ID = "default_star";
const LOBBY_DESIGN_DEFAULT_BOARD_THEME_ID = "classic";
const LOBBY_DESIGN_MAX_SKIN_ID_LENGTH = 120;

const normalizeLobbyDesignSkinId = (value, fallback = "") => {
  let normalized = String(value || "").trim();

  if (!normalized) return fallback;
  normalized = normalized.replace(/^skins\//i, "");
  normalized = normalized.replace(/\.[a-z0-9]+$/i, "");

  if (!normalized) return fallback;
  if (normalized.length > LOBBY_DESIGN_MAX_SKIN_ID_LENGTH) return fallback;
  if (normalized.includes("..")) return fallback;
  if (normalized.includes("/") || normalized.includes("\\")) return fallback;

  return normalized;
};

const getLobbyDesignSkinLabelFromId = (skinId) => {
  const normalized = normalizeLobbyDesignSkinId(skinId, "");
  if (!normalized) return "Skin";

  return (
    normalized
      .replace(/[_-]+/g, " ")
      .replace(/\s+/g, " ")
      .trim() || "Skin"
  );
};

const getLobbyDesignSkinImageFromId = (skinId) => {
  const normalized = normalizeLobbyDesignSkinId(skinId, "");
  if (!normalized) return "";
  return resolvePublicAssetPath(`skins/${encodeURIComponent(`${normalized}.png`)}`);
};

const LOBBY_DESIGN_SKIN_PRESETS = [
  {
    id: "default_star",
    label: i18nT("profile.defaultStar"),
    image: resolvePublicAssetPath("special-blocks/star.svg"),
  },
  { id: "star", label: "Estrella", image: resolvePublicAssetPath("special-blocks/star.svg") },
  { id: "frog", label: "Rana", image: resolvePublicAssetPath("special-blocks/frog.svg") },
  { id: "snake", label: "Serpiente", image: resolvePublicAssetPath("special-blocks/snake.svg") },
  { id: "dragon", label: "Dragon", image: resolvePublicAssetPath("special-blocks/dragon.svg") },
  { id: "crystal", label: "Cristal", image: resolvePublicAssetPath("special-blocks/crystal.svg") },
  { id: "flame", label: "Fuego", image: resolvePublicAssetPath("special-blocks/flame.svg") },
];

const LOBBY_DESIGN_SKIN_PRESET_MAP = LOBBY_DESIGN_SKIN_PRESETS.reduce(
  (accumulator, preset) => {
    accumulator[preset.id] = preset;
    return accumulator;
  },
  {},
);

const WOOD_BOARD_PREVIEW_BACKGROUND =
  "radial-gradient(circle at 32% 24%, rgba(210,146,78,0.16), transparent 34%), linear-gradient(100deg, rgba(46,18,6,0.98), rgba(112,53,19,0.96) 48%, rgba(58,22,7,0.98))";

const LOBBY_DESIGN_BOARD_THEME_PRESET_MAP = {
  classic: { id: "classic", label: "Classic Neon", light: "#f2f0e8", dark: "#005c4a", glow: "#00be95" },
  cosmic: { id: "cosmic", label: "Cosmic Blue", light: "#f2f0e8", dark: "#082c7a", glow: "#2da0ee" },
  magma: { id: "magma", label: "Magma", light: "#f2f0e8", dark: "#7a260f", glow: "#e65c22" },
  emerald: { id: "emerald", label: "Madera", light: "#8a481a", dark: "#3f1708", glow: "#b7662d", previewBackground: WOOD_BOARD_PREVIEW_BACKGROUND },
  royal: { id: "royal", label: "Royal", light: "#f2f0e8", dark: "#3927a3", glow: "#7e66ee" },
  mono: { id: "mono", label: "Mono Night", light: "#f2f0e8", dark: "#293140", glow: "#8b9ab4" },
};

const getLobbyDesignSkinPreset = (id, fallbackId = LOBBY_DESIGN_DEFAULT_SPECIAL_BLOCK_ID) => {
  const normalized = normalizeLobbyDesignSkinId(id, fallbackId);
  if (LOBBY_DESIGN_SKIN_PRESET_MAP[normalized]) {
    return LOBBY_DESIGN_SKIN_PRESET_MAP[normalized];
  }

  return {
    id: normalized,
    label: getLobbyDesignSkinLabelFromId(normalized),
    image: getLobbyDesignSkinImageFromId(normalized),
  };
};

const getLobbyDesignBoardThemePreset = (id) =>
  LOBBY_DESIGN_BOARD_THEME_PRESET_MAP[id] ||
  LOBBY_DESIGN_BOARD_THEME_PRESET_MAP[LOBBY_DESIGN_DEFAULT_BOARD_THEME_ID];

const getLobbyDesignUserStorageKey = (user) => {
  const candidates = [user?.email, user?.loginIdentifier, user?.username, user?.userName, user?.id]
    .map((value) => String(value || "").trim().toLowerCase())
    .filter(Boolean);
  return candidates[0] || "guest";
};

const readLobbyDesignSpecialBlockId = (userStorageKey) => {
  if (typeof window === "undefined") return LOBBY_DESIGN_DEFAULT_SPECIAL_BLOCK_ID;
  const userScopedValue = normalizeLobbyDesignSkinId(
    window.localStorage.getItem(`${LOBBY_DESIGN_SPECIAL_BLOCK_STORAGE_PREFIX}${userStorageKey}`) || "",
    "",
  );
  const globalValue = normalizeLobbyDesignSkinId(
    window.localStorage.getItem(LOBBY_DESIGN_GLOBAL_SPECIAL_BLOCK_KEY) || "",
    "",
  );
  return userScopedValue || globalValue || LOBBY_DESIGN_DEFAULT_SPECIAL_BLOCK_ID;
};

const readLobbyDesignCommonBlockId = (userStorageKey) => {
  if (typeof window === "undefined") return LOBBY_DESIGN_DEFAULT_COMMON_BLOCK_THEME_ID;
  const userScopedValue = normalizeLobbyDesignSkinId(
    window.localStorage.getItem(`${LOBBY_DESIGN_COMMON_BLOCK_STORAGE_PREFIX}${userStorageKey}`) || "",
    "",
  );
  const globalValue = normalizeLobbyDesignSkinId(
    window.localStorage.getItem(LOBBY_DESIGN_GLOBAL_COMMON_BLOCK_THEME_KEY) || "",
    "",
  );
  return userScopedValue || globalValue || LOBBY_DESIGN_DEFAULT_COMMON_BLOCK_THEME_ID;
};

const readLobbyDesignBoardThemeId = () => {
  if (typeof window === "undefined") return LOBBY_DESIGN_DEFAULT_BOARD_THEME_ID;
  const storedValue = String(
    window.localStorage.getItem(LOBBY_DESIGN_GLOBAL_BOARD_THEME_KEY) || "",
  )
    .trim()
    .toLowerCase();
  return getLobbyDesignBoardThemePreset(storedValue).id;
};

const getLobbyDesignBoardPreviewStyle = (preset) => ({
  backgroundColor: preset.dark,
  backgroundImage:
    preset.previewBackground ||
    `linear-gradient(45deg, ${preset.light} 25%, transparent 25%, transparent 75%, ${preset.light} 75%), linear-gradient(45deg, ${preset.light} 25%, transparent 25%, transparent 75%, ${preset.light} 75%)`,
  backgroundPosition: preset.previewBackground ? "center, 0 0" : "0 0, 16px 16px",
  backgroundSize: preset.previewBackground ? "auto, 16px 16px" : "32px 32px",
  boxShadow: `0 0 26px ${preset.glow}66, inset 0 0 18px rgba(255,255,255,0.12)`,
});

const getProfileStorageKey = (email) => {
  const normalizedEmail = String(email || "guest").trim().toLowerCase();
  return `${PROFILE_STORAGE_PREFIX}${normalizedEmail || "guest"}`;
};

const parseStoredProfile = (rawValue) => {
  try {
    if (!rawValue) return null;
    const parsed = JSON.parse(rawValue);
    if (!parsed || typeof parsed !== "object") return null;
    return parsed;
  } catch {
    return null;
  }
};

const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Failed to read avatar file"));
    reader.readAsDataURL(file);
  });

const loadImageFromDataUrl = (dataUrl) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Failed to load avatar image"));
    image.src = dataUrl;
  });

const optimizeAvatarDataUrl = async (rawDataUrl) => {
  const dataUrl = String(rawDataUrl || "").trim();
  if (!dataUrl.startsWith("data:image/")) {
    return dataUrl.slice(0, AVATAR_MAX_DATA_URL_LENGTH);
  }

  if (dataUrl.length <= AVATAR_MAX_DATA_URL_LENGTH) {
    return dataUrl;
  }

  const image = await loadImageFromDataUrl(dataUrl);
  const maxEdge = Math.max(image.width || 1, image.height || 1);
  const scale = Math.min(1, AVATAR_MAX_DIMENSION / maxEdge);
  const width = Math.max(1, Math.round((image.width || 1) * scale));
  const height = Math.max(1, Math.round((image.height || 1) * scale));

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  if (!context) return dataUrl.slice(0, AVATAR_MAX_DATA_URL_LENGTH);
  context.drawImage(image, 0, 0, width, height);

  let bestCandidate = "";
  for (let i = 0; i < AVATAR_QUALITY_STEPS.length; i += 1) {
    const quality = AVATAR_QUALITY_STEPS[i];
    const candidate = canvas.toDataURL("image/jpeg", quality);
    if (!candidate) continue;
    bestCandidate = candidate;
    if (candidate.length <= AVATAR_MAX_DATA_URL_LENGTH) {
      return candidate;
    }
  }

  return String(bestCandidate || "").slice(0, AVATAR_MAX_DATA_URL_LENGTH);
};

const AI_AVATAR_PRESETS = [
  { id: "aurora", name: "Aurora", src: resolveVersionedPublicAssetPath("assets/profile-avatars/avatar-aurora.png") },
  { id: "ember", name: "Ember", src: resolveVersionedPublicAssetPath("assets/profile-avatars/avatar-ember.png") },
  { id: "verdant", name: "Verdant", src: resolveVersionedPublicAssetPath("assets/profile-avatars/avatar-verdant.png") },
  { id: "royal", name: "Royal", src: resolveVersionedPublicAssetPath("assets/profile-avatars/avatar-royal.png") },
  { id: "solar", name: "Solar", src: resolveVersionedPublicAssetPath("assets/profile-avatars/avatar-solar.png") },
  { id: "tide", name: "Tide", src: resolveVersionedPublicAssetPath("assets/profile-avatars/avatar-tide.png") },
];

const getLobbyStorageKey = (user) => {
  const candidates = [
    user?.id,
    user?.userId,
    user?.userCode,
    user?.loginIdentifier,
    user?.email,
    user?.username,
    user?.userName,
    user?.name,
  ]
    .map((value) => String(value || "").trim().toLowerCase())
    .filter(Boolean);
  const keySuffix = candidates[0] || "guest";
  return `${LOBBY_LAYOUT_STORAGE_PREFIX}:${keySuffix}`;
};

const normalizeNumber = (value, fallback) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : fallback;
};

const normalizeMobilePortraitButtons = (rawButtons) => {
  if (!rawButtons || typeof rawButtons !== "object") return null;

  const normalized = {};
  Object.entries(rawButtons).forEach(([id, entry]) => {
    if (!entry || typeof entry !== "object") return;

    const x = normalizeNumber(entry.x, null);
    const y = normalizeNumber(entry.y, null);
    const width = normalizeNumber(entry.width, null);
    const height = normalizeNumber(entry.height, null);
    if (
      x === null ||
      y === null ||
      width === null ||
      height === null ||
      width <= 0 ||
      height <= 0
    ) {
      return;
    }

    normalized[String(id)] = { x, y, width, height };
  });

  return Object.keys(normalized).length ? normalized : null;
};

const layoutMapToMobileSnapshot = (layoutMap) => {
  if (!layoutMap) return null;

  const snapshot = {};
  layoutMap.forEach((entry, id) => {
    snapshot[String(id)] = {
      x: entry.x,
      y: entry.y,
      width: entry.width,
      height: entry.height,
    };
  });

  return snapshot;
};

const mergeMobilePortraitRenderLayout = (generatedLayout, savedLayout) => {
  if (!generatedLayout) return null;

  const merged = new Map(generatedLayout);
  const normalizedSavedLayout = normalizeMobilePortraitButtons(savedLayout);
  if (!normalizedSavedLayout) return merged;

  Object.entries(normalizedSavedLayout).forEach(([id, entry]) => {
    if (!merged.has(id)) return;
    merged.set(id, entry);
  });

  return merged;
};

const applySavedLayoutToDefaults = (defaults, rawSnapshot) => {
  if (
    rawSnapshot &&
    Number.isFinite(Number(rawSnapshot.layoutVersion)) &&
    Number(rawSnapshot.layoutVersion) > LOBBY_LAYOUT_VERSION
  ) {
    return defaults;
  }

  const savedButtons = Array.isArray(rawSnapshot?.buttons)
    ? rawSnapshot.buttons
    : [];
  const savedById = new Map();
  savedButtons.forEach((entry) => {
    if (!entry?.id) return;
    savedById.set(String(entry.id), entry);
  });
  return defaults.map((entry) => {
    const saved = savedById.get(String(entry.id));
    if (!saved) return entry;

    const rawX = normalizeNumber(saved.x, entry.x);
    const rawY = normalizeNumber(saved.y, entry.y);
    const rawWidth = normalizeNumber(saved.width, entry.width);
    const rawHeight = normalizeNumber(saved.height, entry.height);

    const minWidth = RESIZABLE_RECTANGLE_BUTTON_IDS.has(String(entry.id))
      ? RECTANGLE_RESIZABLE_MIN_WIDTH
      : Math.min(TILE_SIZE, Math.max(1, entry.width));
    const minHeight = RESIZABLE_RECTANGLE_BUTTON_IDS.has(String(entry.id))
      ? RECTANGLE_RESIZABLE_MIN_HEIGHT
      : Math.min(TILE_SIZE, Math.max(1, entry.height));

    const sanitizeNumber = (value, fallback, minValue) => {
      if (!Number.isFinite(value) || value < minValue) return fallback;
      return value;
    };

    const nextWidth = sanitizeNumber(rawWidth, entry.width, minWidth);
    const nextHeight = sanitizeNumber(rawHeight, entry.height, minHeight);

    return {
      ...entry,
      x: Math.max(0, rawX),
      y: Math.max(0, rawY),
      width: nextWidth,
      height: nextHeight,
    };
  });
};

const loadLobbySnapshot = (storageKey) => {
  if (typeof window === "undefined" || !storageKey) return null;
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return parsed;
  } catch {
    return null;
  }
};

const saveLobbySnapshot = (storageKey, buttons, videoPosition, mobilePortraitButtons) => {
  if (typeof window === "undefined" || !storageKey) return;
  try {
    const normalizedVideoPosition = {
      ...videoPosition,
      scale: normalizeNumber(videoPosition?.scale, DEFAULT_VIDEO_SCALE),
    };
    const normalizedMobilePortraitButtons =
      normalizeMobilePortraitButtons(mobilePortraitButtons);
    const snapshot = {
      layoutVersion: LOBBY_LAYOUT_VERSION,
      savedAt: Date.now(),
      buttons: buttons.map((entry) => ({
        id: entry.id,
        x: entry.x,
        y: entry.y,
        width: entry.width,
        height: entry.height,
      })),
      videoPosition: normalizedVideoPosition,
      mobilePortraitLayoutVersion: MOBILE_LOBBY_PORTRAIT_LAYOUT_VERSION,
      mobilePortraitButtons: normalizedMobilePortraitButtons,
    };
    window.localStorage.setItem(storageKey, JSON.stringify(snapshot));
  } catch {
    // ignore write errors (private mode/full storage limits/etc).
  }
};

const getAdminAccess = (user) => {
  if (user?.isAdmin === true) return true;
  const candidates = [
    user?.role,
    user?.type,
    user?.permission,
    user?.accountType,
    user?.accessLevel,
  ]
    .map((value) => String(value || "").trim().toLowerCase())
    .filter(Boolean);
  return candidates.some((value) => value === "admin" || value === "superuser");
};

const createGlobalChatEntry = (rawPayload) => {
  const payload = rawPayload && typeof rawPayload === "object" ? rawPayload : {};
  const text = String(payload.text || payload.message || "")
    .trim()
    .slice(0, GLOBAL_CHAT_MAX_LENGTH);
  if (!text) return null;

  const sender = String(payload.sender || payload.displayName || payload.user || "Jugador").trim();
  const senderId = String(payload.senderId || payload.userId || payload.user || "").trim();
  const createdAtIso = new Date(payload.createdAt || Date.now()).toISOString();
  const id =
    String(payload.id || "").trim() ||
    `${senderId || sender || "guest"}:${createdAtIso}:${text.slice(0, 24)}`;

  return {
    id,
    sender: sender || "Jugador",
    senderId,
    text,
    createdAt: createdAtIso,
  };
};

const parseStoredGlobalChatMessages = () => {
  if (typeof window === "undefined") return [];
  try {
    const raw = sessionStorage.getItem(GLOBAL_CHAT_SESSION_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((entry) => createGlobalChatEntry(entry))
      .filter(Boolean)
      .slice(-GLOBAL_CHAT_MAX_MESSAGES);
  } catch {
    return [];
  }
};

const getButtonPalette = (id) => {
  if (RECTANGLE_BUTTON_PALETTE_OVERRIDE[id]) {
    return RECTANGLE_BUTTON_PALETTE_OVERRIDE[id];
  }
  const order = Number.parseInt(String(id || "01"), 10);
  const hue = (order * 29) % 360;
  return {
    background: `linear-gradient(155deg, hsl(${hue} 65% 22%), hsl(${hue + 35} 90% 12%))`,
    ring: `0 0 0 1px hsl(${hue} 80% 50% / 0.55)`,
  };
};

const getLobbyUserProfileImage = (user) => {
  const candidates = [
    user?.currentAvatarUrl,
    user?.profileAvatar,
    user?.avatar,
    user?.avatarUrl,
    user?.avatar_url,
    user?.photo,
    user?.photoUrl,
    user?.photo_url,
    user?.profileImage,
    user?.profile_image,
    user?.profile?.avatarUrl,
    user?.profile?.avatar_url,
    user?.profilePicture,
    user?.profile_picture,
    user?.picture,
    user?.pictureUrl,
    user?.picture_url,
    user?.image,
    user?.imageUrl,
    user?.image_url,
    user?.img,
    user?.imgUrl,
    user?.img_url,
    user?.foto,
    user?.profilePhoto,
    user?.profilephoto,
    user?.profile_pic,
    user?.photoURL,
    user?.profile?.avatar,
    user?.profile?.profileAvatar,
    user?.profile?.currentAvatarUrl,
    user?.profile?.picture,
    user?.profile?.photoUrl,
    user?.profile?.image,
  ];

  const directProfileImage = candidates
    .map((candidate) => (candidate || "").toString().trim())
    .find(Boolean);

  if (directProfileImage) return resolveAvatarSrc(directProfileImage);

  if (typeof window === "undefined") return "";

  const storageCandidates = [user?.email, user?.loginIdentifier, user?.username, user?.userName].map(
    (value) => String(value || "").trim().toLowerCase(),
  );
  const storedProfileKey = storageCandidates
    .map((keyValue) => (keyValue ? `${PROFILE_STORAGE_PREFIX}${keyValue}` : ""))
    .find((keyValue) => keyValue && window.localStorage.getItem(keyValue));

  if (!storedProfileKey) return "";

  try {
    const rawProfile = JSON.parse(window.localStorage.getItem(storedProfileKey));
    return resolveAvatarSrc(
      rawProfile?.currentAvatarUrl ||
      rawProfile?.profileAvatar ||
      rawProfile?.avatarUrl ||
      rawProfile?.avatar ||
      rawProfile?.avatar_url ||
      rawProfile?.imageUrl ||
      rawProfile?.image_url ||
      rawProfile?.photoUrl ||
      rawProfile?.photo_url ||
      rawProfile?.pictureUrl ||
      rawProfile?.picture_url ||
      rawProfile?.image ||
      "",
    );
  } catch {
    return "";
  }
};

const getLobbyUserDisplayName = (user) => {
  const normalizePublicName = (value) => {
    const raw = String(value || "").trim();
    if (!raw) return "";
    if (raw.includes("@")) return String(raw.split("@")[0] || "").trim();
    return raw;
  };
  const directCandidates = [
    user?.name,
    user?.displayName,
    user?.fullName,
    user?.username,
    user?.userName,
    user?.loginIdentifier,
  ]
    .map(normalizePublicName)
    .filter(Boolean);

  if (directCandidates.length > 0) return directCandidates[0];

  const firstName = normalizePublicName(user?.firstName);
  const lastName = normalizePublicName(user?.lastName);
  const fullName = `${firstName} ${lastName}`.trim();
  if (fullName) return fullName;

  const fallback = normalizePublicName(user?.email);
  return fallback || "USUARIO";
};

const getLobbyUserInitials = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "P";
  const parts = raw.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0]?.[0] || ""}${parts[1]?.[0] || ""}`.toUpperCase();
};

const normalizeLobbyWalletBalance = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 0;
  return Math.max(0, Math.round(parsed));
};

const getLobbyUserWalletBalance = (source) => {
  const candidates = [
    source?.walletBalance,
    source?.wallet?.balance,
    source?.bonuses,
    source?.bonusBalance,
    source?.bonusAmount,
    source?.earnedBonuses,
    source?.rewardBalance,
    source?.rewardsBalance,
  ];
  const value = candidates.find((candidate) => Number.isFinite(Number(candidate)));
  return normalizeLobbyWalletBalance(value);
};

const formatLobbyWalletBalance = (value, language = "es") =>
  `${formatLobbyNumber(normalizeLobbyWalletBalance(value), language)}X`;

const LOBBY_RECTANGLE_CIMAX_SCORE_KEYS = {
  "12": ["ciencia", "science", "cimaxCiencia", "scienceCimax", "cienciaCimax"],
  "14": ["cienzar", "cimaxCienzar", "cienzarCimax"],
  "15": ["sciencevsazar", "contrazar", "cimaxContrazar"],
  "13": ["azar", "random", "luck", "cimaxAzar", "azarCimax"],
};

const normalizeLobbyCimaxScore = (value) => {
  const source =
    value && typeof value === "object"
      ? value.score ?? value.cimax ?? value.value ?? value.points ?? value.total
      : value;
  const numericValue = Number(source);
  if (!Number.isFinite(numericValue)) return 0;
  return Math.max(0, Math.round(numericValue));
};

const getLobbyGlobalCimaxRank = (profile) => {
  const rankPayload =
    profile?.globalCimaxRank ||
    profile?.globalCimaxRanking ||
    profile?.globalRank ||
    null;
  const rank =
    rankPayload && typeof rankPayload === "object"
      ? rankPayload.rank ?? rankPayload.position ?? rankPayload.globalPosition
      : rankPayload ?? profile?.globalCimaxRankPosition ?? profile?.globalPosition;
  return normalizeLobbyPositiveInteger(rank);
};

const getLobbyGlobalCimaxTotal = (profile, fallbackScores = {}) => {
  const rankPayload =
    profile?.globalCimaxRank ||
    profile?.globalCimaxRanking ||
    profile?.globalRank ||
    null;
  const explicitTotal =
    rankPayload && typeof rankPayload === "object"
      ? rankPayload.total ?? rankPayload.score ?? rankPayload.points
      : profile?.globalCimaxTotal;
  if (Number.isFinite(Number(explicitTotal))) {
    return normalizeLobbyCimaxScore(explicitTotal);
  }

  const cimaxScores = profile?.cimaxScores || profile?.cimax || null;
  if (cimaxScores && typeof cimaxScores === "object") {
    return (
      getLobbyCimaxScore(profile, "12") +
      getLobbyCimaxScore(profile, "14") +
      getLobbyCimaxScore(profile, "15") +
      getLobbyCimaxScore(profile, "13")
    );
  }

  return (
    normalizeLobbyCimaxScore(fallbackScores?.["12"]) +
    normalizeLobbyCimaxScore(fallbackScores?.["14"]) +
    normalizeLobbyCimaxScore(fallbackScores?.["15"]) +
    normalizeLobbyCimaxScore(fallbackScores?.["13"])
  );
};

const formatLobbyGlobalRankLabel = (rank, language = "es") => {
  const normalizedRank = normalizeLobbyPositiveInteger(rank);
  if (!normalizedRank) return getLobbyCopy(language, "unrankedWorldwide");
  return getLobbyCopy(language, "rankWorldwide", {
    rank: formatLobbyNumber(normalizedRank, language),
  });
};

const getLobbyCimaxScore = (profile, buttonId) => {
  const keys = LOBBY_RECTANGLE_CIMAX_SCORE_KEYS[String(buttonId)] || [];
  const scoreSources = [
    profile?.cimaxScores,
    profile?.cimax,
    profile?.cimaxScore,
    profile?.ximaxScores,
    profile,
  ].filter((source) => source && typeof source === "object");

  for (const source of scoreSources) {
    for (const key of keys) {
      if (source[key] !== undefined && source[key] !== null) {
        return normalizeLobbyCimaxScore(source[key]);
      }
    }
  }

  return 0;
};

const formatLobbyCimaxScore = (value, language = "es") =>
  `${formatLobbyNumber(normalizeLobbyCimaxScore(value), language)} CIMAX`;

const LOBBY_MODE_WINNERS_SECTION_ORDER = [
  {
    key: "ciencia",
    title: "CIENCIA",
    aliases: ["science", "cimaxCiencia", "scienceCimax", "cienciaCimax"],
    icon: "/mode-icons/free.png",
    panelClass: "border-sky-400/70 bg-sky-950/65",
    titleClass: "text-sky-200",
    badgeClass: "border-sky-300/50 bg-sky-500/15 text-sky-100",
  },
  {
    key: "cienzar",
    title: "CIENZAR",
    aliases: ["cimaxCienzar", "cienzarCimax"],
    icon: "/mode-icons/cienzar.png",
    panelClass: "border-emerald-400/70 bg-emerald-950/65",
    titleClass: "text-emerald-200",
    badgeClass: "border-emerald-300/50 bg-emerald-500/15 text-emerald-100",
  },
  {
    key: "sciencevsazar",
    title: "CONTRAZAR",
    aliases: ["contrazar", "cimaxContrazar"],
    icon: "/mode-icons/logo-contrazar.png",
    panelClass: "border-rose-400/70 bg-rose-950/65",
    titleClass: "text-rose-100",
    badgeClass: "border-rose-300/50 bg-rose-500/15 text-rose-100",
  },
  {
    key: "azar",
    title: "AZAR",
    aliases: ["random", "luck", "cimaxAzar", "azarCimax"],
    icon: "/mode-icons/logo-modo-azar.png",
    panelClass: "border-amber-300/70 bg-amber-950/65",
    titleClass: "text-amber-100",
    badgeClass: "border-amber-200/50 bg-amber-500/15 text-amber-100",
  },
];

const buildEmptyLobbyModeWinnersMap = () =>
  LOBBY_MODE_WINNERS_SECTION_ORDER.reduce((accumulator, section) => {
    accumulator[section.key] = [];
    return accumulator;
  }, {});

const ensureLobbyArray = (value) => (Array.isArray(value) ? value : []);

const getLobbyModeWinnersEntries = (modes, section) => {
  const keys = [section?.key, ...(Array.isArray(section?.aliases) ? section.aliases : [])].filter(
    Boolean,
  );
  for (const key of keys) {
    const entries = ensureLobbyArray(modes?.[key]);
    if (entries.length > 0) return entries;
  }
  return [];
};

const getLobbyLeaderboardDisplayName = (entry) => {
  const raw = String(
    entry?.displayName ||
      entry?.firstName ||
      entry?.username ||
      entry?.userName ||
      entry?.email ||
      "",
  ).trim();

  if (!raw) return "Jugador";
  if (raw.includes("@")) {
    return String(raw.split("@")[0] || "Jugador").trim() || "Jugador";
  }

  return raw;
};

const normalizeLobbyModeWinners = (modes) => {
  const next = buildEmptyLobbyModeWinnersMap();

  LOBBY_MODE_WINNERS_SECTION_ORDER.forEach((section) => {
    next[section.key] = getLobbyModeWinnersEntries(modes, section)
      .map((entry, index) => {
        const displayName = getLobbyLeaderboardDisplayName(entry);
        return {
          ...entry,
          rank: Math.max(1, Number(entry?.rank) || index + 1),
          displayName,
          avatarUrl: resolveAvatarSrc(entry?.avatarUrl || entry?.avatar || entry?.image || ""),
          cimaxScore: normalizeLobbyCimaxScore(entry?.cimaxScore),
        };
      })
      .slice(0, 3);
  });

  return next;
};

const Lobby = () => {
  const { user, refreshUserProfile } = useAuth();
  const navigate = useNavigate();
  const outletContext = useOutletContext() || {};
  const language = normalizeLanguage(outletContext.language);
  const lobbyText = useCallback(
    (key, values) => getLobbyCopy(language, key, values),
    [language],
  );

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = language;
    }
  }, [language]);

  const currentUserProfileIdentitySet = useMemo(
    () => buildCurrentUserProfileIdentitySet(user),
    [
      user?._id,
      user?.email,
      user?.id,
      user?.loginIdentifier,
      user?.playerCode,
      user?.userId,
      user?.userName,
      user?.username,
    ],
  );
  const [lobbyProfileData, setLobbyProfileData] = useState(null);
  const [firstLobbyTutorialStatus, setFirstLobbyTutorialStatus] = useState(null);
  const [firstLobbyTutorialOpen, setFirstLobbyTutorialOpen] = useState(false);
  const [firstLobbyTutorialSaving, setFirstLobbyTutorialSaving] = useState(false);
  const [guidedTutorialMenuOpen, setGuidedTutorialMenuOpen] = useState(false);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [profileAvatar, setProfileAvatar] = useState("");
  const [profileDraftAvatar, setProfileDraftAvatar] = useState("");
  const [profileDraftAvatarFile, setProfileDraftAvatarFile] = useState(null);
  const [profileFirstName, setProfileFirstName] = useState("");
  const [profileLastName, setProfileLastName] = useState("");
  const [profileContactEmail, setProfileContactEmail] = useState("");
  const [profilePhone, setProfilePhone] = useState("");
  const [profileDraftFirstName, setProfileDraftFirstName] = useState("");
  const [profileDraftLastName, setProfileDraftLastName] = useState("");
  const [profileDraftContactEmail, setProfileDraftContactEmail] = useState("");
  const [profileDraftPhone, setProfileDraftPhone] = useState("");
  const [profileDraftCurrentPassword, setProfileDraftCurrentPassword] = useState("");
  const [profileDraftNewPassword, setProfileDraftNewPassword] = useState("");
  const [profileDraftConfirmPassword, setProfileDraftConfirmPassword] = useState("");
  const [profileSavingPersonal, setProfileSavingPersonal] = useState(false);
  const profileStorageKey = useMemo(() => getProfileStorageKey(user?.email), [user?.email]);
  const mergedLobbyUserProfile = useMemo(
    () => ({
      ...(user || {}),
      ...(lobbyProfileData || {}),
      ...(profileFirstName ? { firstName: profileFirstName } : {}),
      ...(profileLastName ? { lastName: profileLastName } : {}),
      ...(profileAvatar
        ? {
            avatar: profileAvatar,
            avatarUrl: profileAvatar,
            currentAvatarUrl: profileAvatar,
            profileAvatar,
          }
        : {}),
    }),
    [lobbyProfileData, profileAvatar, profileFirstName, profileLastName, user],
  );
  const canEditLobbyLayout = useMemo(() => {
    if (!getAdminAccess(mergedLobbyUserProfile)) return false;

    const normalizeIdentityValue = (value) =>
      String(value ?? "")
        .trim()
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");

    const identityCandidates = [
      mergedLobbyUserProfile?.username,
      mergedLobbyUserProfile?.userName,
      mergedLobbyUserProfile?.name,
      mergedLobbyUserProfile?.nombre,
      mergedLobbyUserProfile?.displayName,
      mergedLobbyUserProfile?.fullName,
      mergedLobbyUserProfile?.email,
      mergedLobbyUserProfile?.correo,
    ]
      .filter(Boolean)
      .flatMap((value) => {
        const normalized = normalizeIdentityValue(value);
        const emailName = normalized.includes("@")
          ? normalized.split("@")[0]
          : normalized;
        return [normalized, emailName];
      });

    return identityCandidates.some(
      (value) => value === "yosdel" || value === "yosde",
    );
  }, [mergedLobbyUserProfile]);
  const hasLobbyGlobalChatAdminAccess = useMemo(
    () => getAdminAccess(mergedLobbyUserProfile),
    [mergedLobbyUserProfile],
  );
  const lobbyProfileImage = useMemo(
    () => getLobbyUserProfileImage(mergedLobbyUserProfile),
    [mergedLobbyUserProfile],
  );
  const lobbyUserName = useMemo(
    () => getLobbyUserDisplayName(mergedLobbyUserProfile),
    [mergedLobbyUserProfile],
  );
  const lobbyUserInitials = useMemo(() => getLobbyUserInitials(lobbyUserName), [lobbyUserName]);
  const globalChatSenderId = useMemo(() => {
    const candidates = [
      mergedLobbyUserProfile?.loginIdentifier,
      mergedLobbyUserProfile?.username,
      mergedLobbyUserProfile?.userName,
      mergedLobbyUserProfile?.email,
      mergedLobbyUserProfile?.userId,
      mergedLobbyUserProfile?.id,
      mergedLobbyUserProfile?._id,
    ];
    return (
      candidates
        .map((candidate) => String(candidate || "").trim().toLowerCase())
        .find(Boolean) || "guest"
    );
  }, [mergedLobbyUserProfile]);
  const serverAvatarUrl = useMemo(
    () => getLobbyUserProfileImage({ ...(user || {}), ...(lobbyProfileData || {}) }),
    [lobbyProfileData, user],
  );
  const currentAvatarUrl = profileAvatar || serverAvatarUrl;
  const profileDisplayName = useMemo(() => {
    return getLobbyUserDisplayName({
      ...(mergedLobbyUserProfile || {}),
      firstName: profileFirstName || mergedLobbyUserProfile?.firstName,
      lastName: profileLastName || mergedLobbyUserProfile?.lastName,
    });
  }, [mergedLobbyUserProfile, profileFirstName, profileLastName]);
  const lobbyCimaxScores = useMemo(
    () => ({
      "12": getLobbyCimaxScore(mergedLobbyUserProfile, "12"),
      "14": getLobbyCimaxScore(mergedLobbyUserProfile, "14"),
      "15": getLobbyCimaxScore(mergedLobbyUserProfile, "15"),
      "13": getLobbyCimaxScore(mergedLobbyUserProfile, "13"),
    }),
    [mergedLobbyUserProfile],
  );
  const lobbyGlobalCimaxRank = useMemo(
    () => getLobbyGlobalCimaxRank(mergedLobbyUserProfile),
    [mergedLobbyUserProfile],
  );
  const lobbyGlobalCimaxRankLabel = useMemo(
    () => formatLobbyGlobalRankLabel(lobbyGlobalCimaxRank, language),
    [language, lobbyGlobalCimaxRank],
  );
  const lobbyGlobalCimaxTotal = useMemo(
    () => getLobbyGlobalCimaxTotal(mergedLobbyUserProfile, lobbyCimaxScores),
    [lobbyCimaxScores, mergedLobbyUserProfile],
  );
  const [lobbyWalletBalance, setLobbyWalletBalance] = useState(() =>
    getLobbyUserWalletBalance(user),
  );
  const [lobbyModeWinnersByMode, setLobbyModeWinnersByMode] = useState(() =>
    buildEmptyLobbyModeWinnersMap(),
  );
  const [lobbyModeWinnersLoading, setLobbyModeWinnersLoading] = useState(false);
  const [activeLobbyRankingModeKey, setActiveLobbyRankingModeKey] = useState("ciencia");
  const [connectedPlayersCount, setConnectedPlayersCount] = useState(0);
  const [connectedPlayers, setConnectedPlayers] = useState([]);
  const [connectedPlayersListOpen, setConnectedPlayersListOpen] = useState(false);
  const [directMessageUnreadSummary, setDirectMessageUnreadSummary] = useState(
    readPublishedDirectMessageUnreadSummary
  );
  const [globalChatMessages, setGlobalChatMessages] = useState(() =>
    parseStoredGlobalChatMessages(),
  );
  const [globalChatInput, setGlobalChatInput] = useState("");
  const storageKey = useMemo(() => getLobbyStorageKey(user), [
    user?.email,
    user?.id,
    user?.loginIdentifier,
    user?.name,
    user?.userCode,
    user?.userId,
    user?.userName,
    user?.username,
  ]);
  const containerRef = useRef(null);
  const [buttons, setButtons] = useState(() => buildBoardLayout());
  const [dragState, setDragState] = useState(null);
  const [videoPos, setVideoPos] = useState({
    x: 0,
    y: 0,
    isAbsolute: false,
    scale: DEFAULT_VIDEO_SCALE,
  });
  const [mobileLobbyScale, setMobileLobbyScale] = useState(1);
  const [mobileLobbyButtonScale, setMobileLobbyButtonScale] = useState(1);
  const [lobbyViewportSize, setLobbyViewportSize] = useState(() => getLobbyViewportSize());
  const [mobilePortraitButtons, setMobilePortraitButtons] = useState(null);
  const [selectedButtonId, setSelectedButtonId] = useState(null);
  const [, setPrimaryPlayModeId] = useState(() =>
    readStoredPrimaryPlayModeId(),
  );
  const [showLobbyEmptyInterface, setShowLobbyEmptyInterface] = useState(false);
  const [showLobbyPuzzleInterface, setShowLobbyPuzzleInterface] = useState(false);
  const [lobbyBoard3DStatus, setLobbyBoard3DStatus] = useState("loading");
  const [lobbyDesign, setLobbyDesign] = useState(() => {
    try {
      const stored = window.localStorage.getItem(LOBBY_DESIGN_STORAGE_KEY);
      // Players who never chose a design start with En vivo (2026-10-25); a saved choice is kept.
      return stored === "board" || stored === "classic" || stored === "modern" || stored === "live" ? stored : "live";
    } catch (error) {
      return "live";
    }
  });
  const chooseLobbyDesign = (next) => {
    if (next === lobbyDesign) return;
    try {
      window.localStorage.setItem(LOBBY_DESIGN_STORAGE_KEY, next);
    } catch (error) {
      // Without storage the choice only lasts until the page reloads.
    }
    setLobbyDesign(next);
    setLobbyBoard3DStatus("loading");
  };
  // The Tablero design is hidden: only the site owner can still pick it.
  const isLobbyOwner = isOwnerAccount(user);
  useEffect(() => {
    if (!isLobbyOwner && lobbyDesign === "board") setLobbyDesign("modern");
  }, [isLobbyOwner, lobbyDesign]);

  // The Moderno design brings its own side menu, so the app menu hides while it is open.
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    document.body.classList.toggle(
      "xima-lobby-modern-active",
      lobbyDesign === "modern" || lobbyDesign === "live",
    );
    return () => document.body.classList.remove("xima-lobby-modern-active");
  }, [lobbyDesign]);
  const lobbyDesignUserStorageKey = useMemo(() => getLobbyDesignUserStorageKey(user), [
    user?.email,
    user?.id,
    user?.loginIdentifier,
    user?.userName,
    user?.username,
  ]);
  const [lobbyDesignSpecialBlockId, setLobbyDesignSpecialBlockId] = useState(() =>
    readLobbyDesignSpecialBlockId(getLobbyDesignUserStorageKey(user)),
  );
  const [lobbyDesignCommonBlockId, setLobbyDesignCommonBlockId] = useState(() =>
    readLobbyDesignCommonBlockId(getLobbyDesignUserStorageKey(user)),
  );
  const [lobbyDesignBoardThemeId, setLobbyDesignBoardThemeId] = useState(() =>
    readLobbyDesignBoardThemeId(),
  );
  const [lobbyDraftSpecialBlockId, setLobbyDraftSpecialBlockId] = useState(
    lobbyDesignSpecialBlockId,
  );
  const [lobbyDraftCommonBlockId, setLobbyDraftCommonBlockId] = useState(
    lobbyDesignCommonBlockId,
  );
  const [lobbyDraftBoardThemeId, setLobbyDraftBoardThemeId] = useState(
    lobbyDesignBoardThemeId,
  );
  const [lobbySpecialBlockModalOpen, setLobbySpecialBlockModalOpen] = useState(false);
  const [lobbyCommonBlocksModalOpen, setLobbyCommonBlocksModalOpen] = useState(false);
  const [lobbyBoardThemeModalOpen, setLobbyBoardThemeModalOpen] = useState(false);
  const layoutHydratedRef = useRef(false);
  const [layoutReady, setLayoutReady] = useState(false);
  const mobileLobbyScaleRef = useRef(1);
  const mobileLobbyButtonScaleRef = useRef(1);
  const mobileLongPressTimerRef = useRef(null);
  const buttonSoundAudioRef = useRef(null);
  const lastButtonHoverAtRef = useRef({});
  const globalChatScrollRef = useRef(null);
  const lobbyDesignSpecialBlock = useMemo(
    () => getLobbyDesignSkinPreset(lobbyDesignSpecialBlockId),
    [lobbyDesignSpecialBlockId],
  );
  const lobbyDesignCommonBlock = useMemo(
    () =>
      getLobbyDesignSkinPreset(
        lobbyDesignCommonBlockId,
        LOBBY_DESIGN_DEFAULT_COMMON_BLOCK_THEME_ID,
      ),
    [lobbyDesignCommonBlockId],
  );
  const lobbyDesignBoardTheme = useMemo(
    () => getLobbyDesignBoardThemePreset(lobbyDesignBoardThemeId),
    [lobbyDesignBoardThemeId],
  );
  const visibleConnectedPlayersCount = Math.max(
    0,
    Number(connectedPlayersCount) || 0,
  );
  const connectedPlayersCountLabel = useMemo(
    () => formatConnectedPlayersCount(visibleConnectedPlayersCount, language),
    [language, visibleConnectedPlayersCount],
  );
  const visibleConnectedPlayers = useMemo(
    () => connectedPlayers.slice(0, CONNECTED_PLAYERS_LIST_LIMIT),
    [connectedPlayers],
  );
  const currentLobbyPlayerCode = String(mergedLobbyUserProfile?.playerCode || "")
    .trim()
    .toUpperCase();
  const directMessageUnreadTotal = Math.max(
    0,
    Math.floor(Number(directMessageUnreadSummary?.total) || 0),
  );
  const directMessageUnreadByPlayerCode =
    directMessageUnreadSummary?.byPlayerCode &&
    typeof directMessageUnreadSummary.byPlayerCode === "object"
      ? directMessageUnreadSummary.byPlayerCode
      : {};
  const openDirectMessageComposer = useCallback((event, player) => {
    event.stopPropagation();
    if (typeof window === "undefined" || !player?.publicId) return;
    window.dispatchEvent(
      new CustomEvent(DIRECT_MESSAGE_OPEN_EVENT, {
        detail: { player },
      })
    );
  }, []);
  const toggleConnectedPlayersList = useCallback(() => {
    setConnectedPlayersListOpen((isOpen) => !isOpen);
  }, []);
  const handleConnectedPlayersKeyDown = useCallback(
    (event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        toggleConnectedPlayersList();
        return;
      }
      if (event.key === "Escape") {
        setConnectedPlayersListOpen(false);
      }
    },
    [toggleConnectedPlayersList],
  );

  const renderConnectedPlayersWidget = (variant) => (
    <div
      className={`xima-connected-pill is-${variant}${connectedPlayersListOpen ? " is-open" : ""}`}
      role="button"
      tabIndex={0}
      aria-expanded={connectedPlayersListOpen}
      aria-haspopup="true"
      onClick={toggleConnectedPlayersList}
      onKeyDown={handleConnectedPlayersKeyDown}
    >
      <span>{lobbyText("connectedPlayers")}</span>
      <strong aria-live="polite">{connectedPlayersCountLabel}</strong>
      {directMessageUnreadTotal > 0 ? (
        <span
          className="xima-connected-unread-badge"
          aria-label={`${directMessageUnreadTotal} mensajes sin leer`}
        >
          {Math.min(99, directMessageUnreadTotal)}
        </span>
      ) : null}
      {connectedPlayersListOpen ? (
        <div
          className="xima-connected-list"
          role="dialog"
          aria-label={lobbyText("lastConnectedPlayers")}
          onClick={(event) => event.stopPropagation()}
        >
          <div className="xima-connected-list-title">
            {lobbyText("lastConnectedPlayers")}
          </div>
          <div className="xima-connected-list-body" aria-live="polite">
            {visibleConnectedPlayers.length > 0 ? (
              visibleConnectedPlayers.map((player) => (
                <div className="xima-connected-player" key={player.id}>
                  <span className="xima-connected-player-dot" aria-hidden="true" />
                  <span className="xima-connected-player-name">{player.name}</span>
                  {Number(
                    directMessageUnreadByPlayerCode[String(player.publicId || "").trim()]
                  ) > 0 ? (
                    <span className="xima-connected-player-unread" aria-label="Mensajes sin leer">
                      {Math.min(
                        99,
                        Number(
                          directMessageUnreadByPlayerCode[
                            String(player.publicId || "").trim()
                          ]
                        ) || 0,
                      )}
                    </span>
                  ) : null}
                  {canShowConnectedPlayerMessage(player, currentLobbyPlayerCode) ? (
                    <button
                      type="button"
                      className="xima-connected-player-message"
                      aria-label={`Enviar mensaje a ${player.name}`}
                      title={`Enviar mensaje a ${player.name}`}
                      onClick={(event) => openDirectMessageComposer(event, player)}
                    >
                      <IoMailOutline aria-hidden="true" />
                    </button>
                  ) : null}
                </div>
              ))
            ) : (
              <div className="xima-connected-empty">
                {lobbyText("noConnectedPlayers")}
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const applyUnreadSummary = (value) => {
      const source = value && typeof value === "object" ? value : {};
      setDirectMessageUnreadSummary({
        total: Math.max(0, Math.floor(Number(source.total) || 0)),
        byPlayerCode:
          source.byPlayerCode && typeof source.byPlayerCode === "object"
            ? { ...source.byPlayerCode }
            : {},
      });
    };
    const handleUnreadSummary = (event) => applyUnreadSummary(event?.detail);
    applyUnreadSummary(readPublishedDirectMessageUnreadSummary());
    window.addEventListener(DIRECT_MESSAGE_UNREAD_EVENT, handleUnreadSummary);
    return () => window.removeEventListener(DIRECT_MESSAGE_UNREAD_EVENT, handleUnreadSummary);
  }, []);

  const appendGlobalChatMessage = useCallback((rawPayload) => {
    const nextEntry = createGlobalChatEntry(rawPayload);
    if (!nextEntry) return;

    setGlobalChatMessages((previousMessages) => {
      if (previousMessages.some((entry) => entry.id === nextEntry.id)) {
        return previousMessages;
      }
      const nextMessages = [...previousMessages, nextEntry];
      if (nextMessages.length <= GLOBAL_CHAT_MAX_MESSAGES) return nextMessages;
      return nextMessages.slice(nextMessages.length - GLOBAL_CHAT_MAX_MESSAGES);
    });
  }, []);

  const replaceGlobalChatMessages = useCallback((messages) => {
    const source = Array.isArray(messages) ? messages : [];
    const normalized = source
      .map((entry) => createGlobalChatEntry(entry))
      .filter(Boolean)
      .slice(-GLOBAL_CHAT_MAX_MESSAGES);
    setGlobalChatMessages(normalized);
  }, []);

  const handleGlobalChatSend = useCallback(() => {
    const trimmed = String(globalChatInput || "").trim();
    if (!trimmed) return;

    const chatSocket = ensureLobbySharedSocket();
    if (!chatSocket || typeof chatSocket.emit !== "function") return;

    chatSocket.emit(GLOBAL_CHAT_EVENT_NAME, {
      id: `local:${Date.now()}:${Math.random().toString(36).slice(2, 8)}`,
      sender: lobbyUserName || "Jugador",
      senderId: globalChatSenderId || "guest",
      text: trimmed.slice(0, GLOBAL_CHAT_MAX_LENGTH),
      createdAt: new Date().toISOString(),
    });
    setGlobalChatInput("");
  }, [globalChatInput, globalChatSenderId, lobbyUserName]);

  const handleClearGlobalChat = useCallback(() => {
    if (!hasLobbyGlobalChatAdminAccess) return;
    const chatSocket = ensureLobbySharedSocket();
    if (!chatSocket || typeof chatSocket.emit !== "function") return;
    chatSocket.emit(GLOBAL_CHAT_CLEAR_EVENT_NAME, {
      requestedAt: new Date().toISOString(),
    });
  }, [hasLobbyGlobalChatAdminAccess]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    let activeSocket = null;
    let disposed = false;
    let retryTimer = null;
    let countPollTimer = null;
    let unbindListeners = () => {};

    const handleConnectedPlayersCount = (payload) => {
      const nextCount = normalizeConnectedPlayersCount(payload);
      if (nextCount === null) return;
      const nextPlayers = normalizeConnectedPlayersList(payload);
      setConnectedPlayersCount((previous) =>
        previous === nextCount ? previous : nextCount,
      );
      if (nextPlayers) {
        setConnectedPlayers(nextPlayers);
      }
    };

    const requestConnectedPlayersCountOverHttp = async () => {
      try {
        const response = await fetch(CONNECTED_PLAYERS_COUNT_URL, {
          method: "GET",
          cache: "no-store",
        });
        if (!response.ok) return;
        const payload = await response.json();
        handleConnectedPlayersCount(payload);
      } catch {
        // Socket.IO remains the main realtime channel; this is only a quiet fallback.
      }
    };

    const bindSocketListeners = (socket) => {
      if (!socket || typeof socket.on !== "function") return null;
      activeSocket = socket;

      const requestConnectedPlayersCount = () => {
        if (typeof socket.emit === "function") {
          verifyLobbySocketSession(socket);
        }
      };

      if (typeof socket.off === "function") {
        CONNECTED_PLAYERS_COUNT_EVENTS.forEach((eventName) => {
          socket.off(eventName, handleConnectedPlayersCount);
        });
        socket.off("connect", requestConnectedPlayersCount);
      }

      CONNECTED_PLAYERS_COUNT_EVENTS.forEach((eventName) => {
        socket.on(eventName, handleConnectedPlayersCount);
      });
      socket.on("connect", requestConnectedPlayersCount);
      requestConnectedPlayersCount();

      return () => {
        if (typeof socket.off === "function") {
          CONNECTED_PLAYERS_COUNT_EVENTS.forEach((eventName) => {
            socket.off(eventName, handleConnectedPlayersCount);
          });
          socket.off("connect", requestConnectedPlayersCount);
        }
      };
    };

    const tryAttach = () => {
      if (disposed) return;
      const socketCandidate = ensureLobbySharedSocket();
      if (!socketCandidate || activeSocket === socketCandidate) return;
      unbindListeners();
      const nextUnbind = bindSocketListeners(socketCandidate);
      unbindListeners = typeof nextUnbind === "function" ? nextUnbind : () => {};
    };

    tryAttach();
    requestConnectedPlayersCountOverHttp();
    retryTimer = window.setInterval(tryAttach, 1500);
    countPollTimer = window.setInterval(requestConnectedPlayersCountOverHttp, 10000);

    return () => {
      disposed = true;
      if (retryTimer) window.clearInterval(retryTimer);
      if (countPollTimer) window.clearInterval(countPollTimer);
      unbindListeners();
      activeSocket = null;
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const chatSocket = ensureLobbySharedSocket();
    if (!chatSocket || typeof chatSocket.on !== "function") return undefined;

    const handleIncomingGlobalChat = (payload) => appendGlobalChatMessage(payload);
    const handleGlobalChatHistory = (historyPayload) =>
      replaceGlobalChatMessages(historyPayload);
    const handleGlobalChatRejected = () => {
      notification.warning({
        message: i18nT("lobby.mensajeBloqueado"),
        description: i18nT("lobby.tuMensajeContieneLenguajeNoPermitidoYNo"),
      });
    };
    const handleGlobalChatCleared = () => {
      setGlobalChatMessages([]);
      notification.success({
        message: i18nT("lobby.chatGlobalLimpiado"),
        description: i18nT("lobby.losMensajesFueronEliminadosPorUnAdministradocbb4bd"),
      });
    };
    const handleGlobalChatClearRejected = (payload) => {
      const reason = String(payload?.reason || "").trim();
      notification.warning({
        message: i18nT("lobby.noAutorizado"),
        description:
          reason || "Solo administradores o superusuarios pueden limpiar el chat global.",
      });
    };

    if (typeof chatSocket.off === "function") {
      chatSocket.off(GLOBAL_CHAT_EVENT_NAME, handleIncomingGlobalChat);
      chatSocket.off(GLOBAL_CHAT_HISTORY_EVENT_NAME, handleGlobalChatHistory);
      chatSocket.off("GLOBAL_CHAT_REJECTED", handleGlobalChatRejected);
      chatSocket.off(GLOBAL_CHAT_CLEARED_EVENT_NAME, handleGlobalChatCleared);
      chatSocket.off(GLOBAL_CHAT_CLEAR_REJECTED_EVENT_NAME, handleGlobalChatClearRejected);
    }

    chatSocket.on(GLOBAL_CHAT_EVENT_NAME, handleIncomingGlobalChat);
    chatSocket.on(GLOBAL_CHAT_HISTORY_EVENT_NAME, handleGlobalChatHistory);
    chatSocket.on("GLOBAL_CHAT_REJECTED", handleGlobalChatRejected);
    chatSocket.on(GLOBAL_CHAT_CLEARED_EVENT_NAME, handleGlobalChatCleared);
    chatSocket.on(GLOBAL_CHAT_CLEAR_REJECTED_EVENT_NAME, handleGlobalChatClearRejected);
    if (typeof chatSocket.emit === "function") {
      chatSocket.emit(GLOBAL_CHAT_HISTORY_REQUEST_EVENT_NAME);
    }

    return () => {
      if (typeof chatSocket.off !== "function") return;
      chatSocket.off(GLOBAL_CHAT_EVENT_NAME, handleIncomingGlobalChat);
      chatSocket.off(GLOBAL_CHAT_HISTORY_EVENT_NAME, handleGlobalChatHistory);
      chatSocket.off("GLOBAL_CHAT_REJECTED", handleGlobalChatRejected);
      chatSocket.off(GLOBAL_CHAT_CLEARED_EVENT_NAME, handleGlobalChatCleared);
      chatSocket.off(GLOBAL_CHAT_CLEAR_REJECTED_EVENT_NAME, handleGlobalChatClearRejected);
    };
  }, [appendGlobalChatMessage, replaceGlobalChatMessages]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      sessionStorage.setItem(GLOBAL_CHAT_SESSION_KEY, JSON.stringify(globalChatMessages));
    } catch {
      // Ignore storage limitations; the socket history remains authoritative.
    }
  }, [globalChatMessages]);

  useEffect(() => {
    const scrollContainer = globalChatScrollRef.current;
    if (!scrollContainer) return;
    scrollContainer.scrollTop = scrollContainer.scrollHeight;
  }, [globalChatMessages]);

  useEffect(() => {
    let cancelled = false;
    setLobbyProfileData(null);
    setLobbyWalletBalance(getLobbyUserWalletBalance(user));

    const loadLobbyWalletBalance = async () => {
      try {
        const response = await axios.get("/auth/profile");
        if (cancelled) return;
        setLobbyProfileData(response?.data || null);
        setLobbyWalletBalance(getLobbyUserWalletBalance(response?.data));
        setProfileFirstName(
          String(response?.data?.firstName || "")
            .trim()
            .slice(0, PROFILE_NAME_MAX_LENGTH),
        );
        setProfileLastName(
          String(response?.data?.lastName || "")
            .trim()
            .slice(0, PROFILE_NAME_MAX_LENGTH),
        );
        setProfileContactEmail(
          String(response?.data?.contactEmail || "")
            .trim()
            .toLowerCase()
            .slice(0, PROFILE_CONTACT_EMAIL_MAX_LENGTH),
        );
        setProfilePhone(normalizeProfilePhoneInput(response?.data?.phone).trim());
      } catch {
        // Keep the token/local fallback when the profile cannot be loaded.
      }
    };

    if (user) {
      loadLobbyWalletBalance();
    }

    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    let cancelled = false;
    setFirstLobbyTutorialStatus(null);
    setFirstLobbyTutorialOpen(false);

    const loadFirstLobbyTutorialStatus = async () => {
      try {
        const response = await axios.get("/auth/onboarding/first-lobby");
        if (cancelled) return;
        const status = response?.data || null;
        setFirstLobbyTutorialStatus(status);
        setFirstLobbyTutorialOpen(Boolean(status?.shouldPrompt));
      } catch {
        // Do not interrupt the lobby when onboarding status cannot be verified.
      }
    };

    if (user) {
      loadFirstLobbyTutorialStatus();
    }

    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    const syncLobbyDesignFromStorage = () => {
      const nextSpecialBlockId = readLobbyDesignSpecialBlockId(lobbyDesignUserStorageKey);
      const nextCommonBlockId = readLobbyDesignCommonBlockId(lobbyDesignUserStorageKey);
      const nextBoardThemeId = readLobbyDesignBoardThemeId();

      setLobbyDesignSpecialBlockId(nextSpecialBlockId);
      setLobbyDraftSpecialBlockId(nextSpecialBlockId);
      setLobbyDesignCommonBlockId(nextCommonBlockId);
      setLobbyDraftCommonBlockId(nextCommonBlockId);
      setLobbyDesignBoardThemeId(nextBoardThemeId);
      setLobbyDraftBoardThemeId(nextBoardThemeId);
    };

    const handleSpecialBlockChange = (event) => {
      const incomingId = normalizeLobbyDesignSkinId(
        event?.detail?.imageName || event?.detail?.id || "",
        "",
      );
      const nextId = incomingId || readLobbyDesignSpecialBlockId(lobbyDesignUserStorageKey);
      setLobbyDesignSpecialBlockId(nextId);
      setLobbyDraftSpecialBlockId(nextId);
    };

    const handleCommonBlockChange = (event) => {
      const incomingId = normalizeLobbyDesignSkinId(
        event?.detail?.imageName || event?.detail?.id || "",
        "",
      );
      const nextId = incomingId || readLobbyDesignCommonBlockId(lobbyDesignUserStorageKey);
      setLobbyDesignCommonBlockId(nextId);
      setLobbyDraftCommonBlockId(nextId);
    };

    const handleBoardThemeChange = (event) => {
      const incomingId = String(event?.detail?.id || "").trim().toLowerCase();
      const nextId = getLobbyDesignBoardThemePreset(incomingId).id;
      setLobbyDesignBoardThemeId(nextId);
      setLobbyDraftBoardThemeId(nextId);
    };

    syncLobbyDesignFromStorage();

    if (typeof window === "undefined") return undefined;
    window.addEventListener("xima-special-block-change", handleSpecialBlockChange);
    window.addEventListener("xima-common-block-theme-change", handleCommonBlockChange);
    window.addEventListener("xima-board-theme-change", handleBoardThemeChange);

    return () => {
      window.removeEventListener("xima-special-block-change", handleSpecialBlockChange);
      window.removeEventListener("xima-common-block-theme-change", handleCommonBlockChange);
      window.removeEventListener("xima-board-theme-change", handleBoardThemeChange);
    };
  }, [lobbyDesignUserStorageKey]);

  useEffect(() => {
    setProfileAvatar(String(serverAvatarUrl || "").trim());
  }, [serverAvatarUrl]);

  useEffect(() => {
    let cancelled = false;

    const loadLobbyModeWinners = async () => {
      setLobbyModeWinnersLoading(true);
      try {
        const response = await axios.get("/tournaments/leaderboard/modes");
        if (cancelled) return;
        setLobbyModeWinnersByMode(normalizeLobbyModeWinners(response?.data?.modes || {}));
      } catch (error) {
        console.log(error);
      } finally {
        if (!cancelled) {
          setLobbyModeWinnersLoading(false);
        }
      }
    };

    loadLobbyModeWinners();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const updateScale = () => {
      const nextButtonScale = getMobileLobbyButtonScale();
      const nextViewportSize = getLobbyViewportSize();
      const nextScale = getMobileLobbyScale();
      if (Math.abs(nextScale - mobileLobbyScaleRef.current) > 0.001) {
        mobileLobbyScaleRef.current = nextScale;
        setMobileLobbyScale(nextScale);
      } else {
        mobileLobbyScaleRef.current = nextScale;
      }

      if (Math.abs(nextButtonScale - mobileLobbyButtonScaleRef.current) > 0.001) {
        mobileLobbyButtonScaleRef.current = nextButtonScale;
        setMobileLobbyButtonScale(nextButtonScale);
      } else {
        mobileLobbyButtonScaleRef.current = nextButtonScale;
      }

      setLobbyViewportSize((previous) =>
        previous.width === nextViewportSize.width && previous.height === nextViewportSize.height
          ? previous
          : nextViewportSize,
      );
    };

    updateScale();
    window.addEventListener("resize", updateScale);
    return () => window.removeEventListener("resize", updateScale);
  }, []);

  useEffect(() => {
    if (!dragState || !containerRef.current) return undefined;
    const boardScale = Math.max(0.0001, mobileLobbyScaleRef.current);
    const buttonScale = Math.max(
      0.0001,
      mobileLobbyScaleRef.current * mobileLobbyButtonScaleRef.current,
    );

    const handleMouseMove = (event) => {
      if (dragState.kind === "mobile-button") return;
      const activeButtonScaleMultiplier =
        dragState.kind === "button" || dragState.kind === "button-resize"
          ? Math.max(1, Number(dragState.scaleMultiplier || 1))
          : 1;
      const activeButtonScale = Math.max(
        0.0001,
        buttonScale * activeButtonScaleMultiplier,
      );
      event.preventDefault();
      if (
        dragState.kind === "button-resize"
          ? event.buttons !== 1 && event.buttons !== 2
          : event.buttons !== 2
      ) {
        setDragState(null);
        return;
      }

      const rect = containerRef.current.getBoundingClientRect();
      const buttonPointerX = (event.clientX - rect.left) / activeButtonScale;
      const buttonPointerY = (event.clientY - rect.top) / activeButtonScale;
      const normalizedPointerX = (event.clientX - rect.left) / boardScale;
      const normalizedPointerY = (event.clientY - rect.top) / boardScale;
      const nextPointerX = Math.max(0, buttonPointerX);
      const nextPointerY = Math.max(0, buttonPointerY);
      const nextX = Math.max(0, nextPointerX - dragState.offsetX);
      const nextY = Math.max(0, nextPointerY - dragState.offsetY);

      if (dragState.kind === "button") {
        setButtons((previous) =>
          previous.map((button) =>
            button.id !== dragState.id
              ? button
              : {
                  ...button,
                  x: nextX,
                  y: nextY,
                }
          )
        );
      } else if (dragState.kind === "button-resize") {
        setButtons((previous) =>
          previous.map((button) => {
            if (button.id !== dragState.id) return button;

            const fixedRight = dragState.startX + dragState.startWidth;
            const fixedBottom = dragState.startY + dragState.startHeight;
            let width = button.width;
            let height = button.height;
            let x = button.x;
            let y = button.y;

            if (dragState.anchor === "nw") {
              width = Math.max(
                RECTANGLE_RESIZABLE_MIN_WIDTH,
                fixedRight - nextPointerX,
              );
              height = Math.max(
                RECTANGLE_RESIZABLE_MIN_HEIGHT,
                fixedBottom - nextPointerY,
              );
              x = fixedRight - width;
              y = fixedBottom - height;
            } else if (dragState.anchor === "ne") {
              width = Math.max(
                RECTANGLE_RESIZABLE_MIN_WIDTH,
                nextPointerX - dragState.startX,
              );
              height = Math.max(
                RECTANGLE_RESIZABLE_MIN_HEIGHT,
                fixedBottom - nextPointerY,
              );
              x = dragState.startX;
              y = fixedBottom - height;
            } else if (dragState.anchor === "sw") {
              width = Math.max(
                RECTANGLE_RESIZABLE_MIN_WIDTH,
                fixedRight - nextPointerX,
              );
              height = Math.max(
                RECTANGLE_RESIZABLE_MIN_HEIGHT,
                nextPointerY - dragState.startY,
              );
              x = fixedRight - width;
              y = dragState.startY;
            } else {
              width = Math.max(
                RECTANGLE_RESIZABLE_MIN_WIDTH,
                nextPointerX - dragState.startX,
              );
              height = Math.max(
                RECTANGLE_RESIZABLE_MIN_HEIGHT,
                nextPointerY - dragState.startY,
              );
              x = dragState.startX;
              y = dragState.startY;
            }

            return {
              ...button,
              x: Math.max(0, x),
              y: Math.max(0, y),
              width,
              height,
            };
          })
        );
      } else if (dragState.kind === "video") {
        setVideoPos((previous) => ({
          x: Math.max(0, normalizedPointerX - dragState.offsetX),
          y: Math.max(0, normalizedPointerY - dragState.offsetY),
          isAbsolute: true,
          scale: previous.scale ?? DEFAULT_VIDEO_SCALE,
        }));
      }
    };

    const handleTouchMove = (event) => {
      if (dragState.kind !== "mobile-button") return;
      const touch = event.touches?.[0];
      if (!touch) {
        setDragState(null);
        return;
      }

      event.preventDefault();
      const rect = containerRef.current.getBoundingClientRect();
      const pointerX = (touch.clientX - rect.left) / boardScale;
      const pointerY = (touch.clientY - rect.top) / boardScale;
      const nextX = Math.max(0, pointerX - dragState.offsetX);
      const nextY = Math.max(0, pointerY - dragState.offsetY);

      setMobilePortraitButtons((previous) => ({
        ...(previous || {}),
        [dragState.id]: {
          x: nextX,
          y: nextY,
          width: dragState.width,
          height: dragState.height,
        },
      }));
    };

    const handleMouseUp = () => {
      setDragState(null);
    };

    const handleTouchEnd = () => {
      if (dragState.kind === "mobile-button") {
        setDragState(null);
      }
    };

    const touchListenerOptions = { passive: false };
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    document.addEventListener("touchmove", handleTouchMove, touchListenerOptions);
    document.addEventListener("touchend", handleTouchEnd);
    document.addEventListener("touchcancel", handleTouchEnd);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("touchmove", handleTouchMove, touchListenerOptions);
      document.removeEventListener("touchend", handleTouchEnd);
      document.removeEventListener("touchcancel", handleTouchEnd);
    };
  }, [dragState]);

  useEffect(() => {
    if (!storageKey) return;
    layoutHydratedRef.current = false;
    setLayoutReady(false);
    setButtons(buildBoardLayout());
    setVideoPos({ x: 0, y: 0, isAbsolute: false, scale: DEFAULT_VIDEO_SCALE });
    setMobilePortraitButtons(null);

    const snapshot = loadLobbySnapshot(storageKey);
    if (!snapshot) {
      setLayoutReady(true);
      layoutHydratedRef.current = true;
      return;
    }

    setButtons((previous) => applySavedLayoutToDefaults(previous, snapshot));
    const snapshotVideo = snapshot?.videoPosition || snapshot?.video || {};
    const snapshotVideoScale = clampVideoScale(
      normalizeNumber(snapshotVideo.scale, DEFAULT_VIDEO_SCALE),
    );
    const nextVideoPos = {
      x: normalizeNumber(snapshotVideo.x, 0),
      y: normalizeNumber(snapshotVideo.y, 0),
      isAbsolute: Boolean(snapshotVideo?.isAbsolute),
      scale: snapshotVideoScale,
    };
    setVideoPos((previous) => ({
      ...previous,
      ...nextVideoPos,
    }));
    setMobilePortraitButtons(
      snapshot?.mobilePortraitLayoutVersion === MOBILE_LOBBY_PORTRAIT_LAYOUT_VERSION
        ? normalizeMobilePortraitButtons(snapshot?.mobilePortraitButtons)
        : null,
    );
    setLayoutReady(true);
    layoutHydratedRef.current = true;
  }, [storageKey]);

  useEffect(() => {
    if (!layoutReady || !layoutHydratedRef.current || !storageKey) return;
    const onBeforeUnload = () =>
      saveLobbySnapshot(storageKey, buttons, videoPos, mobilePortraitButtons);
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [storageKey, layoutReady, buttons, videoPos, mobilePortraitButtons]);

  useEffect(() => {
    if (!layoutReady || !layoutHydratedRef.current || !storageKey) return;
    if (dragState !== null) return;
    saveLobbySnapshot(storageKey, buttons, videoPos, mobilePortraitButtons);
  }, [buttons, videoPos, mobilePortraitButtons, dragState, layoutReady, storageKey]);

  useEffect(() => {
    return () => {
      if (storageKey && layoutHydratedRef.current) {
        saveLobbySnapshot(storageKey, buttons, videoPos, mobilePortraitButtons);
      }
    };
  }, [buttons, videoPos, mobilePortraitButtons, storageKey]);

  const playLobbyButtonAssetSound = (volume = 0.55) => {
    if (typeof window === "undefined") return;

    try {
      if (!buttonSoundAudioRef.current) {
        const audio = new Audio(LOBBY_BUTTON_SOUND_SRC);
        audio.preload = "auto";
        buttonSoundAudioRef.current = audio;
      }

      const audio = buttonSoundAudioRef.current;
      audio.pause();
      audio.currentTime = 0;
      audio.volume = Math.min(1, Math.max(0, Number(volume) || 0.55));
      void audio.play().catch(() => {});
    } catch {
      // Ignore audio playback restrictions.
    }
  };

  const playButtonHoverSound = () => {
    return;
  };

  const playButtonSelectSound = (buttonId) => {
    const now = Date.now();
    const lastPlayed = lastButtonHoverAtRef.current[`selected-${buttonId}`] || 0;
    if (now - lastPlayed < HOVER_SOUND_MIN_INTERVAL_MS) return;

    lastButtonHoverAtRef.current[`selected-${buttonId}`] = now;
    playLobbyButtonAssetSound(0.62);
  };

  const handleVideoWheelScale = (event) => {
    if (!canEditLobbyLayout) return;

    if (!event.ctrlKey) return;
    event.preventDefault();
    const currentScale = clampVideoScale(Number(videoPos.scale || DEFAULT_VIDEO_SCALE));
    const delta = event.deltaY > 0 ? -VIDEO_SCALE_STEP : VIDEO_SCALE_STEP;
    const nextScale = clampVideoScale(currentScale + delta);
    setVideoPos((previous) => ({
      ...previous,
      scale: nextScale,
      isAbsolute: previous.isAbsolute || false,
    }));
  };

  const showLobbyPremiumUnavailableMessage = () => {
    navigate("/game/premium");
  };

  const openLobbyPlayerProfile = (rawUser) => {
    const route = resolvePlayerProfileRoute(rawUser, currentUserProfileIdentitySet);
    if (!route) return false;
    navigate(route);
    return true;
  };

  const saveLobbyDesignPreferencesToServer = (specialBlockId, commonBlockId) => {
    const normalizedSpecialBlockId = normalizeLobbyDesignSkinId(
      specialBlockId,
      LOBBY_DESIGN_DEFAULT_SPECIAL_BLOCK_ID,
    );
    const normalizedCommonBlockId = normalizeLobbyDesignSkinId(
      commonBlockId,
      LOBBY_DESIGN_DEFAULT_COMMON_BLOCK_THEME_ID,
    );

    axios
      .patch("/auth/profile/skins", {
        specialBlockId: normalizedSpecialBlockId,
        commonBlockThemeId: normalizedCommonBlockId,
      })
      .catch(() => {
        // Keep the local design even if the network is unavailable.
      });
  };

  const openLobbySpecialBlockModal = () => {
    setLobbyDraftSpecialBlockId(lobbyDesignSpecialBlockId);
    setLobbySpecialBlockModalOpen(true);
  };

  const openLobbyCommonBlocksModal = () => {
    setLobbyDraftCommonBlockId(lobbyDesignCommonBlockId);
    setLobbyCommonBlocksModalOpen(true);
  };

  const openLobbyBoardThemeModal = () => {
    setLobbyDraftBoardThemeId(lobbyDesignBoardThemeId);
    setLobbyBoardThemeModalOpen(true);
  };

  const saveLobbySpecialBlockSelection = () => {
    const preset = getLobbyDesignSkinPreset(lobbyDraftSpecialBlockId);
    setLobbyDesignSpecialBlockId(preset.id);
    setLobbyDraftSpecialBlockId(preset.id);

    if (typeof window !== "undefined") {
      window.localStorage.setItem(
        `${LOBBY_DESIGN_SPECIAL_BLOCK_STORAGE_PREFIX}${lobbyDesignUserStorageKey}`,
        preset.id,
      );
      window.localStorage.setItem(LOBBY_DESIGN_GLOBAL_SPECIAL_BLOCK_KEY, preset.id);
      window.localStorage.setItem(
        LOBBY_DESIGN_GLOBAL_SPECIAL_BLOCK_IMAGE_KEY,
        preset.image,
      );
      window.dispatchEvent(
        new CustomEvent("xima-special-block-change", {
          detail: {
            ...preset,
            imageName: preset.id,
          },
        }),
      );
    }

    saveLobbyDesignPreferencesToServer(preset.id, lobbyDesignCommonBlockId);
    setLobbySpecialBlockModalOpen(false);
    notification.success({
      message: i18nT("lobby.especialActualizado"),
      description: `Bloque especial seleccionado: ${preset.label}.`,
    });
  };

  const saveLobbyCommonBlocksSelection = () => {
    const preset = getLobbyDesignSkinPreset(
      lobbyDraftCommonBlockId,
      LOBBY_DESIGN_DEFAULT_COMMON_BLOCK_THEME_ID,
    );
    setLobbyDesignCommonBlockId(preset.id);
    setLobbyDraftCommonBlockId(preset.id);

    if (typeof window !== "undefined") {
      window.localStorage.setItem(
        `${LOBBY_DESIGN_COMMON_BLOCK_STORAGE_PREFIX}${lobbyDesignUserStorageKey}`,
        preset.id,
      );
      window.localStorage.setItem(LOBBY_DESIGN_GLOBAL_COMMON_BLOCK_THEME_KEY, preset.id);
      window.dispatchEvent(
        new CustomEvent("xima-common-block-theme-change", {
          detail: {
            ...preset,
            imageName: preset.id,
          },
        }),
      );
    }

    saveLobbyDesignPreferencesToServer(lobbyDesignSpecialBlockId, preset.id);
    setLobbyCommonBlocksModalOpen(false);
    notification.success({
      message: i18nT("lobby.bloquesActualizados"),
      description: `Bloque comun seleccionado: ${preset.label}.`,
    });
  };

  const saveLobbyBoardThemeSelection = () => {
    const preset = getLobbyDesignBoardThemePreset(lobbyDraftBoardThemeId);
    setLobbyDesignBoardThemeId(preset.id);
    setLobbyDraftBoardThemeId(preset.id);

    if (typeof window !== "undefined") {
      window.localStorage.setItem(LOBBY_DESIGN_GLOBAL_BOARD_THEME_KEY, preset.id);
      window.dispatchEvent(
        new CustomEvent("xima-board-theme-change", {
          detail: preset,
        }),
      );
    }

    setLobbyBoardThemeModalOpen(false);
    notification.success({
      message: i18nT("lobby.tableroActualizado"),
      description: `Tema seleccionado: ${preset.label}.`,
    });
  };

  const rememberPrimaryPlayMode = (id) => {
    const normalizedModeId = storePrimaryPlayModeId(id);
    setPrimaryPlayModeId(normalizedModeId);
    return normalizedModeId;
  };

  const openFirstLobbyGuidedTutorial = useCallback(() => {
    unlockGameAudio();
    const target = buildFirstLobbyTutorialTarget();
    rememberTutorialReturnPath(`${window.location.pathname}${window.location.search}`);
    navigate(target.pathname, { state: target.state });
  }, [navigate]);

  const openGuidedTutorialLevel = useCallback(
    (level) => {
      unlockGameAudio();
      setGuidedTutorialMenuOpen(false);
      const target = Number(level) === 2
        ? buildTutorialLevel2Target()
        : buildFirstLobbyTutorialTarget();
      rememberTutorialReturnPath(`${window.location.pathname}${window.location.search}`);
      navigate(target.pathname, { state: target.state });
    },
    [navigate],
  );

  const saveFirstLobbyTutorialDecision = useCallback(
    async (decision) => {
      if (firstLobbyTutorialSaving) return;
      const normalizedDecision = decision === "accepted" ? "accepted" : "declined";

      try {
        setFirstLobbyTutorialSaving(true);
        const response = await axios.patch("/auth/onboarding/first-lobby", {
          decision: normalizedDecision,
        });
        const status = response?.data || null;
        setFirstLobbyTutorialStatus(status);
        setFirstLobbyTutorialOpen(false);

        if (normalizedDecision === "accepted") {
          openFirstLobbyGuidedTutorial();
        }
      } catch (error) {
        notification.error({
          message: lobbyText("firstTutorialAccess"),
          description:
            error?.response?.data?.error ||
            error?.message ||
            "No se pudo guardar tu elección. Inténtalo nuevamente.",
        });
      } finally {
        setFirstLobbyTutorialSaving(false);
      }
    },
    [firstLobbyTutorialSaving, lobbyText, openFirstLobbyGuidedTutorial],
  );

  const handleLobbyButtonClick = (id) => {
    const buttonId = String(id);
    setSelectedButtonId(buttonId);
    setShowLobbyPuzzleInterface(false);

    if (buttonId === "02") {
      setShowLobbyEmptyInterface(false);
      navigate("/game/design", {
        state: {
          fromLobbySource: "design",
          source: "lobbyDesignButton",
        },
      });
      return;
    }

    setShowLobbyEmptyInterface(false);

    if (buttonId === "04") {
      openProfileModal();
      return;
    }

    if (buttonId === "11") {
      setShowLobbyPuzzleInterface(true);
      return;
    }

    const dedicatedModeEntry = {
      "12": { mode: "free", source: "modo-ciencia", stateSource: "lobby-science-mode" },
      "13": { mode: "dice", source: "modo-azar", stateSource: "lobby-luck-mode" },
      "14": { mode: "cienzar", source: "modo-cienzar", stateSource: "lobby-cienzar-mode" },
      "15": {
        mode: "sciencevsazar",
        source: "modo-ciencia-vs-azar",
        stateSource: "lobby-science-vs-azar-mode",
      },
    }[buttonId];

    if (dedicatedModeEntry) {
      const nextPrimaryPlayModeId = rememberPrimaryPlayMode(buttonId);
      const targetModeQuery = encodeURIComponent(dedicatedModeEntry.mode);
      const targetSourceQuery = encodeURIComponent(dedicatedModeEntry.source);
      navigate(
        `/game/play?modeInterface=${targetModeQuery}&mode=${targetModeQuery}&fromLobbySource=${targetSourceQuery}`,
        {
          state: {
            fromLobbyTournament: "1",
            fromLobbySource: dedicatedModeEntry.source,
            source: dedicatedModeEntry.stateSource,
            modeInterface: dedicatedModeEntry.mode,
            scienceMode: dedicatedModeEntry.mode === "free",
            tournamentSection: nextPrimaryPlayModeId,
            tournamentMode: dedicatedModeEntry.mode,
          },
        },
      );
      return;
    }

    if (RESIZABLE_RECTANGLE_BUTTON_IDS.has(buttonId)) {
      const nextPrimaryPlayModeId = rememberPrimaryPlayMode(buttonId);
      const targetTournamentMode = RECTANGLE_TOURNAMENT_MODE_BY_ID[nextPrimaryPlayModeId] || "free";
      const targetTournamentModeQuery = encodeURIComponent(targetTournamentMode);
      navigate(
        `/game?quickView=torneos&tournamentMode=${targetTournamentModeQuery}&fromLobbyTournament=1&fromLobbySource=torneos`,
        {
          state: {
            fromLobbyTournament: "1",
            fromLobbySource: "torneos",
            source: "lobbyTournament",
            tournamentSection: nextPrimaryPlayModeId,
            tournamentMode: String(targetTournamentMode),
          },
        },
      );
      return;
    }

    if (buttonId === "03") {
      navigate("/game/store?fromLobbySource=store");
      return;
    }

    if (buttonId === "06") {
      navigate("/game/history?fromLobbySource=history");
      return;
    }

    if (buttonId === "05") {
      navigate("/game/friends?fromLobbySource=friends");
      return;
    }

    if (buttonId === "09") {
      navigate("/game/learn?fromLobbySource=learn");
      return;
    }

    if (buttonId === "07") {
      navigate("/game/create-game", {
        state: {
          fromLobbySource: "lobby",
          source: "lobbyCreateEntry",
        },
      });
      return;
    }

    if (buttonId === "10") {
      navigate("/game/spectate?fromLobbySource=spectate");
      return;
    }

    if (buttonId === "08") {
      navigate("/game/settings?fromLobbySource=settings");
      return;
    }

    if (buttonId === "language") {
      navigate("/game/settings?section=language&fromLobbySource=settings");
      return;
    }

    if (buttonId !== "01") return;
    showLobbyPremiumUnavailableMessage();
  };

  const lobbyQuickPlayBusyRef = useRef(false);
  const handleLobbyQuickPlayClick = async () => {
    if (lobbyQuickPlayBusyRef.current) return;
    lobbyQuickPlayBusyRef.current = true;
    const playerEmail = user?.email;
    try {
      const lastJoined = readLastJoinedTournament(playerEmail);
      const preferences = readQuickPlayPreferences(playerEmail);
      const tournamentsResponse = await axios.get("/tournaments");
      const { tournament } = pickLobbyQuickPlayTournament({
        tournaments: tournamentsResponse?.data,
        lastJoined,
        preferences,
      });

      if (tournament) {
        const tournamentId = String(tournament._id || tournament.id || "").trim();
        await axios.post("/tournaments/join", { id: tournamentId });
        writeLastJoinedTournament(playerEmail, tournament);
        const sequenceNumber = Math.max(0, Number(tournament.tournamentSequenceNumber) || 0);
        navigate(`/game/${tournamentId}?autoplay=1`, {
          state: {
            fromLobbySource: "lobby-quick-play",
            ...(sequenceNumber > 0
              ? { tournamentUiSequenceNumber: sequenceNumber, tournamentSequenceNumber: sequenceNumber }
              : {}),
          },
        });
        return;
      }

      // No tournament to return to: open a Cienzar 1v1 (10 min) that the server fills with a bot after 30 s.
      const createResponse = await axios.post("/tournaments", {
        tournamentName: "",
        mode: LOBBY_QUICK_PLAY_DEFAULT_MATCH.mode,
        memberSize: LOBBY_QUICK_PLAY_DEFAULT_MATCH.memberSize,
        havingTime: LOBBY_QUICK_PLAY_DEFAULT_MATCH.havingTime,
        botDifficulty: "medium",
        duration: 1,
        startingTime: new Date(Date.now() + 1000).toISOString(),
        creationSection: "independientes",
        isIndependentMatch: true,
        isQuickMatch: false,
        hasMatchPassword: false,
        matchPassword: "",
        tournamentPassword: "",
        joinPassword: "",
        tournamentLifetimeMinutes: 0,
      });
      const createdId = String(createResponse?.data?._id || "").trim();
      if (!createdId) throw new Error(lobbyText("quickPlayError"));
      navigate(`/game/${createdId}?autoplay=1`, {
        state: {
          fromLobbySource: "lobby-quick-play",
          lobbyCreatedIndependentChallenge: createResponse.data,
          createdTournamentId: createdId,
        },
      });
    } catch (error) {
      console.error("Lobby quick play failed:", error);
      notification.error({
        message: lobbyText("quickPlayError"),
        description: error?.response?.data?.message || error?.message || "",
      });
    } finally {
      lobbyQuickPlayBusyRef.current = false;
    }
  };

  const handlePrimaryPlayClick = () => {
    navigate("/game/play", {
      state: {
        fromLobbySource: "primary-play",
        source: "lobbyPrimaryPlay",
      },
    });
  };

  const openProfileModal = () => {
    setProfileDraftAvatar(currentAvatarUrl || "");
    setProfileDraftAvatarFile(null);
    setProfileDraftFirstName(
      String(profileFirstName || mergedLobbyUserProfile?.firstName || "")
        .trim()
        .slice(0, PROFILE_NAME_MAX_LENGTH),
    );
    setProfileDraftLastName(
      String(profileLastName || mergedLobbyUserProfile?.lastName || "")
        .trim()
        .slice(0, PROFILE_NAME_MAX_LENGTH),
    );
    setProfileDraftContactEmail(
      String(profileContactEmail || "")
        .trim()
        .toLowerCase()
        .slice(0, PROFILE_CONTACT_EMAIL_MAX_LENGTH),
    );
    setProfileDraftPhone(normalizeProfilePhoneInput(profilePhone).trim());
    setProfileDraftCurrentPassword("");
    setProfileDraftNewPassword("");
    setProfileDraftConfirmPassword("");
    setProfileModalOpen(true);
  };

  const closeProfileModal = () => {
    setProfileModalOpen(false);
    setProfileDraftAvatar(currentAvatarUrl || "");
    setProfileDraftAvatarFile(null);
    setProfileDraftFirstName(
      String(profileFirstName || mergedLobbyUserProfile?.firstName || "")
        .trim()
        .slice(0, PROFILE_NAME_MAX_LENGTH),
    );
    setProfileDraftLastName(
      String(profileLastName || mergedLobbyUserProfile?.lastName || "")
        .trim()
        .slice(0, PROFILE_NAME_MAX_LENGTH),
    );
    setProfileDraftContactEmail(
      String(profileContactEmail || "")
        .trim()
        .toLowerCase()
        .slice(0, PROFILE_CONTACT_EMAIL_MAX_LENGTH),
    );
    setProfileDraftPhone(normalizeProfilePhoneInput(profilePhone).trim());
    setProfileDraftCurrentPassword("");
    setProfileDraftNewPassword("");
    setProfileDraftConfirmPassword("");
  };

  const handleAvatarUpload = async (event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;

    if (!String(file.type || "").startsWith("image/")) {
      notification.error({
        message: i18nT("lobby.invalidFile"),
        description: i18nT("lobby.pleaseUploadAnImageFile"),
      });
      event.target.value = "";
      return;
    }

    if (Number(file.size || 0) > MAX_AVATAR_FILE_SIZE_BYTES) {
      notification.error({
        message: i18nT("lobby.imageTooLarge"),
        description: i18nT("lobby.pleaseUseAnImageSmallerThanMb"),
      });
      event.target.value = "";
      return;
    }

    try {
      const rawAvatar = await readFileAsDataUrl(file);
      let previewAvatar = rawAvatar;
      try {
        previewAvatar = await optimizeAvatarDataUrl(rawAvatar);
      } catch (previewError) {
        previewAvatar = rawAvatar;
      }
      setProfileDraftAvatar(previewAvatar || "");
      setProfileDraftAvatarFile(file);
    } catch (error) {
      notification.error({
        message: i18nT("lobby.imageError"),
        description: error?.message || "Could not process this image.",
      });
    }

    event.target.value = "";
  };

  const saveProfileChanges = async () => {
    const nextFirstName = String(profileDraftFirstName || "")
      .trim()
      .slice(0, PROFILE_NAME_MAX_LENGTH);
    const nextLastName = String(profileDraftLastName || "")
      .trim()
      .slice(0, PROFILE_NAME_MAX_LENGTH);
    const nextContactEmail = String(profileDraftContactEmail || "")
      .trim()
      .toLowerCase()
      .slice(0, PROFILE_CONTACT_EMAIL_MAX_LENGTH);
    const nextPhone = normalizeProfilePhoneInput(profileDraftPhone).trim();
    const nextCurrentPassword = String(profileDraftCurrentPassword || "");
    const nextNewPassword = String(profileDraftNewPassword || "");
    const nextConfirmPassword = String(profileDraftConfirmPassword || "");

    if (!nextFirstName) {
      notification.error({
        message: i18nT("lobby.nombreObligatorio"),
        description: i18nT("lobby.debesEscribirTuNombreParaActualizarElPerfil"),
      });
      return;
    }

    if (!nextLastName) {
      notification.error({
        message: i18nT("lobby.apellidoObligatorio"),
        description: i18nT("lobby.debesEscribirTuApellidoParaActualizarElPerfic88b3a"),
      });
      return;
    }

    if (nextContactEmail && !PROFILE_CONTACT_EMAIL_PATTERN.test(nextContactEmail)) {
      notification.error({
        message: i18nT("profile.correoNoValido"),
        description: i18nT("lobby.elCorreoDeContactoDebeTenerUnFormato"),
      });
      return;
    }

    if (nextPhone && !PROFILE_PHONE_PATTERN.test(nextPhone)) {
      notification.error({
        message: i18nT("lobby.telefonoNoValido"),
        description: i18nT("lobby.elTelefonoSoloPuedeIncluirNumerosEspaciosY"),
      });
      return;
    }

    const wantsPasswordUpdate = nextNewPassword.trim().length > 0;
    if (wantsPasswordUpdate) {
      if (!nextCurrentPassword.trim()) {
        notification.error({
          message: i18nT("lobby.faltaLaContrasenaActual"),
          description: i18nT("lobby.paraCambiarLaContrasenaDebesEscribirLaContra0bb74e"),
        });
        return;
      }

      if (nextNewPassword.length < 6) {
        notification.error({
          message: i18nT("lobby.contrasenaDemasiadoCorta"),
          description: i18nT("lobby.laNuevaContrasenaDebeTenerAlMenosCaracteres"),
        });
        return;
      }

      if (nextNewPassword !== nextConfirmPassword) {
        notification.error({
          message: i18nT("lobby.contrasenasDiferentes"),
          description: i18nT("lobby.laConfirmacionNoCoincideConLaNuevaContrasena"),
        });
        return;
      }
    } else if (nextCurrentPassword.trim() || nextConfirmPassword.trim()) {
      notification.error({
        message: i18nT("lobby.cambioDeContrasenaIncompleto"),
        description: i18nT("lobby.escribeTambienLaNuevaContrasenaParaCompletar8a18a4"),
      });
      return;
    }

    setProfileSavingPersonal(true);
    try {
      const draftAvatarValue = String(profileDraftAvatar || "").trim();
      const shouldUploadAvatarFile = Boolean(profileDraftAvatarFile);
      const shouldSaveAvatarReference =
        !shouldUploadAvatarFile && !draftAvatarValue.startsWith("data:image/");

      const payload = {
        firstName: nextFirstName,
        lastName: nextLastName,
        contactEmail: nextContactEmail,
        phone: nextPhone,
      };
      if (shouldSaveAvatarReference) {
        payload.avatarUrl = draftAvatarValue;
      }
      if (wantsPasswordUpdate) {
        payload.currentPassword = nextCurrentPassword;
        payload.newPassword = nextNewPassword;
      }

      const response = await axios.patch("/auth/profile/personal", payload);
      let updatedProfile = response?.data?.user || {};
      if (shouldUploadAvatarFile) {
        const avatarFormData = new FormData();
        avatarFormData.append("avatar", profileDraftAvatarFile);
        const avatarResponse = await axios.patch("/auth/profile/avatar", avatarFormData);
        updatedProfile = avatarResponse?.data?.user || updatedProfile;
      }

      const savedFirstName = String(updatedProfile?.firstName || nextFirstName)
        .trim()
        .slice(0, PROFILE_NAME_MAX_LENGTH);
      const savedLastName = String(updatedProfile?.lastName || nextLastName)
        .trim()
        .slice(0, PROFILE_NAME_MAX_LENGTH);
      const savedContactEmail = String(updatedProfile?.contactEmail || nextContactEmail)
        .trim()
        .toLowerCase()
        .slice(0, PROFILE_CONTACT_EMAIL_MAX_LENGTH);
      const savedPhone = String(updatedProfile?.phone || nextPhone)
        .trim()
        .slice(0, PROFILE_PHONE_MAX_LENGTH);
      const savedAvatarUrl = String(updatedProfile?.avatarUrl || "").trim();

      setProfileFirstName(savedFirstName);
      setProfileLastName(savedLastName);
      setProfileContactEmail(savedContactEmail);
      setProfilePhone(savedPhone);
      setProfileDraftFirstName(savedFirstName);
      setProfileDraftLastName(savedLastName);
      setProfileDraftContactEmail(savedContactEmail);
      setProfileDraftPhone(savedPhone);
      setProfileDraftAvatarFile(null);
      setProfileDraftCurrentPassword("");
      setProfileDraftNewPassword("");
      setProfileDraftConfirmPassword("");

      if (profileStorageKey) {
        if (savedAvatarUrl) {
          localStorage.setItem(
            profileStorageKey,
            JSON.stringify({
              avatarUrl: savedAvatarUrl,
            }),
          );
        } else {
          localStorage.removeItem(profileStorageKey);
        }
      }
      setProfileAvatar(savedAvatarUrl);

      const nextProfilePayload = {
        ...(updatedProfile || {}),
        firstName: savedFirstName,
        lastName: savedLastName,
        contactEmail: savedContactEmail,
        phone: savedPhone,
        avatarUrl: savedAvatarUrl,
      };

      setLobbyProfileData((previous) => ({
        ...(previous || {}),
        ...nextProfilePayload,
      }));
      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("xima-profile-change", {
            detail: nextProfilePayload,
          }),
        );
      }
      refreshUserProfile?.().catch(() => {});

      setProfileModalOpen(false);
      notification.success({
        message: i18nT("lobby.perfilActualizado"),
        description:
          String(response?.data?.message || "").trim() ||
          "Tu informacion personal fue actualizada correctamente.",
      });
    } catch (error) {
      notification.error({
        message: i18nT("lobby.noSePudoActualizarElPerfil"),
        description:
          String(error?.response?.data?.error || "").trim() ||
          error?.message ||
          "Ocurrio un error al actualizar tu perfil.",
      });
    } finally {
      setProfileSavingPersonal(false);
    }
  };

  const resetProfileAvatar = () => {
    if (!profileStorageKey) return;

    localStorage.removeItem(profileStorageKey);
    setProfileAvatar("");
    setProfileDraftAvatar(serverAvatarUrl || "");
    setProfileDraftAvatarFile(null);
    notification.info({
      message: i18nT("lobby.profileReset"),
      description: i18nT("lobby.customProfilePhotoRemoved"),
    });
  };

  const getLobbySquareScaleMultiplier = (buttonId) => {
    const normalizedButtonId = String(buttonId || "");
    if (mobileLobbyButtonScale < 1) return 1;
    if (lobbyViewportSize.width > 0 && lobbyViewportSize.width <= lobbyViewportSize.height)
      return 1;
    return DESKTOP_LOBBY_SQUARE_SCALE;
  };

  const startDrag = (event, id) => {
    if (event.button === 0) {
      setSelectedButtonId(id);
      playButtonSelectSound(id);
      return;
    }
    if (!canEditLobbyLayout) return;

    if (event.button !== 2) return;
    event.preventDefault();

    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;

    const pressedButton = buttons.find((button) => button.id === id);
    if (!pressedButton) return;
    const squareScaleMultiplier = getLobbySquareScaleMultiplier(id);
    const scale = Math.max(
      0.0001,
      mobileLobbyScaleRef.current * mobileLobbyButtonScaleRef.current * squareScaleMultiplier,
    );
    const logicalX = (event.clientX - rect.left) / scale;
    const logicalY = (event.clientY - rect.top) / scale;
    const buttonPointerX = Math.max(0, logicalX);
    const buttonPointerY = Math.max(0, logicalY);

    setDragState({
      kind: "button",
      id,
      offsetX: buttonPointerX - pressedButton.x,
      offsetY: buttonPointerY - pressedButton.y,
      scaleMultiplier: squareScaleMultiplier,
    });
  };

  const startButtonResize = (event, id, anchor) => {
    if (!canEditLobbyLayout) return;

    if (event.button !== 0 && event.button !== 2) return;
    event.preventDefault();
    event.stopPropagation();

    if (!RESIZABLE_RECTANGLE_BUTTON_IDS.has(String(id))) return;

    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;

    const pressedButton = buttons.find((button) => button.id === id);
    if (!pressedButton) return;

    const squareScaleMultiplier = getLobbySquareScaleMultiplier(id);
    const scale = Math.max(
      0.0001,
      mobileLobbyScaleRef.current * mobileLobbyButtonScaleRef.current * squareScaleMultiplier,
    );
    const logicalPointerX = (event.clientX - rect.left) / scale;
    const logicalPointerY = (event.clientY - rect.top) / scale;
    const buttonPointerX = Math.max(0, logicalPointerX);
    const buttonPointerY = Math.max(0, logicalPointerY);

    setDragState({
      kind: "button-resize",
      id,
      anchor,
      startX: pressedButton.x,
      startY: pressedButton.y,
      startWidth: pressedButton.width,
      startHeight: pressedButton.height,
      offsetX: buttonPointerX - pressedButton.x,
      offsetY: buttonPointerY - pressedButton.y,
      scaleMultiplier: squareScaleMultiplier,
    });
  };

  const startVideoDrag = (event) => {
    if (!canEditLobbyLayout) return;

    if (event.button !== 2) return;
    event.preventDefault();

    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const scale = Math.max(0.0001, mobileLobbyScaleRef.current);

    const containerLogicalWidth = rect.width / scale;
    const centeredX = videoPos.isAbsolute
      ? videoPos.x
      : Math.max(
          0,
          (containerLogicalWidth - DEFAULT_VIDEO_WIDTH_GUESS * (videoPos.scale || DEFAULT_VIDEO_SCALE)) / 2,
        );
    const centeredY = videoPos.isAbsolute ? videoPos.y : 0;

    if (!videoPos.isAbsolute) {
      setVideoPos((previous) => ({
        x: centeredX,
        y: centeredY,
        isAbsolute: true,
        scale: previous.scale ?? DEFAULT_VIDEO_SCALE,
      }));
    }

    setDragState({
      kind: "video",
      id: "video",
      offsetX: (event.clientX - rect.left) / scale - centeredX,
      offsetY: (event.clientY - rect.top) / scale - centeredY,
    });
  };

  const isMobilePortraitLobby =
    mobileLobbyButtonScale < 1 &&
    lobbyViewportSize.width > 0 &&
    lobbyViewportSize.width <= lobbyViewportSize.height;

  const generatedMobilePortraitButtonRenderLayout = useMemo(() => {
    return isMobilePortraitLobby
      ? buildMobilePortraitButtonRenderLayout(buttons, lobbyViewportSize)
      : null;
  }, [buttons, lobbyViewportSize, isMobilePortraitLobby]);

  const mobilePortraitButtonRenderLayout = useMemo(() => {
    return mergeMobilePortraitRenderLayout(
      generatedMobilePortraitButtonRenderLayout,
      mobilePortraitButtons,
    );
  }, [generatedMobilePortraitButtonRenderLayout, mobilePortraitButtons]);
  const mobilePortraitLogoTop = useMemo(() => {
    if (!isMobilePortraitLobby || !mobilePortraitButtonRenderLayout) return 4;
    const referenceTops = ["04", "01"]
      .map((id) => mobilePortraitButtonRenderLayout.get(id)?.y)
      .filter((value) => Number.isFinite(Number(value)))
      .map((value) => Number(value));
    if (referenceTops.length === 0) return 64;
    return Math.max(0, Math.min(...referenceTops));
  }, [isMobilePortraitLobby, mobilePortraitButtonRenderLayout]);

  const mobileTopPlayersPanelLayout = useMemo(() => {
    if (!isMobilePortraitLobby || !mobilePortraitButtonRenderLayout) return null;

    const rectangleEntries = ["12", "14", "13"]
      .map((id) => mobilePortraitButtonRenderLayout.get(id))
      .filter(Boolean);
    if (rectangleEntries.length === 0) return null;

    const left = Math.min(...rectangleEntries.map((entry) => Number(entry.x || 0)));
    const right = Math.max(
      ...rectangleEntries.map((entry) => Number(entry.x || 0) + Number(entry.width || 0)),
    );
    const bottom = Math.max(
      ...rectangleEntries.map((entry) => Number(entry.y || 0) + Number(entry.height || 0)),
    );

    return {
      x: left,
      y: bottom + MOBILE_LOBBY_TOP_PLAYERS_PANEL_GAP,
      width: Math.max(1, right - left),
      height: MOBILE_LOBBY_TOP_PLAYERS_PANEL_HEIGHT,
    };
  }, [isMobilePortraitLobby, mobilePortraitButtonRenderLayout]);

  const mobilePortraitContentHeight = useMemo(() => {
    if (!isMobilePortraitLobby || !mobilePortraitButtonRenderLayout) {
      return 0;
    }

    const contentBottom = Math.max(
      1,
      ...Array.from(mobilePortraitButtonRenderLayout.values()).map(
        (entry) => Number(entry.y || 0) + Number(entry.height || 0),
      ),
      mobileTopPlayersPanelLayout
        ? Number(mobileTopPlayersPanelLayout.y || 0) +
            Number(mobileTopPlayersPanelLayout.height || 0)
        : 0,
    );

    return contentBottom + MOBILE_LOBBY_PORTRAIT_BOTTOM_PADDING;
  }, [isMobilePortraitLobby, mobilePortraitButtonRenderLayout, mobileTopPlayersPanelLayout]);

  const lobbyEditButtonRenderEntry = useMemo(() => {
    const userButtonEntry = isMobilePortraitLobby
      ? mobilePortraitButtonRenderLayout?.get("04")
      : buttons.find((button) => String(button.id) === "04");

    if (!userButtonEntry) return null;

    const width = Number(userButtonEntry.width || 0);
    const height = Number(userButtonEntry.height || 0);
    const x = Number(userButtonEntry.x || 0);
    const y = Number(userButtonEntry.y || 0);

    if (!width || !height) return null;

    return {
      x,
      y: y + height,
      width,
      height: Math.max(30, Math.round(height * 0.32)),
    };
  }, [buttons, isMobilePortraitLobby, mobilePortraitButtonRenderLayout]);

  const clearMobileLongPress = () => {
    if (!mobileLongPressTimerRef.current) return;
    window.clearTimeout(mobileLongPressTimerRef.current);
    mobileLongPressTimerRef.current = null;
  };

  const startMobileLongPressDrag = (event, id) => {
    if (!canEditLobbyLayout) return;

    if (!isMobilePortraitLobby || !mobilePortraitButtonRenderLayout) return;
    const touch = event.touches?.[0];
    if (!touch) return;

    clearMobileLongPress();

    const rect = containerRef.current?.getBoundingClientRect();
    const renderedButton = mobilePortraitButtonRenderLayout.get(String(id));
    if (!rect || !renderedButton) return;

    const scale = Math.max(0.0001, mobileLobbyScaleRef.current);
    const pointerX = (touch.clientX - rect.left) / scale;
    const pointerY = (touch.clientY - rect.top) / scale;
    const currentLayoutSnapshot = layoutMapToMobileSnapshot(mobilePortraitButtonRenderLayout);

    mobileLongPressTimerRef.current = window.setTimeout(() => {
      mobileLongPressTimerRef.current = null;
      setSelectedButtonId(id);
      playButtonSelectSound(id);
      setMobilePortraitButtons((previous) => ({
        ...(currentLayoutSnapshot || {}),
        ...(normalizeMobilePortraitButtons(previous) || {}),
      }));
      setDragState({
        kind: "mobile-button",
        id,
        offsetX: pointerX - renderedButton.x,
        offsetY: pointerY - renderedButton.y,
        width: renderedButton.width,
        height: renderedButton.height,
      });
    }, MOBILE_LOBBY_LONG_PRESS_MS);
  };

  const cancelMobileLongPressDrag = () => {
    clearMobileLongPress();
  };

  useEffect(() => {
    return () => clearMobileLongPress();
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    document.body.classList.add("xima-lobby-screen-active");
    document.documentElement.classList.add("xima-lobby-screen-active");

    return () => {
      document.body.classList.remove("xima-lobby-screen-active");
      document.documentElement.classList.remove("xima-lobby-screen-active");
    };
  }, []);

  const lobbyDesignPanels = [
    {
      id: "special",
      title: i18nT("lobby.bloqueEspecial3a874a"),
      value: lobbyDesignSpecialBlock.label,
      image: lobbyDesignSpecialBlock.image,
      actionLabel: "EDITAR ESPECIAL",
      onEdit: openLobbySpecialBlockModal,
    },
    {
      id: "common",
      title: i18nT("lobby.bloquesComunes8995aa"),
      value: lobbyDesignCommonBlock.label,
      image: lobbyDesignCommonBlock.image,
      actionLabel: "EDITAR BLOQUES",
      onEdit: openLobbyCommonBlocksModal,
    },
    {
      id: "board",
      title: i18nT("lobby.tableroXimad4cb26"),
      value: lobbyDesignBoardTheme.label,
      boardTheme: lobbyDesignBoardTheme,
      actionLabel: "EDITAR TABLERO",
      onEdit: openLobbyBoardThemeModal,
    },
  ];

  const getLobbyDesignSelectionClassName = (isSelected) =>
    `rounded-xl border p-3 text-left transition ${
      isSelected
        ? "border-[#44ffaf] bg-[#143927] shadow-[0_0_18px_rgba(68,255,175,0.22)]"
        : "border-[#315d8f] bg-[#0d2342] hover:border-[#8fd4ff] hover:bg-[#12305c]"
    }`;

  const openLobbyQuickView = (quickView, source, extraState = {}) => {
    const nextQuickView = String(quickView || "torneos").trim().toLowerCase();
    const nextSource = String(source || nextQuickView).trim().toLowerCase();
    navigate(
      `/game?quickView=${encodeURIComponent(nextQuickView)}&fromLobbySource=${encodeURIComponent(
        nextSource,
      )}`,
      {
        state: {
          openMatchmakingQuickView: nextQuickView,
          openMatchmakingQuickViewRequestKey: `lobby-${nextSource}-${Date.now()}`,
          fromLobbySource: nextSource,
          source: "lobby",
          ...extraState,
        },
      },
    );
  };

  const openDailyChallengesInterface = () => {
    openLobbyQuickView("daily-puzzles", "daily-puzzles", {
      source: "dailyPuzzles",
    });
  };

  const openFindMatchesInterface = () => {
    openLobbyQuickView("buscar-partidos", "buscar-partidos", {
      source: "findMatches",
    });
  };

  const openLocalFriendsInterface = () => {
    navigate("/game/play-local-friends", {
      state: {
        fromLobbySource: "local-friends",
        source: "lobbyLocalFriendsButton",
      },
    });
  };

  const openBotInterface = () => {
    navigate("/game/play-bot", {
      state: {
        fromLobbySource: "bot",
        source: "lobbyBotButton",
      },
    });
  };

  const openSandboxInterface = () => {
    navigate("/game/play-sandbox", {
      state: {
        fromLobbySource: "sandbox",
        source: "lobbySandboxButton",
      },
    });
  };

  const lobbyPlayChoices = [
    {
      id: "11",
      title: lobbyText("solvePuzzle"),
      description: lobbyText("puzzleDescription"),
      icon: TbPuzzle,
      color: "#2688ff",
      onClick: openDailyChallengesInterface,
      hideOnDesktop: true,
      hideOnMobilePortrait: true,
    },
    {
      id: "bot",
      title: lobbyText("playVsBot"),
      description: lobbyText("botDescription"),
      icon: IoGameControllerOutline,
      color: "#ef852f",
      onClick: openBotInterface,
      hideOnDesktop: true,
      hideOnMobilePortrait: true,
    },
    {
      id: "sandbox",
      title: lobbyText("sandboxTitle"),
      description: lobbyText("sandboxDescription"),
      icon: IoCubeOutline,
      color: "#c895ff",
      onClick: openSandboxInterface,
      hideOnMobilePortrait: true,
    },
    {
      id: "local-friends",
      title: lobbyText("friendsPlayTitle"),
      description: lobbyText("friendsPlayDescription"),
      icon: IoPeopleOutline,
      color: "#ffe27a",
      onClick: openLocalFriendsInterface,
    },
    {
      id: "02",
      title: lobbyText("designBoardTitle"),
      description: lobbyText("designBoardDescription"),
      icon: IoHammerOutline,
      color: "#f5b638",
      onClick: () => handleLobbyButtonClick("02"),
    },
  ];

  const lobbyUtilityButtons = [
    {
      id: "language",
      label: lobbyText("languageTitle"),
      description: lobbyText("languageDescription"),
      icon: IoLanguageOutline,
      color: "#7bd2ff",
      onClick: () => handleLobbyButtonClick("language"),
    },
    {
      id: "09",
      label: lobbyText("learnTitle"),
      description: lobbyText("learnDescription"),
      icon: IoBookOutline,
      color: "#24d26b",
      onClick: () => handleLobbyButtonClick("09"),
    },
    {
      id: "08",
      label: "Ajustes",
      description: i18nT("lobby.configuraIdiomaSonidoYPreferencias"),
      icon: IoCogOutline,
      color: "#f8f2e6",
      onClick: () => handleLobbyButtonClick("08"),
      hideOnDesktop: true,
      hideOnMobilePortrait: true,
    },
    {
      id: "05",
      label: lobbyText("friendsTitle"),
      description: lobbyText("friendsDescription"),
      icon: IoPeopleOutline,
      color: "#7bd2ff",
      onClick: () => handleLobbyButtonClick("05"),
    },
    {
      id: "10",
      label: lobbyText("watchMatchesTitle"),
      description: lobbyText("watchMatchesDescription"),
      icon: GiBinoculars,
      color: "#c895ff",
      onClick: () => handleLobbyButtonClick("10"),
      hideOnMobilePortrait: true,
    },
    {
      id: "06",
      label: lobbyText("historyTitle"),
      description: lobbyText("historyDescription"),
      icon: IoFolderOpenOutline,
      color: "#ff4d4f",
      onClick: () => handleLobbyButtonClick("06"),
    },
    {
      id: "03",
      label: lobbyText("shopTitle"),
      description: lobbyText("shopDescription"),
      icon: IoGiftOutline,
      color: "#ffb24d",
      onClick: () => handleLobbyButtonClick("03"),
    },
    {
      id: "stats",
      label: lobbyText("statisticsTitle"),
      description: lobbyText("statisticsDescription"),
      icon: IoStatsChartOutline,
      color: "#24d26b",
      onClick: () => navigate("/game/stats"),
    },
    {
      id: "01",
      label: "Premium",
      description: i18nT("lobby.beneficiosExclusivosYMejorasVisuales"),
      icon: IoDiamondOutline,
      color: "#c895ff",
      onClick: () => handleLobbyButtonClick("01"),
    },
    {
      id: "mobile-play",
      label: "PLAY",
      description: i18nT("lobby.abrirLaInterfaceDeJuego"),
      icon: IoPlayCircleOutline,
      color: "#44ffaf",
      onClick: handlePrimaryPlayClick,
      hideOnDesktop: true,
      hideOnMobilePortrait: true,
    },
  ];

  const leadingLobbyUtilityButtonIds = new Set(["language", "09", "10", "06", "03", "stats"]);
  const leadingLobbyUtilityButtons = lobbyUtilityButtons.filter((button) =>
    leadingLobbyUtilityButtonIds.has(button.id)
  );
  const remainingLobbyUtilityButtons = lobbyUtilityButtons.filter(
    (button) => !leadingLobbyUtilityButtonIds.has(button.id)
  );
  const renderLobbyUtilityButton = (button) => {
    const Icon = button.icon;
    return (
      <button
        key={button.id}
        type="button"
        onClick={button.onClick}
        className={`xima-play-choice xima-play-choice-utility${
          button.hideOnDesktop ? " xima-play-choice-desktop-hidden" : ""
        }${button.hideOnMobilePortrait ? " xima-play-choice-mobile-portrait-hidden" : ""
        }`}
      >
        <span className="xima-play-choice-icon" style={{ color: button.color }}>
          <Icon aria-hidden="true" />
        </span>
        <span className="xima-play-choice-copy">
          <span className="xima-play-choice-title">
            {button.label}
            {button.badge ? <b className="xima-play-choice-badge">{button.badge}</b> : null}
          </span>
          <span className="xima-play-choice-description">{button.description}</span>
        </span>
        <span className="xima-play-choice-arrow" aria-hidden="true">
          &gt;
        </span>
      </button>
    );
  };

  const lobbyModeCards = [
    {
      id: "12",
      title: lobbyText("scienceModeTitle"),
      shortTitle: lobbyText("scienceModeShort"),
      description: lobbyText("scienceModeDescription"),
      icon: "/mode-icons/free.png",
      score: lobbyCimaxScores["12"],
      className: "xima-mode-card xima-mode-science",
      onClick: () => handleLobbyButtonClick("12"),
    },
    {
      id: "13",
      title: lobbyText("chanceModeTitle"),
      shortTitle: lobbyText("chanceModeShort"),
      description: lobbyText("chanceModeDescription"),
      icon: "/mode-icons/logo-modo-azar.png",
      score: lobbyCimaxScores["13"],
      className: "xima-mode-card xima-mode-luck",
      onClick: () => handleLobbyButtonClick("13"),
    },
    {
      id: "14",
      title: lobbyText("cienzarModeTitle"),
      shortTitle: "CIENZAR",
      description: lobbyText("cienzarModeDescription"),
      icon: "/mode-icons/cienzar.png",
      score: lobbyCimaxScores["14"],
      className: "xima-mode-card xima-mode-cienzar",
      onClick: () => handleLobbyButtonClick("14"),
    },
    {
      id: "15",
      title: "CONTRAZAR",
      shortTitle: "CONTRAZAR",
      description: lobbyText("scienceVsAzarModeDescription"),
      icon: "/mode-icons/logo-contrazar.png",
      score: lobbyCimaxScores["15"],
      className: "xima-mode-card xima-mode-sciencevsazar",
      onClick: () => handleLobbyButtonClick("15"),
    },
  ];

  // Pirámide del centro del lobby, de arriba (1 bloque) a abajo (5 bloques).
  const lobbyPyramidRows = [
    [
      { id: "pyramid-play", layoutKey: "play", color: "#22d463", iconKey: "play", label: lobbyText("play"), icon: IoPlayCircleOutline, tone: "apex", onClick: handleLobbyQuickPlayClick },
    ],
    [
      { id: "pyramid-puzzles", layoutKey: "puzzles", color: "#7c4dff", iconKey: "puzzle", label: lobbyText("pyramidPuzzles"), icon: TbPuzzle, tone: "blue", onClick: openDailyChallengesInterface },
      { id: "pyramid-learn", layoutKey: "learn", color: "#00b8a9", iconKey: "book", facePattern: "bands", palette: ["#7b5cff", "#ff6fae", "#ffb347", "#3fd0c9"], label: lobbyText("learnTitle"), icon: IoBookOutline, tone: "blue", onClick: () => handleLobbyButtonClick("09") },
    ],
    [
      { id: "pyramid-history", layoutKey: "history", color: "#e53950", iconKey: "folder", label: lobbyText("historyTitle"), icon: IoFolderOpenOutline, tone: "blue", onClick: () => handleLobbyButtonClick("06") },
      { id: "pyramid-watch", layoutKey: "watch", color: "#9b5cff", iconKey: "binoculars", label: lobbyText("watchMatchesTitle"), icon: GiBinoculars, tone: "blue", onClick: () => handleLobbyButtonClick("10") },
      { id: "pyramid-sandbox", layoutKey: "sandbox", color: "#d4a017", iconKey: "cube", label: lobbyText("sandboxTitle"), icon: IoCubeOutline, tone: "blue", onClick: openSandboxInterface },
    ],
    [
      { id: "pyramid-create", layoutKey: "create", color: "#14a3c7", iconKey: "plus", label: lobbyText("pyramidCreateMatch"), icon: IoAddCircleOutline, tone: "blue", onClick: () => handleLobbyButtonClick("07") },
      { id: "pyramid-find", layoutKey: "find", color: "#5b6cff", iconKey: "list", label: lobbyText("pyramidFindMatch"), icon: TbListCheck, tone: "blue", onClick: openFindMatchesInterface },
      { id: "pyramid-local", layoutKey: "local", color: "#e8732c", iconKey: "people", label: lobbyText("localGame"), icon: IoPeopleOutline, tone: "blue", onClick: openLocalFriendsInterface },
      { id: "pyramid-bot", layoutKey: "bot", color: "#c2410c", iconKey: "controller", label: lobbyText("playVsBot"), icon: IoGameControllerOutline, tone: "blue", onClick: openBotInterface },
    ],
    [
      { ...lobbyModeCards[0], layoutKey: "science", color: "#1f6fe0", label: lobbyText("scienceModeShort"), image: lobbyModeCards[0].icon, tone: "science" },
      { ...lobbyModeCards[2], layoutKey: "cienzar", color: "#169c55", label: "CIENZAR", image: lobbyModeCards[2].icon, tone: "cienzar" },
      { id: "pyramid-tournaments", layoutKey: "tournaments", color: "#e0a614", iconKey: "trophy", facePattern: "quadrants", palette: ["#5aa9ff", "#4fd67f", "#ff7eb6", "#ffb04d"], label: lobbyText("pyramidAllTournaments"), icon: IoTrophyOutline, tone: "tournaments", onClick: handlePrimaryPlayClick },
      { ...lobbyModeCards[3], layoutKey: "contrazar", color: "#d6336c", label: "CONTRAZAR", image: lobbyModeCards[3].icon, tone: "contrazar" },
      { ...lobbyModeCards[1], layoutKey: "chance", color: "#f08a1c", label: lobbyText("chanceModeShort"), image: lobbyModeCards[1].icon, tone: "chance" },
    ],
  ];
  const lobbyBoardBlocks = lobbyPyramidRows.flat();
  // Botones extra del diseño Tablero (2.º piso): las mismas acciones que el menú.
  const lobbyTowerBlocks = [
    ...lobbyBoardBlocks,
    { id: "tower-championship", layoutKey: "championship", color: "#d4a017", iconKey: "podium", label: lobbyText("towerChampionship"), onClick: () => navigate("/game/championship") },
    { id: "tower-friends", layoutKey: "friends", color: "#e0457b", iconKey: "friends", label: lobbyText("towerFriends"), onClick: () => handleLobbyButtonClick("05") },
    { id: "tower-stats", layoutKey: "stats", color: "#2f9e8f", iconKey: "stats", label: lobbyText("towerStats"), onClick: () => navigate("/game/stats") },
    { id: "tower-store", layoutKey: "store", color: "#f08a1c", iconKey: "cart", label: lobbyText("towerStore"), onClick: () => handleLobbyButtonClick("03") },
    { id: "tower-design", layoutKey: "design", color: "#8e44ad", iconKey: "brush", label: lobbyText("towerDesign"), onClick: () => handleLobbyButtonClick("02") },
    { id: "tower-settings", layoutKey: "settings", color: "#5a6b85", iconKey: "gear", label: lobbyText("towerSettings"), onClick: () => handleLobbyButtonClick("08") },
    { id: "tower-language", layoutKey: "language", color: "#1f8ad6", iconKey: "globe", label: lobbyText("towerLanguage"), onClick: () => handleLobbyButtonClick("language") },
    { id: "tower-support", layoutKey: "support", color: "#3c9d4f", iconKey: "headset", label: lobbyText("towerSupport"), onClick: () => navigate("/game/support") },
  ];

  // Diseño «Moderno»: une a una partida que espera rival del modo elegido o crea una nueva.
  const handleModernJoinMatch = async (entry) => {
    const tournamentId = String(entry?._id || entry?.id || "").trim();
    if (!tournamentId) return;
    try {
      await axios.post("/tournaments/join", { id: tournamentId });
      writeLastJoinedTournament(user?.email, entry);
      const sequenceNumber = Math.max(0, Number(entry?.tournamentSequenceNumber) || 0);
      navigate(`/game/${tournamentId}?autoplay=1`, {
        state: {
          fromLobbySource: "lobby-modern",
          returnTo: "/game/lobby",
          ...(sequenceNumber > 0
            ? { tournamentUiSequenceNumber: sequenceNumber, tournamentSequenceNumber: sequenceNumber }
            : {}),
        },
      });
    } catch (error) {
      console.error("Lobby modern join failed:", error);
      notification.error({
        message: lobbyText("quickPlayError"),
        description: error?.response?.data?.message || error?.message || "",
      });
    }
  };

  const handleModernPlayMode = async (mode, waitingMatches) => {
    const sameMode = (Array.isArray(waitingMatches) ? waitingMatches : []).find(
      (item) => String(item?.entry?.mode || "").trim().toLowerCase() === mode
    );
    if (sameMode?.entry) {
      await handleModernJoinMatch(sameMode.entry);
      return;
    }
    try {
      // The server pairs players who press JUGAR in the same mode, so humans meet
      // each other before a bot fills the seat.
      const response = await axios.post("/tournaments/quick-match", { mode });
      const tournamentId = String(response?.data?.tournamentId || "").trim();
      if (!tournamentId) throw new Error(lobbyText("quickPlayError"));
      const sequenceNumber = Math.max(0, Number(response?.data?.tournamentSequenceNumber) || 0);
      if (response?.data?.action === "joined") writeLastJoinedTournament(user?.email, response.data.tournament);
      navigate(`/game/${tournamentId}?autoplay=1`, {
        state: {
          fromLobbySource: "lobby-modern",
          returnTo: "/game/lobby",
          ...(response?.data?.action === "created"
            ? { lobbyCreatedIndependentChallenge: response.data.tournament, createdTournamentId: tournamentId }
            : {}),
          ...(sequenceNumber > 0
            ? { tournamentUiSequenceNumber: sequenceNumber, tournamentSequenceNumber: sequenceNumber }
            : {}),
        },
      });
    } catch (error) {
      console.error("Lobby modern play failed:", error);
      notification.error({
        message: lobbyText("quickPlayError"),
        description: error?.response?.data?.message || error?.message || "",
      });
    }
  };

  // «Volver a jugar» al acabar una partida: el lobby busca otra del mismo modo.
  const lobbyLocation = useLocation();
  const playAgainHandledRef = useRef("");
  useEffect(() => {
    const mode = String(lobbyLocation.state?.quickPlayAgainMode || "").trim();
    const requestKey = String(lobbyLocation.state?.quickPlayAgainKey || mode);
    if (!mode || playAgainHandledRef.current === requestKey) return;
    playAgainHandledRef.current = requestKey;
    navigate("/game/lobby", { replace: true, state: {} });
    handleModernPlayMode(mode, []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lobbyLocation.state]);

  const lobbyModernUserIds = [
    user?.email,
    user?._id,
    user?.id,
    user?.userId,
    user?.username,
    mergedLobbyUserProfile?.email,
    mergedLobbyUserProfile?.loginIdentifier,
    mergedLobbyUserProfile?.username,
    globalChatSenderId,
  ]
    .map((value) => String(value || "").trim().toLowerCase())
    .filter((value) => value && value !== "guest");

  const lobbyModernActions = {
    learn: () => handleLobbyButtonClick("09"),
    tournaments: handlePrimaryPlayClick,
    championship: () => navigate("/game/championship"),
    watch: () => handleLobbyButtonClick("10"),
    friends: () => handleLobbyButtonClick("05"),
    stats: () => navigate("/game/stats"),
    history: () => handleLobbyButtonClick("06"),
    design: () => handleLobbyButtonClick("02"),
    store: () => {
      outletContext.markSidebarBadgeSeen?.("store");
      handleLobbyButtonClick("03");
    },
    premium: () => handleLobbyButtonClick("01"),
    settings: () => handleLobbyButtonClick("08"),
    support: () => {
      outletContext.markSidebarBadgeSeen?.("donate");
      navigate("/game/support");
    },
    admin: () => navigate("/game?tab=admin"),
    logout: () => outletContext.onLogout?.(),
    language: () => handleLobbyButtonClick("language"),
    bot: openBotInterface,
    localFriends: openLocalFriendsInterface,
    create: () => handleLobbyButtonClick("07"),
    find: openFindMatchesInterface,
    puzzles: openDailyChallengesInterface,
    sandbox: openSandboxInterface,
    ranking: () => navigate("/game/ranking"),
  };

  const renderLobbyDesignToggle = () => (
    <div className="xima-lobby-design-toggle" role="group" aria-label={lobbyText("lobbyDesignToggle")}>
      <span className="xima-lobby-design-toggle-title">{lobbyText("lobbyDesignToggle")}</span>
      <span className="xima-lobby-design-toggle-options">
        {/* Nombres invertidos a petición de Yosdel: el diseño plano se llama «Clásico»
            y el 3D de siempre «Moderno». El Tablero solo lo ve el dueño, en rojo. */}
        {[
          ["modern", "lobbyDesignClassic"],
          ["classic", "lobbyDesignModern"],
          ["live", "lobbyDesignLive"],
          ...(isLobbyOwner ? [["board", "lobbyDesignBoard"]] : []),
        ].map(([designKey, labelKey]) => (
          <button
            key={designKey}
            type="button"
            className={`${lobbyDesign === designKey ? "is-active" : ""}${
              designKey === "board" ? " is-owner-only" : ""
            }`}
            aria-pressed={lobbyDesign === designKey}
            onClick={() => chooseLobbyDesign(designKey)}
          >
            {lobbyText(labelKey)}
          </button>
        ))}
      </span>
    </div>
  );

  const ximaWorldChampionshipDateLabel = useMemo(
    () => formatLobbyDate(language, XIMA_WORLD_CHAMPIONSHIP_DATE),
    [language],
  );
  // Texto del cintillo que guarda un administrador (GET /api/lobby-ticker); vacío = aviso por defecto.
  const [customLobbyTickerText, setCustomLobbyTickerText] = useState("");
  useEffect(() => {
    let canceled = false;
    axios
      .get("/lobby-ticker")
      .then((response) => {
        if (!canceled) setCustomLobbyTickerText(String(response?.data?.text || "").trim());
      })
      .catch(() => {});
    return () => {
      canceled = true;
    };
  }, []);
  const saveLobbyTickerText = useCallback(async (text) => {
    const response = await axios.put("/lobby-ticker", { text });
    setCustomLobbyTickerText(String(response?.data?.text || "").trim());
  }, []);
  const ximaWorldChampionshipTickerText =
    customLobbyTickerText ||
    lobbyText("worldChampionshipAnnouncement", {
      date: ximaWorldChampionshipDateLabel,
    });

  const localizedLobbyRankingModeTitles = {
    ciencia: lobbyText("scienceModeShort"),
    cienzar: "CIENZAR",
    sciencevsazar: "CONTRAZAR",
    azar: lobbyText("chanceModeShort"),
  };
  const lobbyRankingSections = LOBBY_MODE_WINNERS_SECTION_ORDER.map((section) => ({
    ...section,
    title: localizedLobbyRankingModeTitles[section.key] || section.title,
    players: (Array.isArray(lobbyModeWinnersByMode?.[section.key])
      ? lobbyModeWinnersByMode[section.key]
      : []
    ).slice(0, 3),
  }));
  const hasLobbyRankingData = lobbyRankingSections.some((section) => section.players.length > 0);
  const lobbyModernRankingSections = lobbyRankingSections.map((section) => ({
    key: section.key,
    label: `${String(section.title || "").charAt(0)}${String(section.title || "").slice(1).toLowerCase()}`,
    players: section.players.map((player) => {
      const displayName = String(
        player?.displayName || player?.name || lobbyText("playerFallbackName"),
      ).trim();
      return {
        name: localizeGeneratedGuestName(language, displayName, player),
        score: formatLobbyCimaxScore(player?.cimaxScore, language).replace(" CIMAX", ""),
        raw: player,
      };
    }),
  }));

  const lobbyVisibleGlobalChatMessages =
    globalChatMessages.length > 0
      ? globalChatMessages
      : [
          {
            id: "system-empty-global-chat-lobby",
            sender: lobbyText("systemSender"),
            createdAt: new Date().toISOString(),
            text: t("SISTEMA_CHAT_GLOBAL_LISTO"),
          },
        ];
  const canSendGlobalChat = String(globalChatInput || "").trim().length > 0;
  const lobbyWalletBalanceLabel = formatLobbyWalletBalance(lobbyWalletBalance, language);
  const lobbyBonusLabel = lobbyText("bonusesLabel", { value: lobbyWalletBalanceLabel });

  const lobbyShopPreviewCards = [
    { label: i18nT("lobby.paqueteEstratega"), price: "1.200", image: "/store/xima-board/xima-board-1.jpg" },
    { label: i18nT("lobby.disenoClasico"), price: "800", image: "/store/xima-board/xima-board-2.png" },
    { label: i18nT("lobby.paquetePremium"), price: "2.000", image: "/store/xima-board/xima-board-3.jpeg" },
  ];

  if (showLobbyEmptyInterface) {
    return (
      <div
        ref={containerRef}
        className="relative min-h-[calc(100vh-18px)] overflow-y-auto bg-[#05080f] px-4 py-5 text-white"
        onContextMenu={(event) => event.preventDefault()}
      >
        <div className="mx-auto flex w-full max-w-[760px] flex-col gap-5 pb-28">
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[#1f6fa8]/70 bg-[#06172c]/90 px-4 py-3 shadow-[0_12px_34px_rgba(0,0,0,0.35)]">
            <div>
              <div className="text-xs font-black uppercase tracking-[0.28em] text-[#9de8ff]">{i18nT("lobby.loby")}</div>
              <div className="text-2xl font-black uppercase tracking-[0.14em] text-white">{i18nT("lobby.diseno")}</div>
            </div>
            <button
              type="button"
              onClick={() => setShowLobbyEmptyInterface(false)}
              className="rounded-xl border border-[#44ffaf]/70 bg-[#123927] px-4 py-2 text-xs font-black uppercase tracking-[0.18em] text-[#d9ffe9] transition hover:border-[#7cffc0] hover:bg-[#1d5b3e]"
            >{i18nT("common.volverAlLoby")}</button>
          </div>

          {lobbyDesignPanels.map((panel) => (
            <section
              key={panel.id}
              className="rounded-[30px] border border-[#2f9bd2]/75 bg-[#062d4a]/95 p-4 shadow-[0_18px_42px_rgba(0,0,0,0.45),inset_0_0_0_1px_rgba(129,217,255,0.16)]"
            >
              <div className="rounded-[24px] border border-[#33a8dd]/75 bg-[#06385a]/80 p-4 shadow-[inset_0_0_22px_rgba(42,178,235,0.12)]">
                <div className="text-lg font-black uppercase tracking-[0.34em] text-[#b6ecff]">
                  {panel.title}
                </div>
                <div className="mt-2 text-2xl font-black uppercase tracking-[0.08em] text-white">
                  {panel.value}
                </div>

                <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-center">
                  <div className="flex h-36 w-36 shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-[#45b9f0]/80 bg-[#0d315a] shadow-[0_0_24px_rgba(34,164,238,0.24),inset_0_0_18px_rgba(255,255,255,0.1)]">
                    {panel.id === "board" ? (
                      <div
                        className="h-24 w-24 rounded-xl border border-white/25"
                        style={getLobbyDesignBoardPreviewStyle(panel.boardTheme)}
                      />
                    ) : (
                      <img
                        src={panel.image}
                        alt={panel.value}
                        className="h-full w-full object-cover"
                      />
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={panel.onEdit}
                    className="flex min-h-[96px] flex-1 items-center justify-center rounded-2xl border border-[#45b9f0]/80 bg-[#0d315a] px-5 text-center text-xl font-black uppercase tracking-[0.12em] text-[#d9f5ff] shadow-[inset_0_0_20px_rgba(255,255,255,0.08)] transition hover:border-[#8fe8ff] hover:bg-[#143f70] hover:text-white"
                  >
                    {panel.actionLabel}
                  </button>
                </div>
              </div>
            </section>
          ))}
        </div>

        <Modal
          open={lobbySpecialBlockModalOpen}
          onCancel={() => {
            setLobbySpecialBlockModalOpen(false);
            setLobbyDraftSpecialBlockId(lobbyDesignSpecialBlockId);
          }}
          footer={null}
          maskClosable={false}
          width={1200}
          title={<span className="font-semibold text-[#8fd4ff]">{i18nT("lobby.bloqueEspecial")}</span>}
        >
          <div className="max-h-[78vh] space-y-4 overflow-y-auto pr-1">
            <div className="text-sm text-[#a9c4e9]">{i18nT("lobby.seleccionaLaImagenQueQuieresUsarParaEl")}</div>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
              {LOBBY_DESIGN_SKIN_PRESETS.map((preset) => {
                const isSelected = lobbyDraftSpecialBlockId === preset.id;
                return (
                  <button
                    key={`lobby-special-${preset.id}`}
                    type="button"
                    onClick={() => setLobbyDraftSpecialBlockId(preset.id)}
                    className={getLobbyDesignSelectionClassName(isSelected)}
                  >
                    <div className="mb-2 flex items-center gap-2">
                      <img
                        src={preset.image}
                        alt={preset.label}
                        className="h-14 w-14 shrink-0 rounded border border-[#66c6ff88] bg-[#12345a] object-cover"
                      />
                      <div className="min-w-0 truncate text-xs font-black uppercase tracking-wide text-[#d5ecff]">
                        {preset.label}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
            <div className="grid grid-cols-2 gap-2 pt-1">
              <button
                type="button"
                onClick={() => {
                  setLobbySpecialBlockModalOpen(false);
                  setLobbyDraftSpecialBlockId(lobbyDesignSpecialBlockId);
                }}
                className="rounded border border-[#6f7ea0] bg-[#1b2a4a] px-2 py-2 text-xs font-semibold uppercase text-[#ccdaf7] transition hover:border-[#9fb4e6]"
              >{i18nT("lobby.cancelar")}</button>
              <button
                type="button"
                onClick={saveLobbySpecialBlockSelection}
                className="rounded border border-[#33d368] bg-[#14662f] px-2 py-2 text-xs font-semibold uppercase text-[#cbffe0] transition hover:border-[#6bf0a1]"
              >{i18nT("lobby.guardar")}</button>
            </div>
          </div>
        </Modal>

        <Modal
          open={lobbyCommonBlocksModalOpen}
          onCancel={() => {
            setLobbyCommonBlocksModalOpen(false);
            setLobbyDraftCommonBlockId(lobbyDesignCommonBlockId);
          }}
          footer={null}
          maskClosable={false}
          width={1200}
          title={<span className="font-semibold text-[#8fd4ff]">{i18nT("lobby.bloquesComunes")}</span>}
        >
          <div className="max-h-[78vh] space-y-4 overflow-y-auto pr-1">
            <div className="text-sm text-[#a9c4e9]">{i18nT("lobby.seleccionaLaImagenQueQuieresUsarParaLos")}</div>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
              {LOBBY_DESIGN_SKIN_PRESETS.map((preset) => {
                const isSelected = lobbyDraftCommonBlockId === preset.id;
                return (
                  <button
                    key={`lobby-common-${preset.id}`}
                    type="button"
                    onClick={() => setLobbyDraftCommonBlockId(preset.id)}
                    className={getLobbyDesignSelectionClassName(isSelected)}
                  >
                    <div className="mb-2 flex items-center gap-2">
                      <img
                        src={preset.image}
                        alt={preset.label}
                        className="h-14 w-14 shrink-0 rounded border border-[#66c6ff88] bg-[#12345a] object-cover"
                      />
                      <div className="min-w-0 truncate text-xs font-black uppercase tracking-wide text-[#d5ecff]">
                        {preset.label}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
            <div className="grid grid-cols-2 gap-2 pt-1">
              <button
                type="button"
                onClick={() => {
                  setLobbyCommonBlocksModalOpen(false);
                  setLobbyDraftCommonBlockId(lobbyDesignCommonBlockId);
                }}
                className="rounded border border-[#6f7ea0] bg-[#1b2a4a] px-2 py-2 text-xs font-semibold uppercase text-[#ccdaf7] transition hover:border-[#9fb4e6]"
              >{i18nT("lobby.cancelar")}</button>
              <button
                type="button"
                onClick={saveLobbyCommonBlocksSelection}
                className="rounded border border-[#33d368] bg-[#14662f] px-2 py-2 text-xs font-semibold uppercase text-[#cbffe0] transition hover:border-[#6bf0a1]"
              >{i18nT("lobby.guardar")}</button>
            </div>
          </div>
        </Modal>

        <Modal
          open={lobbyBoardThemeModalOpen}
          onCancel={() => {
            setLobbyBoardThemeModalOpen(false);
            setLobbyDraftBoardThemeId(lobbyDesignBoardThemeId);
          }}
          footer={null}
          maskClosable={false}
          width={920}
          title={<span className="font-semibold text-[#8fd4ff]">{i18nT("lobby.colorDelTablero")}</span>}
        >
          <div className="max-h-[78vh] space-y-4 overflow-y-auto pr-1">
            <div className="text-sm text-[#a9c4e9]">{i18nT("lobby.eligeElTemaVisualParaElTableroDe")}</div>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
              {Object.values(LOBBY_DESIGN_BOARD_THEME_PRESET_MAP).map((preset) => {
                const isSelected = lobbyDraftBoardThemeId === preset.id;
                return (
                  <button
                    key={`lobby-board-${preset.id}`}
                    type="button"
                    onClick={() => setLobbyDraftBoardThemeId(preset.id)}
                    className={getLobbyDesignSelectionClassName(isSelected)}
                  >
                    <div className="mb-2 flex items-center gap-2">
                      <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded border border-[#66c6ff88] bg-[#12345a]">
                        <div
                          className="h-10 w-10 rounded border border-white/25"
                          style={getLobbyDesignBoardPreviewStyle(preset)}
                        />
                      </div>
                      <div className="min-w-0 truncate text-xs font-black uppercase tracking-wide text-[#d5ecff]">
                        {preset.label}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
            <div className="grid grid-cols-2 gap-2 pt-1">
              <button
                type="button"
                onClick={() => {
                  setLobbyBoardThemeModalOpen(false);
                  setLobbyDraftBoardThemeId(lobbyDesignBoardThemeId);
                }}
                className="rounded border border-[#6f7ea0] bg-[#1b2a4a] px-2 py-2 text-xs font-semibold uppercase text-[#ccdaf7] transition hover:border-[#9fb4e6]"
              >{i18nT("lobby.cancelar")}</button>
              <button
                type="button"
                onClick={saveLobbyBoardThemeSelection}
                className="rounded border border-[#33d368] bg-[#14662f] px-2 py-2 text-xs font-semibold uppercase text-[#cbffe0] transition hover:border-[#6bf0a1]"
              >{i18nT("lobby.guardar")}</button>
            </div>
          </div>
        </Modal>
      </div>
    );
  }

  if (showLobbyPuzzleInterface) {
    const lobbyPuzzleButtons = [
      {
        id: "12",
        label: RECTANGLE_BUTTON_LABEL_MAP["12"],
        image: RECTANGLE_BUTTON_IMAGE_MAP["12"],
        modeIcon: RECTANGLE_BUTTON_MODE_ICON_MAP["12"],
        score: lobbyCimaxScores["12"],
      },
      {
        id: "13",
        label: RECTANGLE_BUTTON_LABEL_MAP["13"],
        image: RECTANGLE_BUTTON_IMAGE_MAP["13"],
        modeIcon: RECTANGLE_BUTTON_MODE_ICON_MAP["13"],
        score: lobbyCimaxScores["13"],
      },
      {
        id: "14",
        label: RECTANGLE_BUTTON_LABEL_MAP["14"],
        image: RECTANGLE_BUTTON_IMAGE_MAP["14"],
        modeIcon: RECTANGLE_BUTTON_MODE_ICON_MAP["14"],
        score: lobbyCimaxScores["14"],
      },
    ];

    return (
      <div
        ref={containerRef}
        className="relative min-h-[calc(100vh-18px)] overflow-y-auto bg-[#05080f] px-4 py-5 text-white"
        onContextMenu={(event) => event.preventDefault()}
      >
        <div className="mx-auto flex w-full max-w-[760px] flex-col gap-5 pb-28">
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[#1f6fa8]/70 bg-[#06172c]/90 px-4 py-3 shadow-[0_12px_34px_rgba(0,0,0,0.35)]">
            <div>
              <div className="text-xs font-black uppercase tracking-[0.28em] text-[#9de8ff]">{i18nT("lobby.loby")}</div>
              <div className="text-2xl font-black uppercase tracking-[0.14em] text-white">{i18nT("lobby.modoPuzzle")}</div>
            </div>
            <button
              type="button"
              onClick={() => setShowLobbyPuzzleInterface(false)}
              className="rounded-xl border border-[#44ffaf]/70 bg-[#123927] px-4 py-2 text-xs font-black uppercase tracking-[0.18em] text-[#d9ffe9] transition hover:border-[#7cffc0] hover:bg-[#1d5b3e]"
            >{i18nT("common.volverAlLoby")}</button>
          </div>

          <div className="space-y-3">
            {lobbyPuzzleButtons.map((entry) => {
              const palette = getButtonPalette(entry.id);
              return (
                <button
                      key={entry.id}
                      type="button"
                      onClick={() => {
                          const targetPuzzleMode = RECTANGLE_PUZZLE_MODE_BY_ID[entry.id] || "free";
                      const targetPuzzleModeQuery = encodeURIComponent(targetPuzzleMode);
                      navigate(`/game?quickView=puzzles&puzzleMode=${targetPuzzleModeQuery}&fromLobbyPuzzle=1&fromLobbySource=puzzle`, {
                      state: {
                        fromLobbyPuzzle: "1",
                        fromLobbySource: "puzzle",
                        source: "lobbyPuzzle",
                        puzzleSection: String(entry.id),
                        puzzleMode: String(targetPuzzleMode),
                      },
                      });
                  }}
                  className="group relative flex h-48 w-full flex-col items-stretch overflow-hidden rounded-2xl border border-white/25 text-white transition duration-300"
                  style={{
                    background: palette.background,
                    boxShadow: palette.ring,
                  }}
                >
                  <div className="relative flex h-full w-full flex-row items-center justify-start gap-3 overflow-hidden rounded-xl bg-black/25 p-2 text-left">
                    <img
                      src={entry.image}
                      alt={`Modo ${entry.label}`}
                      className="absolute left-0 top-0 h-full w-[58%] object-cover object-left"
                      style={
                        WHITE_RECTANGLE_BUTTON_IMAGE_IDS.has(entry.id)
                          ? { filter: "brightness(0) invert(1)" }
                          : {}
                      }
                    />
                    <div className="absolute right-0 top-0 z-10 h-full w-[42%] px-2 py-2">
                      <img
                        src={entry.modeIcon}
                        alt={`Logo ${entry.label}`}
                        className="pointer-events-none absolute left-0 right-0 top-[7%] mx-auto h-[24%] w-full object-contain drop-shadow-[0_0_10px_rgba(255,255,255,0.35)]"
                      />
                      <img
                        src={RECTANGLE_PLAY_BUTTON_IMAGE_SRC}
                        alt={i18nT("lobby.playXima")}
                        className="pointer-events-none absolute left-0 right-0 top-[21%] mx-auto h-[62%] w-full object-contain"
                      />
                      <span className="absolute bottom-[6%] left-0 right-0 text-center text-[11px] font-black uppercase tracking-[0.12em] text-[#bfffee] drop-shadow-[0_0_8px_rgba(68,255,175,0.55)]">
                        {formatLobbyCimaxScore(entry.score, language)}
                      </span>
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/0 to-black/45" />
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // «Clásico» (clave modern) y «En vivo» (clave live) reciben los mismos datos.
  const LobbyDesignView = lobbyDesign === "live" ? LobbyLive : LobbyModern;

  return (
    <div
      ref={containerRef}
      className={
        lobbyDesign === "modern" || lobbyDesign === "live"
          ? "xima-lobby-modern-shell relative h-full text-white"
          : "xima-gold-lobby relative min-h-[calc(100vh-18px)] overflow-y-auto px-3 py-3 text-white md:px-5 md:py-4"
      }
      onContextMenu={(event) => event.preventDefault()}
    >
      {lobbyDesign === "modern" || lobbyDesign === "live" ? (
        <LobbyDesignView
          language={language}
          userName={lobbyUserName}
          userAvatar={lobbyProfileImage}
          userIds={lobbyModernUserIds}
          rankLabel={lobbyGlobalCimaxRankLabel}
          cimaxTotalLabel={formatLobbyNumber(lobbyGlobalCimaxTotal, language)}
          isAdmin={Boolean(outletContext.hasAdminMenuAccess)}
          isOwner={isOwnerAccount(user)}
          tickerText={ximaWorldChampionshipTickerText}
          onSaveTicker={saveLobbyTickerText}
          badges={outletContext.sidebarBadges || {}}
          socialPending={Math.max(0, Number(outletContext.socialAlert?.pendingTotal) || 0)}
          designChooser={renderLobbyDesignToggle()}
          newsTicker={
            <div className="xima-board-panel-head is-mobile-top" aria-hidden="true">
              <div className="xima-news-ticker">
                <div className="xima-news-ticker-track">
                  <span>{ximaWorldChampionshipTickerText}</span>
                  <span>{ximaWorldChampionshipTickerText}</span>
                  <span>{ximaWorldChampionshipTickerText}</span>
                </div>
              </div>
              <span className="xima-board-info">i</span>
            </div>
          }
          rankingSections={lobbyModernRankingSections}
          rankingLoading={lobbyModeWinnersLoading && !hasLobbyRankingData}
          onTutorial={() => openGuidedTutorialLevel(1)}
          onPlayMode={handleModernPlayMode}
          onJoinMatch={handleModernJoinMatch}
          onOpenPlayerProfile={(player) => {
            if (player && resolveProfileTargetFromUserLike(player)) openLobbyPlayerProfile(player);
          }}
          onOpenProfile={() => handleLobbyButtonClick("04")}
          onNavigate={(path, options) => navigate(path, options)}
          actions={lobbyModernActions}
        />
      ) : (
      <>
      <header className="xima-lobby-mobile-topbar">
        <div className="xima-lobby-mobile-logo">XIMA</div>
        <button
          type="button"
          onClick={() => handleLobbyButtonClick("08")}
          className="xima-lobby-mobile-menu"
          aria-label={i18nT("lobby.abrirAjustes")}
        >
          <IoMenuOutline aria-hidden="true" />
        </button>
      </header>

      <div className="xima-lobby-grid">
        <section className="xima-lobby-panel xima-play-panel">
          <div className="xima-panel-title">{lobbyText("playPanelTitle")}</div>
          <div className="xima-play-list scrollbar-gamified">
            {leadingLobbyUtilityButtons.map(renderLobbyUtilityButton)}
            {lobbyPlayChoices.map((choice) => {
              const Icon = choice.icon;
              return (
                <button
                  key={choice.id}
                  type="button"
                  onClick={choice.onClick}
                  className={`xima-play-choice${
                    choice.hideOnDesktop ? " xima-play-choice-desktop-hidden" : ""
                  }${choice.hideOnMobilePortrait ? " xima-play-choice-mobile-portrait-hidden" : ""
                  }`}
                >
                  <span className="xima-play-choice-icon" style={{ color: choice.color }}>
                    <Icon aria-hidden="true" />
                  </span>
                  <span className="xima-play-choice-copy">
                    <span className="xima-play-choice-title">{choice.title}</span>
                    <span className="xima-play-choice-description">{choice.description}</span>
                  </span>
                  <span className="xima-play-choice-arrow" aria-hidden="true">
                    &gt;
                  </span>
                </button>
              );
            })}
            {remainingLobbyUtilityButtons.map(renderLobbyUtilityButton)}
          </div>
          <div className="xima-primary-create-anchor">
            <button
              type="button"
              onClick={() => handleLobbyButtonClick("07")}
              className="xima-primary-create"
            >
              <span aria-hidden="true">+</span>
              {lobbyText("createGame")}
            </button>
          </div>
        </section>

        <section className="xima-lobby-panel xima-board-panel">
          <button
            type="button"
            className="xima-mobile-player-card"
            onClick={() =>
              navigate("/game/design", {
                state: {
                  fromLobbySource: "profile",
                  source: "lobbyProfileCard",
                },
              })
            }
            aria-label={i18nT("lobby.abrirModoDisenarDelPerfil")}
          >
            <span className="xima-mobile-player-emblem">
              {lobbyProfileImage ? (
                <img src={lobbyProfileImage} alt={lobbyUserName} />
              ) : (
                lobbyUserInitials
              )}
            </span>
            <span className="xima-mobile-player-copy">
              <strong>{lobbyUserName}</strong>
              <small>{lobbyGlobalCimaxRankLabel}</small>
            </span>
            <span className="xima-mobile-player-score">
              <strong>{formatLobbyNumber(lobbyGlobalCimaxTotal, language)}</strong>
              <small>CIMAX</small>
            </span>
            <span className="xima-mobile-player-bonus">
              <IoGiftOutline aria-hidden="true" />
              <span>
                <small>{lobbyBonusLabel.replace(lobbyWalletBalanceLabel, "").trim()}</small>
                <strong>{lobbyWalletBalanceLabel}</strong>
              </span>
            </span>
          </button>
          {/* Móvil en vertical: la cinta de noticias va aquí, debajo del perfil. */}
          <div className="xima-board-panel-head is-mobile-top" aria-hidden="true">
            <div className="xima-news-ticker">
              <div className="xima-news-ticker-track">
                <span>{ximaWorldChampionshipTickerText}</span>
                <span>{ximaWorldChampionshipTickerText}</span>
                <span>{ximaWorldChampionshipTickerText}</span>
              </div>
            </div>
            <span className="xima-board-info">i</span>
          </div>
          <div className="xima-mobile-presence-row">
            {firstLobbyTutorialStatus?.showPersistentAccess ? (
              <div className="xima-first-tutorial-menu is-mobile">
                <button
                  type="button"
                  className="xima-first-tutorial-access"
                  aria-expanded={guidedTutorialMenuOpen}
                  aria-haspopup="menu"
                  onClick={() => setGuidedTutorialMenuOpen((open) => !open)}
                >
                  <IoBookOutline aria-hidden="true" />
                  <span>{lobbyText("firstTutorialAccess")}</span>
                </button>
                {guidedTutorialMenuOpen ? (
                  <div className="xima-first-tutorial-levels" role="menu">
                    <button type="button" role="menuitem" onClick={() => openGuidedTutorialLevel(1)}>
                      {lobbyText("guidedTutorialLevel1")}
                    </button>
                    <button type="button" role="menuitem" onClick={() => openGuidedTutorialLevel(2)}>
                      {lobbyText("guidedTutorialLevel2")}
                    </button>
                  </div>
                ) : null}
                {renderLobbyDesignToggle()}
              </div>
            ) : (
              <div className="xima-first-tutorial-menu is-mobile">{renderLobbyDesignToggle()}</div>
            )}
            {renderConnectedPlayersWidget("mobile")}
            <div
              id="xima-direct-message-mobile-anchor"
              className="xima-direct-message-mobile-anchor"
            />
          </div>
          <div
            id="xima-direct-notification-mobile-anchor"
            className="xima-direct-notification-mobile-anchor"
          />

          <div className="xima-board-panel-head">
            <div
              className="xima-news-ticker"
              aria-label={ximaWorldChampionshipTickerText}
            >
              <div className="xima-news-ticker-track" aria-hidden="true">
                <span>{ximaWorldChampionshipTickerText}</span>
                <span>{ximaWorldChampionshipTickerText}</span>
                <span>{ximaWorldChampionshipTickerText}</span>
              </div>
            </div>
            <span className="xima-board-info" aria-hidden="true">i</span>
          </div>

          <div className="xima-board-preview">
            <img
              src="/imagen-momentanea.png"
              alt={i18nT("lobby.imagenPrincipalDeXima")}
              className="xima-board-preview-image"
            />
            <button
              type="button"
              className="xima-player-pill"
              onClick={() =>
                navigate("/game/design", {
                  state: {
                    fromLobbySource: "profile",
                    source: "lobbyProfileCard",
                  },
                })
              }
              aria-label={i18nT("lobby.abrirModoDisenarDelPerfilc9ab9c")}
            >
              <span className="xima-player-emblem">
                {lobbyProfileImage ? (
                  <img src={lobbyProfileImage} alt={lobbyUserName} />
                ) : (
                  lobbyUserInitials
                )}
              </span>
              <span className="xima-player-copy">
                <strong>{lobbyUserName}</strong>
                <small>{lobbyGlobalCimaxRankLabel}</small>
                <span className="xima-player-bonus-pill">
                  {lobbyBonusLabel}
                </span>
              </span>
              <span className="xima-player-score">
                {formatLobbyNumber(lobbyGlobalCimaxTotal, language)}
              </span>
            </button>
            {firstLobbyTutorialStatus?.showPersistentAccess ? (
              <div className="xima-first-tutorial-menu is-desktop">
                <button
                  type="button"
                  className="xima-first-tutorial-access"
                  aria-expanded={guidedTutorialMenuOpen}
                  aria-haspopup="menu"
                  onClick={() => setGuidedTutorialMenuOpen((open) => !open)}
                >
                  <IoBookOutline aria-hidden="true" />
                  <span>{lobbyText("firstTutorialAccess")}</span>
                </button>
                {guidedTutorialMenuOpen ? (
                  <div className="xima-first-tutorial-levels" role="menu">
                    <button type="button" role="menuitem" onClick={() => openGuidedTutorialLevel(1)}>
                      {lobbyText("guidedTutorialLevel1")}
                    </button>
                    <button type="button" role="menuitem" onClick={() => openGuidedTutorialLevel(2)}>
                      {lobbyText("guidedTutorialLevel2")}
                    </button>
                  </div>
                ) : null}
                {renderLobbyDesignToggle()}
              </div>
            ) : (
              <div className="xima-first-tutorial-menu is-desktop">{renderLobbyDesignToggle()}</div>
            )}
            <div className="xima-desktop-presence-row">
              <div
                id="xima-direct-notification-desktop-anchor"
                className="xima-direct-notification-desktop-anchor"
              />
              {renderConnectedPlayersWidget("desktop")}
            </div>
          </div>

          <section
            className={`xima-lobby-mode-stage xima-lobby-pyramid-stage${
              lobbyBoard3DStatus === "ready" ? " has-board3d" : ""
            }`}
            aria-label={i18nT("lobby.modeShowcase.ariaLabel")}
          >
            {lobbyBoard3DStatus !== "unavailable" && lobbyDesign === "board" ? (
              <LobbyBoardTower3D
                key="lobby-board-tower"
                blocks={lobbyTowerBlocks}
                className={lobbyBoard3DStatus === "ready" ? "is-ready" : ""}
                onReady={() => setLobbyBoard3DStatus("ready")}
                onUnavailable={() => setLobbyBoard3DStatus("unavailable")}
              />
            ) : null}
            {lobbyBoard3DStatus !== "unavailable" && lobbyDesign !== "board" ? (
              <LobbyBoard3D
                key="lobby-board-classic"
                blocks={lobbyBoardBlocks}
                className={lobbyBoard3DStatus === "ready" ? "is-ready" : ""}
                onReady={() => setLobbyBoard3DStatus("ready")}
                onUnavailable={() => setLobbyBoard3DStatus("unavailable")}
              />
            ) : null}
            <div className={`xima-lobby-pyramid${lobbyBoard3DStatus === "ready" ? " is-replaced" : ""}`}>
              {lobbyPyramidRows.map((row, rowIndex) => (
                <div
                  key={`pyramid-row-${rowIndex}`}
                  className={`xima-lobby-pyramid-row xima-lobby-pyramid-row-${row.length}`}
                >
                  {row.map((block) => {
                    const BlockIcon = block.icon;
                    return (
                      <button
                        key={block.id}
                        type="button"
                        onClick={block.onClick}
                        aria-disabled={block.onClick ? undefined : "true"}
                        className={`xima-lobby-pyramid-block xima-lobby-pyramid-block-${block.tone}`}
                      >
                        <span className="xima-lobby-pyramid-icon" aria-hidden="true">
                          {block.image ? <img src={block.image} alt="" /> : <BlockIcon />}
                        </span>
                        <span className="xima-lobby-pyramid-label">{block.label}</span>
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          </section>
        </section>

        <aside className="xima-right-stack">
          <section className="xima-lobby-panel xima-ranking-panel">
            <div className="xima-side-title">
              <span>{lobbyText("topPlayers")}</span>
            </div>
            <div className="xima-ranking-list xima-ranking-list-by-mode">
              {lobbyModeWinnersLoading && !hasLobbyRankingData ? (
                <div className="xima-ranking-empty">{lobbyText("rankingLoading")}</div>
              ) : (
                lobbyRankingSections.map((section) => (
                  <div
                    key={section.key}
                    className={`xima-ranking-mode-section${
                      activeLobbyRankingModeKey === section.key
                        ? " xima-ranking-mode-section-active"
                        : ""
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => setActiveLobbyRankingModeKey(section.key)}
                      className="xima-ranking-mode-title"
                      aria-pressed={activeLobbyRankingModeKey === section.key}
                    >
                      <span>{section.title}</span>
                      <span>CIMAX</span>
                    </button>
                    {section.players.length > 0 ? (
                      section.players.map((player, index) => {
                        const displayName = String(
                          player?.displayName || player?.name || lobbyText("playerFallbackName"),
                        ).trim();
                        const visibleDisplayName = localizeGeneratedGuestName(
                          language,
                          displayName,
                          player,
                        );
                        const avatarUrl = resolveAvatarSrc(player?.avatarUrl || "");
                        const canOpenProfile =
                          Boolean(player) && Boolean(resolveProfileTargetFromUserLike(player));
                        return (
                          <button
                            key={`${section.key}-${displayName}-${index}`}
                            type="button"
                            onClick={canOpenProfile ? () => openLobbyPlayerProfile(player) : undefined}
                            className="xima-ranking-row"
                          >
                            <span className={`xima-rank-number xima-rank-${index + 1}`}>
                              {index + 1}
                            </span>
                            <span className="xima-rank-avatar">
                              {avatarUrl ? (
                                <img src={avatarUrl} alt={visibleDisplayName} />
                              ) : (
                                getLobbyUserInitials(visibleDisplayName)
                              )}
                            </span>
                            <span className="xima-rank-name">{visibleDisplayName}</span>
                            <span className="xima-rank-score">
                              {formatLobbyCimaxScore(player?.cimaxScore, language).replace(" CIMAX", "")}
                            </span>
                          </button>
                        );
                      })
                    ) : (
                      <div className="xima-ranking-empty">{lobbyText("noRankingData")}</div>
                    )}
                  </div>
                ))
              )}
            </div>
            <button type="button" onClick={() => navigate("/game/ranking")} className="xima-panel-link">
              {lobbyText("fullLeaderboard")}
            </button>
          </section>

          <section className="xima-lobby-panel xima-chat-panel">
            <div className="xima-side-title">
              <span>{lobbyText("globalChat")}</span>
              {hasLobbyGlobalChatAdminAccess ? (
                <button
                  type="button"
                  onClick={handleClearGlobalChat}
                  className="xima-chat-clear-button"
                >
                  {lobbyText("clear")}
                </button>
              ) : (
                <span aria-hidden="true">...</span>
              )}
            </div>
            <div ref={globalChatScrollRef} className="xima-chat-list" aria-live="polite">
              {lobbyVisibleGlobalChatMessages.map((message) => {
                const timeLabel = new Date(message.createdAt || Date.now()).toLocaleTimeString(
                  [],
                  {
                    hour: "2-digit",
                    minute: "2-digit",
                  },
                );
                return (
                  <div
                    key={message.id}
                    className={`xima-chat-row ${
                      message.id === "system-empty-global-chat-lobby"
                        ? "xima-chat-row-system"
                        : ""
                    }`}
                  >
                    <strong>{message.sender}</strong>
                    <span>{message.text}</span>
                    <small>{timeLabel}</small>
                  </div>
                );
              })}
            </div>
            <form
              className="xima-chat-compose"
              onSubmit={(event) => {
                event.preventDefault();
                handleGlobalChatSend();
              }}
            >
              <input
                type="text"
                value={globalChatInput}
                maxLength={GLOBAL_CHAT_MAX_LENGTH}
                onChange={(event) => setGlobalChatInput(event.target.value)}
                placeholder={lobbyText("chatPlaceholder")}
                className="xima-chat-field"
              />
              <button
                type="submit"
                className="xima-chat-send"
                disabled={!canSendGlobalChat}
                aria-label={t("ENVIAR")}
              >
                &gt;
              </button>
            </form>
          </section>

          <section className="xima-lobby-panel xima-shop-panel">
            <div className="xima-side-title">
              <span>{lobbyText("shopTitle")}</span>
              <button type="button" onClick={() => handleLobbyButtonClick("03")}>
                {lobbyText("viewAll")}
              </button>
            </div>
            <div className="xima-shop-cards">
              {lobbyShopPreviewCards.map((card) => (
                <button
                  key={card.label}
                  type="button"
                  onClick={() => handleLobbyButtonClick("03")}
                  className="xima-shop-card"
                >
                  <img src={card.image} alt={card.label} />
                  <strong>{card.label}</strong>
                  <small>{card.price}</small>
                </button>
              ))}
            </div>
          </section>
        </aside>

      </div>
      </>
      )}

      <Modal
        open={firstLobbyTutorialOpen}
        centered
        closable={false}
        keyboard={false}
        maskClosable={false}
        footer={null}
        width={600}
        rootClassName="xima-first-tutorial-modal-root"
      >
        <div className="xima-first-tutorial-modal">
          <span className="xima-first-tutorial-icon" aria-hidden="true">
            <IoBookOutline />
          </span>
          <span className="xima-first-tutorial-eyebrow">
            {lobbyText("firstTutorialEyebrow")}
          </span>
          <h2>{lobbyText("firstTutorialWelcome", { name: lobbyUserName })}</h2>
          <h3>{lobbyText("firstTutorialQuestion")}</h3>
          <p>{lobbyText("firstTutorialDescription")}</p>
          <div className="xima-first-tutorial-actions">
            <button
              type="button"
              className="is-primary"
              disabled={firstLobbyTutorialSaving}
              onClick={() => saveFirstLobbyTutorialDecision("accepted")}
            >
              <IoPlayCircleOutline aria-hidden="true" />
              {lobbyText("firstTutorialAccept")}
            </button>
            <button
              type="button"
              disabled={firstLobbyTutorialSaving}
              onClick={() => saveFirstLobbyTutorialDecision("declined")}
            >
              {lobbyText("firstTutorialDecline")}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        open={profileModalOpen}
        onCancel={closeProfileModal}
        footer={null}
        maskClosable={false}
        width={1120}
        title={<span className="font-semibold text-[#8fd4ff]">{i18nT("lobby.perfilDelJugador")}</span>}
      >
        <div className="max-h-[88vh] space-y-4 overflow-y-auto pr-1">
          <div className="flex items-center gap-3 rounded-lg border border-[#21436d] bg-[#0a1a35] p-3">
            <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-lg border border-[#4acbff80] bg-[#123462] text-2xl font-black text-[#b9ebff]">
              {profileDraftAvatar ? (
                <img
                  src={profileDraftAvatar}
                  alt={`${profileDisplayName} ${i18nT("common.profileAvatarPlaceholder")}`}
                  className="h-full w-full object-cover"
                />
              ) : (
                <span>{lobbyUserInitials}</span>
              )}
            </div>
            <div>
              <div className="text-sm font-semibold text-white">{profileDisplayName}</div>
              <div className="mt-1 text-xs text-[#8fd4ff]">{i18nT("lobby.editaTuInformacionYSeleccionaTuFotoDe")}</div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            <div className="space-y-4 rounded-lg border border-[#21436d] bg-[#0a1a35] p-3">
              <div className="text-xs font-bold uppercase tracking-[0.16em] text-[#8fd4ff]">{i18nT("lobby.informacionPersonal")}</div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <label className="block">
                  <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-[#b5d9ff]">{i18nT("lobby.nombre")}</span>
                  <input
                    type="text"
                    value={profileDraftFirstName}
                    onChange={(event) =>
                      setProfileDraftFirstName(
                        String(event?.target?.value || "").slice(0, PROFILE_NAME_MAX_LENGTH),
                      )
                    }
                    className="w-full rounded border border-[#355b90] bg-[#0f264b] px-3 py-2 text-sm text-[#ecf6ff] outline-none transition focus:border-[#79d8ff]"
                    placeholder={i18nT("lobby.tuNombre")}
                    autoComplete="given-name"
                  />
                </label>
                <label className="block">
                  <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-[#b5d9ff]">{i18nT("lobby.apellido")}</span>
                  <input
                    type="text"
                    value={profileDraftLastName}
                    onChange={(event) =>
                      setProfileDraftLastName(
                        String(event?.target?.value || "").slice(0, PROFILE_NAME_MAX_LENGTH),
                      )
                    }
                    className="w-full rounded border border-[#355b90] bg-[#0f264b] px-3 py-2 text-sm text-[#ecf6ff] outline-none transition focus:border-[#79d8ff]"
                    placeholder={i18nT("lobby.tuApellido")}
                    autoComplete="family-name"
                  />
                </label>
              </div>

              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <label className="block">
                  <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-[#b5d9ff]">{i18nT("lobby.correoOpcional")}</span>
                  <input
                    type="email"
                    value={profileDraftContactEmail}
                    onChange={(event) =>
                      setProfileDraftContactEmail(
                        String(event?.target?.value || "")
                          .slice(0, PROFILE_CONTACT_EMAIL_MAX_LENGTH)
                          .toLowerCase(),
                      )
                    }
                    className="w-full rounded border border-[#355b90] bg-[#0f264b] px-3 py-2 text-sm text-[#ecf6ff] outline-none transition focus:border-[#79d8ff]"
                    placeholder={i18nT("profile.correoEjemploCom")}
                    autoComplete="email"
                  />
                </label>
                <label className="block">
                  <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-[#b5d9ff]">{i18nT("lobby.telefonoOpcional")}</span>
                  <input
                    type="text"
                    value={profileDraftPhone}
                    onChange={(event) =>
                      setProfileDraftPhone(normalizeProfilePhoneInput(event?.target?.value || ""))
                    }
                    inputMode="tel"
                    className="w-full rounded border border-[#355b90] bg-[#0f264b] px-3 py-2 text-sm text-[#ecf6ff] outline-none transition focus:border-[#79d8ff]"
                    placeholder="+1 555 123 4567"
                    autoComplete="tel"
                  />
                </label>
              </div>

              <div className="rounded border border-[#2d4771] bg-[#0d213f] p-3">
                <div className="mb-2 text-xs font-bold uppercase tracking-[0.16em] text-[#8fd4ff]">{i18nT("lobby.cambiarContrasenaOpcional")}</div>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                  <label className="block">
                    <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-[#b5d9ff]">{i18nT("lobby.actual")}</span>
                    <input
                      type="password"
                      value={profileDraftCurrentPassword}
                      onChange={(event) =>
                        setProfileDraftCurrentPassword(event?.target?.value || "")
                      }
                      className="w-full rounded border border-[#355b90] bg-[#0f264b] px-3 py-2 text-sm text-[#ecf6ff] outline-none transition focus:border-[#79d8ff]"
                      placeholder={i18nT("lobby.contrasenaActual")}
                      autoComplete="current-password"
                    />
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-[#b5d9ff]">{i18nT("lobby.nueva")}</span>
                    <input
                      type="password"
                      value={profileDraftNewPassword}
                      onChange={(event) =>
                        setProfileDraftNewPassword(event?.target?.value || "")
                      }
                      className="w-full rounded border border-[#355b90] bg-[#0f264b] px-3 py-2 text-sm text-[#ecf6ff] outline-none transition focus:border-[#79d8ff]"
                      placeholder={i18nT("lobby.nuevaContrasena")}
                      autoComplete="new-password"
                    />
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-[#b5d9ff]">{i18nT("lobby.confirmar")}</span>
                    <input
                      type="password"
                      value={profileDraftConfirmPassword}
                      onChange={(event) =>
                        setProfileDraftConfirmPassword(event?.target?.value || "")
                      }
                      className="w-full rounded border border-[#355b90] bg-[#0f264b] px-3 py-2 text-sm text-[#ecf6ff] outline-none transition focus:border-[#79d8ff]"
                      placeholder={i18nT("lobby.repiteLaContrasena")}
                      autoComplete="new-password"
                    />
                  </label>
                </div>
              </div>
            </div>

            <div className="space-y-4 rounded-lg border border-[#21436d] bg-[#0a1a35] p-3">
              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-[#8fd4ff]">{i18nT("lobby.subirFoto")}</label>
                <input
                  type="file"
                  accept="image/*"
                  aria-label={i18nT("lobby.elegirFotoDePerfil")}
                  onChange={handleAvatarUpload}
                  className="block w-full cursor-pointer rounded border border-[#4fc3ff88] bg-[#11305c] text-sm font-semibold text-[#d7efff] file:mr-3 file:cursor-pointer file:border-0 file:bg-[#1d8eca] file:px-3 file:py-2 file:text-sm file:font-semibold file:text-white hover:border-[#7ee5ff]"
                />
                <div className="mt-1 text-xs text-[#92a9d2]">{i18nT("lobby.formatoRecomendadoJpgPngMaximoMb")}</div>
              </div>

              <div>
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-[#8fd4ff]">{i18nT("lobby.avataresPreestablecidos")}</div>
                <div className="max-h-[48vh] overflow-y-auto pr-1">
                  <div className="grid grid-cols-2 gap-2">
                    {AI_AVATAR_PRESETS.map((preset) => {
                      const selected =
                        stripAssetVersion(profileDraftAvatar) === stripAssetVersion(preset.src);
                      return (
                        <button
                          key={preset.id}
                          type="button"
                          onClick={() => {
                            setProfileDraftAvatar(preset.src);
                            setProfileDraftAvatarFile(null);
                          }}
                          className={`rounded border p-1 transition ${
                            selected
                              ? "border-[#51ff74] bg-[#193b2a]"
                              : "border-[#3c5f8f] bg-[#0f2448] hover:border-[#8fd4ff]"
                          }`}
                        >
                          <img
                            src={preset.src}
                            alt={preset.name}
                            className="h-24 w-full rounded object-cover"
                            onError={(event) => {
                              event.currentTarget.style.display = "none";
                            }}
                          />
                          <div className="mt-1 text-[10px] font-semibold uppercase tracking-wide text-[#bfdcff]">
                            {preset.name}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 pt-2 md:grid-cols-4">
            <button
              type="button"
              onClick={() => {
                setProfileDraftAvatar("");
                setProfileDraftAvatarFile(null);
              }}
              disabled={profileSavingPersonal}
              className="rounded border border-[#6f7ea0] bg-[#1b2a4a] px-2 py-2 text-xs font-semibold uppercase text-[#ccdaf7] transition hover:border-[#9fb4e6] disabled:cursor-not-allowed disabled:opacity-60"
            >{i18nT("lobby.sinFoto")}</button>
            <button
              type="button"
              onClick={resetProfileAvatar}
              disabled={profileSavingPersonal}
              className="rounded border border-[#d68d4a] bg-[#4f2f17] px-2 py-2 text-xs font-semibold uppercase text-[#ffd8ae] transition hover:border-[#ffb86b] disabled:cursor-not-allowed disabled:opacity-60"
            >{i18nT("lobby.restablecer")}</button>
            <button
              type="button"
              onClick={closeProfileModal}
              disabled={profileSavingPersonal}
              className="rounded border border-[#5f6f95] bg-[#192742] px-2 py-2 text-xs font-semibold uppercase text-[#c6d8ff] transition hover:border-[#8ea3d4] disabled:cursor-not-allowed disabled:opacity-60"
            >{i18nT("lobby.cancelar")}</button>
            <button
              type="button"
              onClick={saveProfileChanges}
              disabled={profileSavingPersonal}
              className="rounded border border-[#33d368] bg-[#14662f] px-2 py-2 text-xs font-semibold uppercase text-[#cbffe0] transition hover:border-[#6bf0a1] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {profileSavingPersonal ? i18nT("lobby.guardando") : i18nT("lobby.guardarPerfil")}
            </button>
          </div>
        </div>
      </Modal>

      {false && !isMobilePortraitLobby ? (
      <div className="mb-4 flex flex-col items-center gap-3">
        <div
          className="relative overflow-hidden rounded-2xl border-4 border-[#5dff9a] bg-black/45"
        >
          <video
            onMouseDown={startVideoDrag}
            onContextMenu={(event) => event.preventDefault()}
            onWheel={handleVideoWheelScale}
            style={{
              ...(videoPos.isAbsolute
                ? {
                    position: "absolute",
                    left: `${videoPos.x}px`,
                    top: `${videoPos.y}px`,
                    margin: 0,
                    zIndex: dragState?.kind === "video" ? 2 : 1,
                  }
                : { zIndex: 1 }),
              transform: `scale(${videoPos.scale || DEFAULT_VIDEO_SCALE})`,
              transformOrigin: "top left",
            }}
            src="/video_para_interface_inicio.mp4"
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            className="mobile-top-video block object-contain cursor-context-menu"
          />
        </div>
      </div>
      ) : null}

      {false && buttons.map((button) => {
        const palette = getButtonPalette(button.id);
        const Icon = button.icon;
        const isDragging = dragState?.id === button.id;
          const isSelected = selectedButtonId === button.id;
          const isResizing =
            dragState?.kind === "button-resize" && dragState?.id === button.id;
          const isRectangularResizable = RESIZABLE_RECTANGLE_BUTTON_IDS.has(button.id);
          const isModeRectangleButton = isRectangularResizable;
          const mobileRenderButton = mobilePortraitButtonRenderLayout?.get(button.id);
          const squareScaleMultiplier = getLobbySquareScaleMultiplier(button.id);
          const isMobilePortraitSquareButton = Boolean(mobileRenderButton) && !isRectangularResizable;
          const isDesktopFixedModeRectangle =
            isModeRectangleButton &&
            !isMobilePortraitLobby &&
            RECTANGLE_BUTTONS_WITH_FIXED_WIDTH.has(button.id);
          const baseRenderWidth =
            mobileRenderButton?.width ??
            button.width * mobileLobbyButtonScale * squareScaleMultiplier;
          const baseRenderHeight =
            mobileRenderButton?.height ??
            button.height * mobileLobbyButtonScale * squareScaleMultiplier;
          const renderWidth = isDesktopFixedModeRectangle
            ? DESKTOP_MODE_RECTANGLE_BUTTON_WIDTH
            : baseRenderWidth;
          const renderHeight = isDesktopFixedModeRectangle
            ? DESKTOP_MODE_RECTANGLE_BUTTON_HEIGHT
            : baseRenderHeight;
          const renderLeft =
            mobileRenderButton?.x ?? button.x * mobileLobbyButtonScale * squareScaleMultiplier;
          const renderTop =
            mobileRenderButton?.y ?? button.y * mobileLobbyButtonScale * squareScaleMultiplier;
          const showProfileImage =
            button.id === "04" && Boolean(lobbyProfileImage);
          const showProfileInitials = button.id === "04" && !showProfileImage;
          const rectangleButtonImage = RECTANGLE_BUTTON_IMAGE_MAP[button.id];
          const isRectangularModeImage = isRectangularResizable && Boolean(rectangleButtonImage);
          const isSquareCustomImage = Boolean(rectangleButtonImage) && !isRectangularResizable;
          const isPuzzleRectangularImage = button.id === "11";
          const rectangleButtonDisplayLabel =
            RECTANGLE_BUTTON_LABEL_MAP[button.id] || button.id;
          const rectangleButtonModeIcon = RECTANGLE_BUTTON_MODE_ICON_MAP[button.id];
          const shouldShowRectanglePlayButton = !RECTANGLE_BUTTONS_WITH_FIXED_WIDTH.has(button.id);
          const buttonDisplayLabel =
            button.id === "04"
              ? lobbyUserName
              : button.id === "02"
              ? "DISEÑAR"
              : LOBBY_BUTTON_LABEL_MAP[button.id] || button.id;
          const isLongButtonDisplayLabel = String(buttonDisplayLabel || "").length > 8;
          const isDesktopLobbyViewport = !isMobilePortraitLobby;
          const isMobileModeRectangle = isModeRectangleButton && !isDesktopLobbyViewport;
          const rectangleModeScale = isModeRectangleButton
            ? Math.min(
                renderWidth / DESKTOP_MODE_RECTANGLE_BUTTON_WIDTH,
                renderHeight / DESKTOP_MODE_RECTANGLE_BUTTON_HEIGHT
              )
            : 1;
          const rectangleModeBoardImageSize = Math.round(
            isMobileModeRectangle
              ? Math.min(renderHeight, renderWidth * 0.52)
              : RECTANGLE_MODE_BOARD_IMAGE_SIZE * rectangleModeScale
          );
          const rectangleModeRightPanelSize = Math.round(
            isMobileModeRectangle
              ? Math.max(0, renderWidth - rectangleModeBoardImageSize)
              : RECTANGLE_MODE_RIGHT_PANEL_SIZE * rectangleModeScale
          );
          const rectangleModeIconSize = Math.round(
            isMobileModeRectangle
              ? Math.min(rectangleModeRightPanelSize * 0.86, renderHeight * 0.76)
              : RECTANGLE_MODE_ICON_SIZE * rectangleModeScale
          );
          const rectangleModePlayMaxSize = Math.round(
            RECTANGLE_MODE_PLAY_IMAGE_SIZE * rectangleModeScale
          );
          const rectangleModeBoardImageClass =
            "pointer-events-none absolute left-0 top-0 object-contain object-left";
          const rectangleModeBoardImageStyle = isDesktopLobbyViewport
            ? {
                width: `${rectangleModeBoardImageSize}px`,
                height: `${rectangleModeBoardImageSize}px`,
              }
            : {
                width: `${rectangleModeBoardImageSize}px`,
                height: `${rectangleModeBoardImageSize}px`,
              };
          const rectangleModeIconImageClass =
            "pointer-events-none absolute left-0 right-0 top-[8%] mx-auto object-contain drop-shadow-[0_0_10px_rgba(255,255,255,0.35)]";
          const rectangleModeIconImageStyle = isDesktopLobbyViewport
            ? {
                width: `${rectangleModeIconSize}px`,
                height: `${rectangleModeIconSize}px`,
              }
            : {
                width: `${rectangleModeIconSize}px`,
                height: `${rectangleModeIconSize}px`,
              };
          const rectanglePlayImageClass =
            "pointer-events-none absolute left-0 right-0 top-[21%] mx-auto w-full object-contain";
          const rectanglePlayImageStyle = isDesktopLobbyViewport
            ? {
                maxWidth: `${rectangleModePlayMaxSize}px`,
                maxHeight: `${rectangleModePlayMaxSize}px`,
              }
            : {
                maxWidth: `${rectangleModePlayMaxSize}px`,
                maxHeight: `${rectangleModePlayMaxSize}px`,
              };
          const rectangleModeRightPanelClass = isDesktopFixedModeRectangle || isMobileModeRectangle
            ? "absolute top-0 z-10 h-full px-2 py-2"
            : "absolute right-0 top-0 z-10 h-full px-2 py-2";
          const rectangleModeRightPanelStyle = isDesktopFixedModeRectangle
            ? {
                left: `${rectangleModeRightPanelSize}px`,
                width: `${rectangleModeRightPanelSize}px`,
              }
            : isMobileModeRectangle
            ? {
                left: `${rectangleModeBoardImageSize}px`,
                width: `${rectangleModeRightPanelSize}px`,
              }
            : {};
          const selectedLobbyButtonShadow = `${palette.ring}, 0 0 0 2px #44ffaf, 0 0 0 5px #05080f, 0 18px 40px rgba(38,255,139,0.28), inset 0 1px 0 rgba(255,255,255,0.18)`;
          const mobileSquareImageSizeClass = isMobilePortraitSquareButton
            ? "h-[62%] w-[62%]"
            : isDesktopLobbyViewport
            ? DESKTOP_LOBBY_BUTTON_IMAGE_SIZE_CLASS
            : "h-[58%] w-[58%] max-h-full max-w-full";
          const mobileSquareProfileSizeClass = isMobilePortraitSquareButton
            ? "h-[96%] w-[96%]"
            : isDesktopLobbyViewport
            ? DESKTOP_LOBBY_BUTTON_IMAGE_SIZE_CLASS
            : "h-[86%] w-[86%] max-h-full max-w-full";
          const mobileSquareProfileTextClass = isMobilePortraitSquareButton ? "text-lg" : "text-2xl";
          return (
            <button
            key={button.id}
            type="button"
            onMouseDown={(event) => startDrag(event, button.id)}
            onClick={() => handleLobbyButtonClick(button.id)}
            onTouchStart={(event) => startMobileLongPressDrag(event, button.id)}
            onTouchEnd={cancelMobileLongPressDrag}
            onTouchCancel={cancelMobileLongPressDrag}
            onContextMenu={(event) => event.preventDefault()}
            aria-pressed={isSelected}
            className={`absolute flex select-none flex-col items-center justify-center rounded-2xl text-white transition-all duration-300 ease-in-out ${
              isSelected
                ? "scale-105 ring-2 ring-[#44ffaf] ring-offset-2 ring-offset-[#05080f] shadow-[0_18px_40px_rgba(38,255,139,0.22)]"
                : "hover:scale-105 hover:-translate-y-1 hover:brightness-125"
            } ${
              isDragging || isResizing
                ? "scale-105 shadow-[0_0_24px_rgba(255,255,255,0.35)]"
                : ""
            } ${isMobilePortraitSquareButton ? "overflow-hidden" : ""}`}
            style={{
              left: `${renderLeft}px`,
              top: `${renderTop}px`,
              width: `${renderWidth}px`,
              height: `${renderHeight}px`,
              background: palette.background,
              boxShadow: isSelected ? selectedLobbyButtonShadow : `${palette.ring}`,
              outline: isSelected ? "2px solid #44ffaf" : undefined,
              outlineOffset: isSelected ? "2px" : undefined,
              cursor: "context-menu",
              touchAction: isMobilePortraitLobby ? "pan-y" : undefined,
              zIndex: isDragging || isResizing ? 12 : 4,
            }}
          >
            <div
              className={
                isRectangularModeImage
                  ? "flex h-full w-full items-center justify-start"
                  : isMobilePortraitSquareButton
                  ? "flex h-full w-full flex-col items-center justify-between gap-1 px-1 py-1"
                  : ""
              }
              onMouseEnter={() => playButtonHoverSound(button.id)}
              onMouseLeave={() => delete lastButtonHoverAtRef.current[button.id]}
            >
              <div
                className={`rounded-xl border border-white/25 bg-black/25 ${
                  isRectangularModeImage
                    ? "relative flex h-full w-full flex-row items-center justify-start gap-3 overflow-hidden p-2 text-left"
                    : isMobilePortraitSquareButton
                    ? "flex min-h-0 w-full flex-1 flex-col items-center justify-center overflow-hidden p-1 text-center"
                    : "p-2"
                } ${
                  showProfileImage
                    ? "overflow-hidden"
                    : isRectangularModeImage
                    ? ""
                    : ""
                }`}
              >
                {showProfileImage ? (
                  <img
                    src={lobbyProfileImage}
                    alt="Perfil"
                    className={`${mobileSquareProfileSizeClass} rounded-full border border-cyan-300/70 object-cover object-center max-h-full max-w-full`}
                    style={{
                      aspectRatio: "1 / 1",
                    }}
                  />
                ) : showProfileInitials ? (
                  <span
                    className={`${mobileSquareProfileSizeClass} ${mobileSquareProfileTextClass} flex items-center justify-center rounded-full border border-cyan-300/70 bg-[#123462] font-black text-[#b9ebff] max-h-full max-w-full`}
                    style={{
                      aspectRatio: "1 / 1",
                    }}
                  >
                    {lobbyUserInitials}
                  </span>
                ) : (
                  rectangleButtonImage ? (
                    <img
                      src={rectangleButtonImage}
                      alt={`Modo ${button.id}`}
                      className={`${
                        isModeRectangleButton
                          ? rectangleModeBoardImageClass
                          : `${mobileSquareImageSizeClass} object-contain`
                      } ${isSquareCustomImage || isModeRectangleButton ? "" : "border border-cyan-300/50 bg-black/20 p-1"} object-center`}
                      style={{
                        ...(isModeRectangleButton ? rectangleModeBoardImageStyle : {}),
                        ...(WHITE_RECTANGLE_BUTTON_IMAGE_IDS.has(button.id)
                          ? { filter: "brightness(0) invert(1)" }
                          : isPuzzleRectangularImage
                          ? { filter: "brightness(0) saturate(100%) invert(1)" }
                          : {}),
                      }}
                    />
                  ) : (
                    <Icon className={mobileSquareImageSizeClass} aria-hidden="true" />
                  )
                )}
                {isRectangularModeImage ? (
                <div
                  className={rectangleModeRightPanelClass}
                  style={rectangleModeRightPanelStyle}
                >
                  <img
                    src={rectangleButtonModeIcon}
                    alt={`Logo ${rectangleButtonDisplayLabel}`}
                    className={rectangleModeIconImageClass}
                    style={rectangleModeIconImageStyle}
                  />
                  {shouldShowRectanglePlayButton ? (
                    <img
                      src={RECTANGLE_PLAY_BUTTON_IMAGE_SRC}
                      alt={i18nT("lobby.playXima")}
                      className={rectanglePlayImageClass}
                      style={rectanglePlayImageStyle}
                    />
                  ) : null}
                  <span
                    className={`absolute bottom-[6%] left-0 right-0 text-center font-black uppercase tracking-[0.12em] text-[#bfffee] drop-shadow-[0_0_8px_rgba(68,255,175,0.55)] ${
                      isMobileModeRectangle ? "text-[clamp(11px,3.2vw,15px)]" : "text-[12px]"
                    }`}
                  >
                    {formatLobbyCimaxScore(lobbyCimaxScores[button.id], language)}
                  </span>
                </div>
              ) : null}
              </div>
              {isRectangularModeImage ? null : (
                  <span
                    className={`font-black ${
                      isMobilePortraitSquareButton
                        ? "max-w-full px-0.5 text-center text-[9px] leading-none tracking-normal"
                        : isLongButtonDisplayLabel
                        ? "mt-2 max-w-full text-center text-[10px] leading-tight tracking-normal"
                        : "mt-2 text-xs tracking-[0.25em]"
                    } ${
                      button.id === "04" ? "normal-case text-center" : "uppercase"
                    }`}
                  >
                    {buttonDisplayLabel}
                  </span>
                )}
              {canEditLobbyLayout &&
              isRectangularResizable &&
              !isMobilePortraitLobby &&
              !RECTANGLE_BUTTONS_WITH_FIXED_WIDTH.has(button.id) ? (
                <div className="pointer-events-none absolute inset-0">
                  {RECTANGLE_RESIZE_HANDLES.map((handle) => (
                    <span
                      key={`${button.id}-${handle.id}`}
                      role="button"
                      aria-label={`Redimensionar botón ${button.id}`}
                      onMouseDown={(event) =>
                        startButtonResize(event, button.id, handle.id)
                      }
                      onContextMenu={(event) => event.preventDefault()}
                      className={`absolute ${handle.position} z-20 inline-flex h-6 w-6 select-none items-center justify-center rounded-sm border border-white/45 bg-[#0e1f44]/75 text-lg leading-none text-white pointer-events-auto transition hover:bg-[#44ffaf]/30`}
                      style={{ cursor: handle.cursor }}
                    >
                      {handle.symbol}
                    </span>
                  ))}
                </div>
              ) : null}
            </div>
          </button>
        );
      })}
    </div>
  );
};

export default Lobby;
